/* eslint-disable react/no-unescaped-entities */
/**
 * v3.14-178: Deal Dashboard - Block-Based Layout with Drag-and-Drop
 * 
 * ================================================================================
 *   EXTERNAL ORG UI — LOCKED v3.15-565 (April 8, 2026)
 *   DO NOT MODIFY external org layout without explicit owner approval.
 *   Locked: OfferBar, Select Offer buttons, Status checkboxes (read-only),
 *           Right column (Deal Ownership → Deal Financials → Deal Stages),
 *           Mid area (Confirmations → Funding Completion)
 * ================================================================================
 *
 * v3.14-176: Drag-and-Drop reordering via @dnd-kit
 * v3.14-178: Offer bar at TOP with dual Offer 1/2, Accept/Deny for external,
 *            lender names hidden from external users, grey-out on selection.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { formatETDate } from '../utils/timeUtils';
import LenderPortalSection from './LenderPortalSection';  // v3.18-1308
import OfferStackingBar from './OfferStackingBar';  // v3.18-1326
import useLiveActivityClaim from '../hooks/useLiveActivityClaim';   // v3.18-1343 LAC-VERIFY

// =================================================================
// v3.15-1152 — Financing Offers: spreadsheet-grade editor.
//
// Module-scope component (stable identity across parent re-renders) so a
// re-render inside DealDashboard does NOT remount the cell while the user
// is typing. Single source of truth lives on the parent:
//
//   activeEdit = { key: `${offerNum}:${fieldKey}`, draft: string } | null
//
// The parent owns commitDraft() + advance(); the cell only emits intent
// (activate / change-draft / commit / cancel). Mouse clicks on another
// cell use onMouseDown (fires BEFORE blur), so the previous cell commits
// synchronously and the new cell activates in the SAME React batch — no
// focus race, no double-click required.
// =================================================================
const OFFER_FIELD_ORDER = ['applied_for', 'deal_amount', 'approved_amount', 'term', 'payment', 'rate', 'lender'];

const _formatDisplay = (value, prefix, suffix) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (prefix && Number.isFinite(num)) return `${prefix}${num.toLocaleString()}${suffix || ''}`;
  return `${value}${suffix || ''}`;
};

/**
 * OfferCell — one financing-offer field. Stable across parent re-renders.
 *
 * Props:
 *   label, value, prefix='$', suffix='', highlight=false
 *   offerNum, fieldKey                 — identify this cell
 *   activeKey                          — global "which cell is active"
 *   draft                              — current draft when this cell is active
 *   onActivate(key, initialValue)      — request to become active
 *   onDraftChange(value)               — typed value
 *   onCommit({advance})                — commit current draft
 *   onCancel()                         — Escape
 *   isInternal                         — gates editability
 *   secondaryLabel, secondaryValue     — optional sub-line (e.g. PTC Fee)
 *   testIdPath                         — for data-testid stability
 */
const OfferCell = ({
  label, value, prefix = '$', suffix = '', highlight = false,
  offerNum, fieldKey, activeKey, draft, onActivate, onDraftChange, onCommit, onCancel,
  isInternal, secondaryLabel, secondaryValue, testIdPath,
}) => {
  const myKey = `${offerNum}:${fieldKey}`;
  const isActive = activeKey === myKey;
  const inputRef = useRef(null);

  // Focus + select the input ONCE when this cell becomes active.
  useEffect(() => {
    if (!isActive) return;
    const el = inputRef.current;
    if (!el) return;
    // Defer to next frame so React has finished committing the DOM.
    const id = requestAnimationFrame(() => {
      try {
        el.focus();
        // Highlight existing value if present, else place caret at end.
        if (el.value) el.select();
        else el.setSelectionRange(el.value.length, el.value.length);
      } catch { /* noop */ }
    });
    return () => cancelAnimationFrame(id);
  }, [isActive]);

  // Click → activate. onMouseDown so we beat the previous cell's onBlur
  // (which otherwise fires AFTER click and races the activation).
  const handleActivate = (e) => {
    if (!isInternal) return;
    if (isActive) return;
    e.preventDefault();
    onActivate(myKey, value);
  };

  return (
    <div
      className={`min-w-0 ${isInternal && !isActive ? 'cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors' : ''}`}
      onMouseDown={handleActivate}
      data-testid={`offer-field-${testIdPath}`}
    >
      <p className="text-[10px] text-slate-500 uppercase tracking-wide whitespace-nowrap">{label}</p>

      {isActive && isInternal ? (
        <input
          ref={inputRef}
          value={draft ?? ''}
          onChange={(e) => onDraftChange(e.target.value)}
          onBlur={() => onCommit({ advance: false })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onCommit({ advance: true }); }
            else if (e.key === 'Tab') { e.preventDefault(); onCommit({ advance: true }); }
            else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="w-full text-sm font-semibold border border-blue-400 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          data-testid={`edit-${testIdPath}`}
        />
      ) : (
        <p className={`text-sm font-semibold truncate ${highlight ? 'text-emerald-600' : 'text-slate-800'}`}>
          {_formatDisplay(value, prefix, suffix)}
        </p>
      )}

      {/* v3.15-1150B: optional supporting line (e.g. PTC Fee under Deal Amount). */}
      {secondaryLabel && (
        <div
          className="mt-0.5"
          onMouseDown={(e) => e.stopPropagation()}
          data-testid={`offer-field-${testIdPath}-secondary`}
        >
          <p className="text-[9px] text-slate-400 uppercase tracking-wide whitespace-nowrap">
            {secondaryLabel}
          </p>
          <p
            className="text-[11px] font-medium text-slate-500 truncate"
            data-testid={`offer-field-${testIdPath}-secondary-value`}
          >
            {secondaryValue}
          </p>
        </div>
      )}
    </div>
  );
};

// v3.15-1051: Explicit role eligibility for COMPANY-side Deal Ownership
// dropdowns. Manager Director is a hybrid leadership role and IS assignable
// to BOTH Director and Manager ownership slots.
// Dropdown-only. No permission, payroll, commission, or routing impact.
const DEAL_OWNERSHIP_DIRECTOR_ELIGIBLE_ROLES = new Set([
  'director',
  'company_director',
  'manager_director',  // v3.15-1051: hybrid role — Director-eligible
]);
const DEAL_OWNERSHIP_MANAGER_ELIGIBLE_ROLES = new Set([
  'manager',
  'company_manager',
  'manager_director',  // v3.15-1051: hybrid role — Manager-eligible
]);

// ════════════════════════════════════════════════════════════════════════
// v3.15-1060 — PTC INC OWNERSHIP HOTFIX — HARDCODED LITERAL ARRAYS
// v3.15-1065 — gate widened to BOTH PTC company_ids (internal "PTC" +
//              external "Prestigious Travel Club Inc"). Real PTC deals
//              live on the internal id, so the prior single-id gate
//              never fired in production.
// ════════════════════════════════════════════════════════════════════════
// Per Adris's directive (v3.15-1060):
//   - For Prestigious Travel Club Inc deals, bypass ALL generic filtering.
//   - DO NOT derive from role filters / companyUsers / ptcReps / overrides.
//   - JUST RENDER THE ARRAYS.
//   - Lookup by LOWERCASE EMAIL (authoritative identity).
//
// Each entry is a SELF-CONTAINED user object containing every field the
// dropdown render needs (id, full_name, email, role, company_id,
// company_name). The page does NOT need `/api/users` to load — the
// dropdown renders literally from these arrays. If a saved deal already
// references one of these users by id, the dropdown still resolves them.
//
// STRICT SCOPE (display visibility only):
//   - NO permission changes
//   - NO payroll changes
//   - NO commission changes
//   - NO routing / email changes
//   - NO automatic-client-creation
// ════════════════════════════════════════════════════════════════════════
const PTC_INC_COMPANY_ID = '93959d1e-6616-48b4-a5a2-6272dd45ff61';
const PTC_INC_COMPANY_NAME = 'Prestigious Travel Club Inc';
const PTC_INTERNAL_COMPANY_ID = 'b7d1a432-5537-4e79-a2f1-c2df96a16d3a';

// v3.15-1065: ANY PTC-flavoured company_id activates the literal allowlist.
// Production audit (2026-02-21) revealed real PTC deals are on the INTERNAL
// id; the prior single-id gate never fired and the baseline path leaked
// Adris into the Sales Rep dropdown and produced empty Director/Manager
// dropdowns. This Set is the source of truth for "is this a PTC deal?".
const PTC_LITERAL_COMPANY_IDS = new Set([
  PTC_INC_COMPANY_ID,        // 93959d1e — Prestigious Travel Club Inc (external)
  PTC_INTERNAL_COMPANY_ID,   // b7d1a432 — PTC (internal)
]);

// PTC INC LEADERSHIP — used for Director AND Manager dropdowns.
// id is the production user_id; email is the authoritative lookup key.
const PTC_INC_LEADERSHIP_USERS = [
  {
    id: 'de1e1529-ea24-4cec-84d3-120e5b0c7294',
    full_name: 'Adris Davis',
    email: 'a.davis@prestigioustravelclub.org',
    role: 'ptc_admin',
    company_id: PTC_INTERNAL_COMPANY_ID,
    company_name: 'PTC',
  },
  {
    id: 'c8647f17-6081-41f8-ba78-2be7fcf2c35d',
    full_name: 'Lauren Chase',
    email: 'lauren.chase@prestigioustravelclub.org',
    role: 'manager_director',
    company_id: PTC_INC_COMPANY_ID,
    company_name: PTC_INC_COMPANY_NAME,
  },
  {
    id: '4392db22-6207-4569-9e59-32cdc33d4636',
    full_name: 'Jeremy Collins',
    email: 'jeremy.c@prestigioustravelclub.org',
    role: 'ptc_director',
    company_id: PTC_INTERNAL_COMPANY_ID,
    company_name: 'PTC',
  },
];

// PTC INC SALES REPS — strict allowlist. Masha Morris has no preview-DB
// id (will resolve from prod /api/users at render time via email lookup);
// the fallback object below is used if she's not present in the users
// list so the dropdown still shows her name.
const PTC_INC_SALES_REP_USERS = [
  {
    id: '4392db22-6207-4569-9e59-32cdc33d4636',
    full_name: 'Jeremy Collins',
    email: 'jeremy.c@prestigioustravelclub.org',
    role: 'ptc_director',
    company_id: PTC_INTERNAL_COMPANY_ID,
    company_name: 'PTC',
  },
  {
    id: '38b338d8-9a67-488b-b540-1748ac5a8174',
    full_name: 'Jessica Cuadrado',
    email: 'jessica.c@prestigioustravelclub.org',
    role: 'ptc_rep',
    company_id: PTC_INTERNAL_COMPANY_ID,
    company_name: 'PTC',
  },
  {
    // v3.15-1065: Masha Morris production user record.
    //   id: 01f4129f-c712-41fa-b00f-f9ce9ff1c740 (created 2026-02-21)
    //   email: masha.m@prestigioustravelclub.org
    //   role:  ptc_team_member  (internal)
    //   company_id: PTC_INTERNAL_COMPANY_ID
    id: '01f4129f-c712-41fa-b00f-f9ce9ff1c740',
    full_name: 'Masha Morris',
    email: 'masha.m@prestigioustravelclub.org',
    role: 'ptc_team_member',
    company_id: PTC_INTERNAL_COMPANY_ID,
    company_name: 'PTC',
  },
];

// v3.15-1060: lowercase-email resolver. If `/api/users` includes the
// user, prefer that record (preserves the production id used by saved
// deal references); otherwise fall back to the hardcoded entry above.
const _hardenPtcUserList = (literalList, usersFromApi) => {
  const byEmail = {};
  for (const u of (usersFromApi || [])) {
    const e = String(u?.email || '').trim().toLowerCase();
    if (e) byEmail[e] = u;
  }
  return literalList.map((lit) => {
    const e = String(lit.email || '').trim().toLowerCase();
    const fromApi = byEmail[e];
    return fromApi ? { ...lit, ...fromApi } : lit;
  });
};

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
// v3.15-1053: quick-contact hover (UI-only — no extra network calls)
import { OwnershipContactHover } from './OwnershipContactHover';
// v3.15-1057: group assignee dropdowns by company
import { groupUsersByCompany } from '../utils/userGrouping';
import UserMultiSelect from './UserMultiSelect';

// v3.15-1053: normalized trigger styling — prevents the "oversized gold
// selected state" bug by enforcing consistent height, font, and selected-
// text colour across all ownership Select triggers.
const OWNERSHIP_TRIGGER_CLASS =
  "h-7 text-xs font-normal [&>span]:font-normal [&>span]:text-slate-700 " +
  "data-[placeholder]:text-slate-400 [&>span]:truncate";
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import {
  DollarSign,
  FileText,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Save,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Building2,
  User,
  Banknote,
  ArrowRight,
  ExternalLink,
  Edit,
  X,
  Briefcase,
  Mail,
  Phone,
  MessageSquare,
  Send,
  Calendar,
  History,
  Plus,
  Loader2,
  GripVertical,
  ThumbsUp,
  ThumbsDown,
  Shield,
  Landmark,
  Link2,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import DealStagesChecklist from './DealStagesChecklist';
import BankingAchPanel from './BankingAchPanel'; // v3.21-1689
import SendOfferToAgentModal from './SendOfferToAgentModal';
import TextOfferToAgentModal from './TextOfferToAgentModal';
import FinancingResultTeamMessageModal from './FinancingResultTeamMessageModal';
import CreditCardAuthorizationBlock from './CreditCardAuthorizationBlock';
// v3.18-1283 PAYMENT-008 — Unified Funding Sources block (canonical source of truth).
import FundingSourcesBlock from './FundingSourcesBlock';
import ClientLocalTimeBadge from './ClientLocalTimeBadge';
import LetsGetThisDealModal from './LetsGetThisDealModal';
import ClearToCollectModal from './ClearToCollectModal';   // v3.15-1151
import SendToPartnerBlock from './SendToPartnerBlock';
import LenderOffersWorkspace from './LenderOffersWorkspace';   // v3.21-1762
import { pickTopTwoRecommendations, resolveRequestedAmount } from '../lib/offerRanking';   // v3.21-1792 · WS2 auto Top-2
import api from '../lib/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ClickableContact } from './QRCodePopup';
import { PhoneClickable } from './PhoneQRModal';
import EmailDraftModal from './EmailDraftModal';
// v3.15-1104 — CF360 integration UI surface
import CF360DormantBanner from './CF360DormantBanner';
import CF360TransferBanner from './CF360TransferNotice';
import SendToCF360Modal from './SendToCF360Modal';
import MarkReturnedFromCF360Modal from './MarkReturnedFromCF360Modal';
import CF360StateBadge from './CF360StateBadge';
import CF360HistoryTab from './CF360HistoryTab';

// Status Badge Component
const StatusBadge = ({ status, variant = 'default' }) => {
  const variants = {
    paid: 'bg-green-100 text-green-700 border-green-200',
    due: 'bg-amber-100 text-amber-700 border-amber-200',
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    sent: 'bg-blue-100 text-blue-700 border-blue-200',
    unpaid: 'bg-slate-100 text-slate-600 border-slate-200',
    default: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${variants[variant] || variants.default}`}>
      {status}
    </span>
  );
};

// Block Wrapper - Sortable via @dnd-kit
const SortableDashboardBlock = ({ id, title, icon: Icon, children, className = '', compact = false, customHeader = null, orderIndex }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
    order: orderIndex ?? 'unset',
  };

  return (
    <Card 
      ref={setNodeRef}
      style={style}
      id={`deal-block-${id}`}
      data-block-id={id}
      data-testid={`dashboard-block-${id}`}
      className={`h-fit rounded-none shadow-none border-b ${isDragging ? 'ring-2 ring-blue-400 shadow-lg' : ''} ${className}`}
    >
      <CardHeader className="pb-1 pt-2 px-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-0.5 -ml-1 rounded hover:bg-slate-100 transition-colors"
            data-testid={`drag-handle-${id}`}
            aria-label={`Reorder ${title}`}
          >
            <GripVertical className="h-3.5 w-3.5 text-slate-400" />
          </button>
          {customHeader ? customHeader : (<>{Icon && <Icon className="h-4 w-4 text-slate-500" />}{title}</>)}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 px-3 pb-2">
        <div className="max-w-md mx-auto">
          {children}
        </div>
      </CardContent>
    </Card>
  );
};

// v3.14-302: Thin sortable wrapper for components with their own Card (e.g. DealStagesChecklist)
const SortableItemWrapper = ({ id, children, orderIndex }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
    order: orderIndex ?? 'unset',
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`dashboard-block-${id}`} className={`relative ${isDragging ? 'ring-2 ring-blue-400 shadow-lg rounded-lg' : ''}`}>
      <button
        {...attributes}
        {...listeners}
        className="absolute top-3.5 left-1.5 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-slate-100 transition-colors z-10"
        data-testid={`drag-handle-${id}`}
        aria-label={`Reorder block`}
      >
        <GripVertical className="h-3.5 w-3.5 text-slate-400" />
      </button>
      {children}
    </div>
  );
};

// Non-sortable block (for stage strip etc.)
const DashboardBlock = ({ id, title, icon: Icon, children, className = '', compact = false }) => (
  <Card 
    id={`deal-block-${id}`}
    data-block-id={id}
    className={`h-full ${className}`}
  >
    <CardHeader className={`${compact ? 'pb-2 pt-3 px-3' : 'pb-2 pt-3 px-4'}`}>
      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
        {Icon && <Icon className="h-4 w-4 text-slate-500" />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className={`${compact ? 'pt-0 px-3 pb-3' : 'pt-0 px-4 pb-4'}`}>
      {children}
    </CardContent>
  </Card>
);

// Field Display Component
const FieldDisplay = ({ label, value, className = '' }) => (
  <div className={className}>
    <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-medium text-slate-800 truncate">{value || '-'}</p>
  </div>
);

// v3.15-767: read-only underwriting chips. Single line of "·"-separated values
// reflecting ACTUAL stored fields — income_type, employment_status, education,
// pay_frequency. Slug-ish values (e.g. "bi_weekly") are prettified for display
// only; the underlying stored value is never mutated here.
const prettifyChip = (v) => {
  if (v === null || v === undefined || v === '') return '';
  // Only normalise underscores → spaces. Keep hyphens (Bi-Weekly, Full-Time, Part-Time).
  let s = String(v).replace(/_+/g, ' ').trim();
  // Title-case each whitespace/hyphen-separated word.
  s = s
    .split(/(\s|-)/)
    .map((chunk) => (chunk.match(/\s|-/)
      ? chunk
      : (chunk ? chunk[0].toUpperCase() + chunk.slice(1).toLowerCase() : '')))
    .join('');
  return s;
};
const UnderwritingChips = ({ testId, income_type, employment_status, education, pay_frequency }) => {
  const items = [income_type, employment_status, education, pay_frequency]
    .map(prettifyChip)
    .filter(Boolean);
  if (items.length === 0) {
    return (
      <div className="pt-1" data-testid={testId}>
        <p className="text-[11px] text-slate-400 italic">No underwriting data</p>
      </div>
    );
  }
  return (
    <div className="pt-1" data-testid={testId}>
      <p className="text-[11px] font-medium text-slate-700" title="Underwriting data from EQF">
        {items.join(' · ')}
      </p>
    </div>
  );
};

// Editable Field Component - Click to edit inline
const EditableField = ({ label, value, fieldName, onSave, highlight = false, inputType = 'text', placeholder = 'Add...', formatter }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(fieldName, editValue || null);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  // v3.15-1024: System-standard date format = mm/dd/yyyy. Applied uniformly
  // to every date-typed editable cell (DOB, payment dates, etc.).
  const displayValue = inputType === 'date' && value
    ? (() => {
        try {
          const d = new Date(String(value).slice(0, 10) + 'T00:00:00');
          if (isNaN(d.getTime())) return value;
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const yy = d.getFullYear();
          return `${mm}/${dd}/${yy}`;
        } catch {
          return value;
        }
      })()
    : value;

  const hasLabel = label && label.trim().length > 0;

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        {hasLabel && <span className="text-slate-400 text-[10px] uppercase whitespace-nowrap flex-shrink-0">{label}</span>}
        <Input
          type={inputType}
          value={editValue}
          onChange={(e) => setEditValue(formatter ? formatter(e.target.value) : e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          autoFocus
          className="h-6 text-xs py-0 px-1 flex-1"
          disabled={saving}
        />
      </div>
    );
  }

  return (
    <div 
      className={`flex items-center cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5 -mx-1 transition-colors group ${hasLabel ? 'gap-2' : ''}`}
      onClick={() => {
        setEditValue(value || '');
        setIsEditing(true);
      }}
      data-testid={`editable-field-${fieldName}`}
    >
      {hasLabel && <span className="text-slate-400 text-[10px] uppercase whitespace-nowrap flex-shrink-0">{label}</span>}
      <div className="flex items-center gap-1 min-w-0 flex-1">
        <span className={`text-xs truncate ${highlight && value ? 'text-emerald-600 font-semibold' : value ? 'text-slate-800' : 'text-slate-300 italic'}`}>
          {displayValue || placeholder}
        </span>
        <Edit className="h-2.5 w-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      </div>
    </div>
  );
};

// v3.14-512: Editable Dropdown Field for fields with predefined options (education, pay frequency)
const EditableDropdownField = ({ label, value, fieldName, onSave, options = [] }) => {
  const [saving, setSaving] = useState(false);

  const handleChange = async (newValue) => {
    if (newValue === value || newValue === 'none') return;
    setSaving(true);
    try {
      await onSave(fieldName, newValue || null);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 px-1 py-0.5 -mx-1">
      <span className="text-slate-400 text-[10px] uppercase whitespace-nowrap flex-shrink-0">{label}</span>
      <Select value={value || 'none'} onValueChange={handleChange} disabled={saving}>
        <SelectTrigger className="h-6 text-xs py-0 px-1 flex-1 border-slate-200" data-testid={`dropdown-field-${fieldName}`}>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Select...</SelectItem>
          {options.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// v3.21-1767 INTAKE-FIX · Education dropdown normalization.
// Mirrors `backend/services/education_normalizer.py::_CANONICAL_MAP`
// so that stored variants ("Bachelors Degree", "Bachelor's Degree",
// "bachelors", "BA", "bachelors_degree", "Bachelor") ALL resolve to
// the canonical option value `bachelors_degree` used by the AEA
// dropdown.  Without this normalization the Radix <Select> receives
// a value that does not equal any option value and renders BLANK —
// exactly the Valerie Jean regression.
//
// Unknown/blank values return the original string so the operator
// still sees whatever raw text was stored and can correct it.
const _EDU_MAP = {
  'high_school':        'high_school',
  'high school':        'high_school',
  'hs':                 'high_school',
  'ged':                'ged',
  'associates':         'associates_degree',
  'associate':          'associates_degree',
  "associate's":        'associates_degree',
  "associate's degree": 'associates_degree',
  'associates degree':  'associates_degree',
  'associates_degree':  'associates_degree',
  'some_college':       'some_college',
  'some college':       'some_college',
  'trade_school':       'trade_school',
  'trade school':       'trade_school',
  'vocational':         'trade_school',
  'college':            'college_degree',
  'college degree':     'college_degree',
  'college_degree':     'college_degree',
  'bachelors':          'bachelors_degree',
  'bachelor':           'bachelors_degree',
  "bachelor's":         'bachelors_degree',
  "bachelor's degree":  'bachelors_degree',
  'bachelors degree':   'bachelors_degree',
  'bachelors_degree':   'bachelors_degree',
  'ba':                 'bachelors_degree',
  'bs':                 'bachelors_degree',
  'masters':            'masters_degree',
  'master':             'masters_degree',
  "master's":           'masters_degree',
  "master's degree":    'masters_degree',
  'masters degree':     'masters_degree',
  'masters_degree':     'masters_degree',
  'mba':                'masters_degree',
  'ma':                 'masters_degree',
  'ms':                 'masters_degree',
  'doctorate':          'doctorate',
  'doctoral':           'doctorate',
  'phd':                'doctorate',
  'jd':                 'doctorate',
  'md':                 'doctorate',
};
const normalizeEducationForDropdown = (raw) => {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return '';
  const key = s.toLowerCase();
  if (_EDU_MAP[key]) return _EDU_MAP[key];
  const swapped = key.replace(/_/g, ' ');
  if (_EDU_MAP[swapped]) return _EDU_MAP[swapped];
  const usc = key.replace(/ /g, '_');
  if (_EDU_MAP[usc]) return _EDU_MAP[usc];
  // Pass-through so the operator still sees an unknown stored value.
  return s;
};

// Compact Money Display
const MoneyDisplay = ({ label, value, size = 'default', onClick }) => {
  const formatted = value ? `$${Number(value).toLocaleString()}` : '-';
  return (
    <div onClick={onClick} className={onClick ? 'cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1 transition-colors' : ''}>
      <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`font-semibold text-slate-800 ${size === 'large' ? 'text-lg' : 'text-sm'}`}>
        {formatted}
      </p>
    </div>
  );
};

/**
 * v3.14-178: OfferBar — Dual-offer display at the TOP of the dashboard.
 * - Shows Offer 1 and Offer 2 side by side
 * - External users see generic labels (no lender names)
 * - External directors/managers can Accept or Deny
 * - When an offer is accepted, the other greys out
 * - Columns: Applied For, Deal Amount, Approved Amount, Term, Payment, Rate
 */
const OfferBar = ({ client, isInternal, canAcceptOffers, acceptingOffer, decliningOffer, onAcceptOffer, onDeclineOffers, onEdit, onUpdateField, companyIndustry, finturfOffers, onOpenLenderOffers, hasFinturfIdentifier }) => {
  // v3.21-1674 · Runtime-crash fix: `finturfOffers` and the modal-open callback
  // are now received as props from the parent DealDashboard component.
  // v3.21-1677 · `hasFinturfIdentifier` also arrives as a prop so the
  // "View Lender Offers" button surfaces when the Hub already knows a
  // Finturf application identifier (e.g. `clients.deal_number` populated)
  // even before the modal has opened and populated `finturfOffers` for
  // the first time — this fixes the Dennis-Hicks "0 offers" symptom
  // where the button was hidden despite LVM02131 being on the record.
  const safeFinturfOffers = Array.isArray(finturfOffers) ? finturfOffers : [];
  const hasFinturfOffers = safeFinturfOffers.length > 0;
  const optionSelected = client?.option_selected;
  const isApproved = client?.funding_status === 'financing_approved_by_client' || client?.deal_stage === 'financing_approved_by_client';
  const [lookingUpLender, setLookingUpLender] = useState(null); // tracks which offer (1 or 2) is looking up
  const isSolar = (companyIndustry || '').toLowerCase().includes('solar');

  // v3.15-921: Send-to-Agent modal state. Holds the offer payload to send.
  const [sendToAgentTarget, setSendToAgentTarget] = useState(null);  // { offer, offerNumber } or null
  // v3.21-1696: Text-Offer-to-Agent modal state (canonical TextBee SMS).
  const [textToAgentTarget, setTextToAgentTarget] = useState(null);  // { offer, offerNumber } or null

  // v3.15-1130: Financing Offers auto-focus chain — Applied For → Deal
  // Amount → Approved Amount → Term → Payment → Rate → Lender Name.
  // No locking, no disabling — users can still click directly into any
  // field at any time. We use a "pending advance" pattern: when a field
  // saves, it sets `pendingAdvanceRef` to the NEXT field key. Each
  // EditableOfferField checks the pending ref on mount; if matched, it
  // opens itself into edit mode. This is robust to React unmount/remount
  // cycles caused by EditableOfferField being defined inside OfferBar.
  // v3.15-1152: legacy register/advance ref-dance removed — replaced by
  // the parent-owned activeEdit state below. The OFFER_FIELD_ORDER constant
  // now lives at module scope (top of file) and is consumed by commitActiveEdit.

  // Offer 1 data (primary — stored directly on client)
  const rawAppliedFor1 = client?.loan_amount_requested || client?.requested_amount;
  const offer1 = {
    applied_for: isSolar && rawAppliedFor1 ? Math.round(parseFloat(rawAppliedFor1) * 1.10) : rawAppliedFor1,
    applied_for_label: isSolar ? 'Applied For (incl. PTC Fee)' : 'Applied For',
    deal_amount: client?.deal_amount || client?.requested_amount,
    approved_amount: client?.approved_amount,
    term: client?.term,
    payment: client?.payment || client?.monthly_payment,
    rate: client?.rate || client?.apr,
    lender: isInternal ? client?.lender_name : null,
  };

  // Offer 2 data (secondary — stored in financing_option_2, fully independent)
  const opt2 = client?.financing_option_2 || {};
  const rawAppliedFor2 = opt2.loan_amount_requested;
  const offer2 = {
    applied_for: isSolar && rawAppliedFor2 ? Math.round(parseFloat(rawAppliedFor2) * 1.10) : rawAppliedFor2,
    applied_for_label: isSolar ? 'Applied For (incl. PTC Fee)' : 'Applied For',
    deal_amount: opt2.deal_amount,
    approved_amount: opt2.approved_amount,
    term: opt2.term,
    payment: opt2.payment,
    rate: opt2.rate,
    lender: isInternal ? opt2.lender_name : null,
  };

  const hasOffer1 = offer1.approved_amount || offer1.applied_for;
  const hasOffer2 = offer2.approved_amount || offer2.applied_for;

  // v3.15-1152 — Financing Offers spreadsheet-grade editor.
  // Single source of truth for "which cell is currently being edited".
  // Lives on the parent so cross-cell clicks can commit + activate in one
  // React batch (no focus race) and so parent re-renders don't unmount the
  // active input.
  const [activeEdit, setActiveEdit] = useState(null);  // { key, draft } | null
  const activeEditRef = useRef(activeEdit);
  useEffect(() => { activeEditRef.current = activeEdit; }, [activeEdit]);
  const committingRef = useRef(false);                 // re-entrancy guard

  // Commit the active cell's draft. Reads from the ref (not state) so it is
  // synchronous when invoked from event handlers — the typed value never
  // races with a parent re-render.
  const commitActiveEdit = useCallback(async ({ advance = false } = {}) => {
    const cur = activeEditRef.current;
    if (!cur || committingRef.current) return;
    committingRef.current = true;
    try {
      const [offerNumStr, fieldKey] = cur.key.split(':');
      const offerNum = Number(offerNumStr);
      const draft    = cur.draft;
      // Field-path resolver (matches the old `fp(field)` from renderOffer).
      const fp = (f) => offerNum === 1 ? f : `financing_option_2.${f}`;
      // Map fieldKey → fieldPath. The `applied_for` cell writes to the
      // legacy `loan_amount_requested` field; all others 1:1.
      const fieldPath = fp(fieldKey === 'applied_for' ? 'loan_amount_requested' : fieldKey);

      // Resolve the original (untouched) value so we can short-circuit
      // when nothing changed. Mirrors the old `editVal === String(value || '')`
      // guard — but pulls live from `client` (always the freshest copy).
      let originalValue;
      if (fieldPath.startsWith('financing_option_2.')) {
        const sub = fieldPath.replace('financing_option_2.', '');
        originalValue = (client?.financing_option_2 || {})[sub];
      } else {
        originalValue = client?.[fieldPath];
      }
      const changed = String(draft ?? '') !== String(originalValue ?? '');

      if (changed) {
        // v3.15-1152d — Inline financial-field edits self-confirm so they
        // don't trip the Tier-3 financial-confirmation gate (window.confirm)
        // which steals focus from the input, blurs it, and closes the
        // editor before the operator can save.
        const SELF_CONFIRM_FIELDS = new Set(['deal_amount', 'approved_amount', 'applied_for_amount', 'loan_amount_requested']);
        if (fieldPath.startsWith('financing_option_2.')) {
          const sub = fieldPath.replace('financing_option_2.', '');
          const current = client?.financing_option_2 || {};
          // Offer 2 nested writes never go through the Tier-3 gate (gate
          // only checks the three top-level keys), but we add the flag
          // defensively in case a future iteration tightens the gate.
          await onUpdateField('financing_option_2', { ...current, [sub]: draft });
        } else if (SELF_CONFIRM_FIELDS.has(fieldPath)) {
          // Use the object-payload form of onUpdateField so we can attach
          // financial_change_confirm. The ClientDetailPage handler accepts
          // both signatures (string,value) and (object).
          await onUpdateField({ [fieldPath]: draft, financial_change_confirm: true });
        } else {
          await onUpdateField(fieldPath, draft);
        }
      }

      // Advance — set the NEXT cell active in the SAME batch so the user
      // never sees an "empty no-cell-active" frame between commits.
      if (advance) {
        const idx = OFFER_FIELD_ORDER.indexOf(fieldKey);
        if (idx >= 0 && idx < OFFER_FIELD_ORDER.length - 1) {
          const nextKey = OFFER_FIELD_ORDER[idx + 1];
          if (nextKey === 'lender') {
            // Lender uses a separate <input> (not OfferCell). Close active
            // edit; the lender input's own focus chain will pick it up.
            setActiveEdit(null);
            // Best-effort: focus the lender input if it's already mounted.
            const lenderEl = document.querySelector(`[data-testid="offer-${offerNum}-lender-input"]`);
            if (lenderEl && typeof lenderEl.focus === 'function') {
              setTimeout(() => { try { lenderEl.focus(); lenderEl.select?.(); } catch { /* noop */ } }, 0);
            }
          } else {
            // Read the NEW (post-save) initial draft from the freshest
            // client snapshot — important because `onUpdateField` may have
            // mutated the next field (e.g. PTC fee recompute side-effects).
            const nextFieldPath = fp(nextKey === 'applied_for' ? 'loan_amount_requested' : nextKey);
            let nextValue;
            if (nextFieldPath.startsWith('financing_option_2.')) {
              const sub = nextFieldPath.replace('financing_option_2.', '');
              nextValue = (client?.financing_option_2 || {})[sub];
            } else {
              nextValue = client?.[nextFieldPath];
            }
            setActiveEdit({
              key:   `${offerNum}:${nextKey}`,
              draft: nextValue == null ? '' : String(nextValue),
            });
          }
        } else {
          setActiveEdit(null);
        }
      } else {
        setActiveEdit(null);
      }
    } finally {
      committingRef.current = false;
    }
    // We intentionally depend on `client` + `onUpdateField` only — touching
    // `activeEdit` here would cause stale-closure issues; we read from the ref.

  }, [client, onUpdateField]);

  // Click-to-activate handler emitted by OfferCell. Synchronously commits
  // any other active cell first (covers cross-cell clicks — the new cell's
  // onMouseDown beats the previous cell's onBlur).
  const handleActivateCell = useCallback((key, initialValue) => {
    const cur = activeEditRef.current;
    if (cur && cur.key !== key) {
      commitActiveEdit({ advance: false }).finally(() => {
        setActiveEdit({ key, draft: initialValue == null ? '' : String(initialValue) });
      });
    } else if (!cur) {
      setActiveEdit({ key, draft: initialValue == null ? '' : String(initialValue) });
    }
  }, [commitActiveEdit]);

  const handleDraftChange = useCallback((value) => {
    setActiveEdit((prev) => (prev ? { ...prev, draft: value } : prev));
  }, []);

  const handleCancelEdit = useCallback(() => { setActiveEdit(null); }, []);

  // v3.15-1152b: Bundle the parent-owned editor controls into a single object
  // so we can hand them to a STABLE, module-scope <OfferCell /> directly at
  // each call site. The previous design wrapped OfferCell in a useCallback
  // named EditableOfferField — but its dep array included `activeEdit`, so
  // every keystroke updated activeEdit → useCallback returned a new function
  // reference → React saw a new component TYPE → it unmounted/remounted the
  // cell on every keystroke → input lost focus and the draft was rebuilt from
  // props. Stable component identity (module-scope OfferCell) is the fix.
  const editorCtx = {
    activeKey:     activeEdit?.key || null,
    draft:         activeEdit?.draft,
    onActivate:    handleActivateCell,
    onDraftChange: handleDraftChange,
    onCommit:      commitActiveEdit,
    onCancel:      handleCancelEdit,
    isInternal,
  };

  const renderOffer = (offer, num) => {
    const isSelected = optionSelected === num;
    const isOtherSelected = optionSelected && optionSelected !== num;
    const borderColor = isSelected ? 'border-emerald-400 bg-emerald-50/40' : isOtherSelected ? 'border-slate-200 bg-slate-50 opacity-50' : 'border-blue-200 bg-blue-50/20';
    const labelColor = isSelected ? 'text-emerald-700' : isOtherSelected ? 'text-slate-400' : 'text-blue-700';

    const fp = (field) => num === 1 ? field : `financing_option_2.${field}`;

    // Lender info: Offer 1 uses client-level fields, Offer 2 uses financing_option_2
    const lenderName = num === 1
      ? (client?.lending_institution || '')
      : (opt2.lending_institution || offer.lender || '');
    const lenderPhone = num === 1
      ? (client?.lender_contact_phone || '')
      : (opt2.lender_contact_phone || '');

    // v3.14-236: Look up lender phone when name is entered
    const handleLenderSave = async (name) => {
      if (!name || name === lenderName) return;
      const fieldPrefix = num === 1 ? '' : 'financing_option_2.';
      // Save lender name first
      if (num === 1) {
        await onUpdateField('lending_institution', name);
      } else {
        const current = client?.financing_option_2 || {};
        await onUpdateField('financing_option_2', { ...current, lending_institution: name });
      }
      // Auto-lookup phone
      setLookingUpLender(num);
      try {
        const res = await api.post('/lenders/lookup', { lender_name: name });
        if (res.data?.phone) {
          if (num === 1) {
            await onUpdateField('lender_contact_phone', res.data.phone);
          } else {
            const current2 = client?.financing_option_2 || {};
            await onUpdateField('financing_option_2', { ...current2, lending_institution: name, lender_contact_phone: res.data.phone });
          }
          toast.success(`Found: ${res.data.phone}`);
        }
      } catch (e) { /* silent */ }
      setLookingUpLender(null);
    };

    // v3.15-1130: callback ref for the always-visible lender input so the
    // auto-focus chain can land on it after "Rate" is saved. Using a
    // callback ref (rather than useRef/useEffect) keeps renderOffer free of
    // hooks (renderOffer is a plain function, not a component).
    // v3.15-1152: lender input no longer participates in the legacy
    // register/advance ref dance. The auto-focus on advance is handled by
    // commitActiveEdit() which queries the DOM directly for the lender
    // input via its data-testid. The callback ref becomes a no-op shim
    // (kept only for JSX call-site compatibility — could be removed in
    // a follow-up sweep).
    const lenderInputCallbackRef = () => {};

    return (
      <div className={`p-3 rounded-lg border-2 ${borderColor} transition-all`} data-testid={`offer-${num}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold uppercase tracking-wider ${labelColor}`}>
              Offer {num}
            </span>
            {isSelected && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">Selected</Badge>
            )}
            {isOtherSelected && (
              <Badge className="bg-slate-100 text-slate-400 border-slate-200 text-[10px]">Not Selected</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* External org: internal Hub comparison-choice button (renamed
                v3.21-1673 · Finturf final-offer selection is completed in
                Finturf until PATCH /offer is approved). */}
            {!isInternal && !isSelected && !isApproved && (offer.approved_amount || offer.applied_for || offer.deal_amount) && (
              <Button
                size="sm"
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => onAcceptOffer(num)}
                disabled={acceptingOffer}
                data-testid={`select-offer-${num}`}
                title="Marks the client's chosen offer inside the Hub. The actual PTC Finance selection is still completed in PTC Finance."
              >
                {acceptingOffer ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ThumbsUp className="h-3 w-3 mr-1" />}
                Mark Client Choice
              </Button>
            )}
            {/* v3.14-236: Editable lender name + auto-looked-up phone */}
            {isInternal && (
              <div className="flex items-center gap-1.5" data-testid={`offer-${num}-lender-info`}>
                <input
                  ref={lenderInputCallbackRef}
                  className="text-xs text-slate-600 bg-transparent border-b border-dashed border-slate-300 focus:border-blue-400 focus:outline-none px-1 py-0.5 w-32 placeholder:text-slate-300"
                  defaultValue={lenderName}
                  placeholder="Lender name"
                  onBlur={(e) => handleLenderSave(e.target.value.trim())}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                  data-testid={`offer-${num}-lender-input`}
                />
                {lookingUpLender === num && <Loader2 className="h-3 w-3 animate-spin text-blue-400" />}
                {lenderPhone && <span className="text-xs text-slate-400">{lenderPhone}</span>}
              </div>
            )}
            {/* Internal Hub comparison-choice button (renamed v3.21-1673).
                Marks the Hub-side option_selected value ONLY. Does NOT
                call Finturf PATCH /offer; the actual consumer offer
                selection is still completed inside Finturf. */}
            {isInternal && !isSelected && (
              <Button
                size="sm"
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={async () => {
                  // v3.15-1062: STRICT — Select Offer marks `option_selected`
                  // ONLY. It NEVER writes deal_amount. Operators who want to
                  // copy the offer amount into Deal Amount must click the
                  // separate "Use Offer Amount" button below.

                  console.log(`[FIN-1673] mark_client_choice offer=${num} → write option_selected ONLY (deal_amount NOT touched; no Finturf PATCH)`);
                  await onUpdateField('option_selected', num);
                  toast.success(`Offer ${num} marked as client's choice. Complete the final selection inside PTC Finance.`);
                }}
                data-testid={`select-offer-${num}`}
                title="Marks the client's chosen offer inside the Hub. The actual PTC Finance selection is still completed in PTC Finance."
              >
                <ThumbsUp className="h-3 w-3 mr-1" />
                Mark Client Choice
              </Button>
            )}
            {/* v3.15-1062: explicit "Use Offer Amount" button — operator
                must click this to push the offer's deal_amount into the
                deal's Gross Deal Amount field. No silent mutation. */}
            {isInternal && (offer.deal_amount || offer.applied_for) && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={async () => {
                  const dealAmt = offer.deal_amount || offer.applied_for;

                  console.log(`[FIN-1062] explicit "Use Offer Amount" offer=${num} → write deal_amount=${dealAmt}`);
                  // Replace-or-cancel confirm — silently overwriting an
                  // already-set Deal Amount is exactly what v3.15-1062
                  // forbids, so we surface it explicitly.
                  const cur = client?.deal_amount;
                  if (cur && Number(cur) !== Number(dealAmt) && !window.confirm(
                    `Replace current Deal Amount ($${Number(cur).toLocaleString()}) with offer amount ($${Number(dealAmt).toLocaleString()})?`
                  )) return;
                  await onUpdateField('deal_amount', dealAmt);
                  toast.success(`Deal Amount set to $${Number(dealAmt).toLocaleString()} (from Offer ${num})`);
                }}
                data-testid={`use-offer-amount-${num}`}
                title="Copy this offer's amount into the deal's Gross Deal Amount"
              >
                Use Offer Amount
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          <OfferCell {...editorCtx} label={offer.applied_for_label || 'Applied For'} value={offer.applied_for} testIdPath={fp('loan_amount_requested')} offerNum={num} fieldKey="applied_for" />
          <OfferCell
            {...editorCtx}
            label="Deal Amount"
            value={offer.deal_amount}
            testIdPath={fp('deal_amount')}
            offerNum={num}
            fieldKey="deal_amount"
            /* v3.15-1150B: PTC Fee is an INTERNAL processing aid only —
               hidden from external partner/agent viewers. */
            secondaryLabel={isInternal ? "PTC Fee (10%)" : undefined}
            secondaryValue={isInternal ? (() => {
              // PTC Fee = Deal Amount × 10%. Auto-recalculates on every
              // render — so offer selection, lender updates, recalculations
              // and manual edits all flow through here.
              // Blank / null / 0 → em-dash per requirements.
              const raw = offer.deal_amount;
              const n = (raw === null || raw === undefined || raw === '')
                ? NaN
                : Number(String(raw).replace(/[^0-9.\-]/g, ''));
              if (!Number.isFinite(n) || n <= 0) return '—';
              const fee = n * 0.1;
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(fee);
            })() : undefined}
          />
          <OfferCell {...editorCtx} label="Approved Amount" value={offer.approved_amount} testIdPath={fp('approved_amount')} highlight={!!offer.approved_amount} offerNum={num} fieldKey="approved_amount" />
          <OfferCell {...editorCtx} label="Term" value={offer.term} testIdPath={fp('term')} prefix="" suffix=" mo" offerNum={num} fieldKey="term" />
          <OfferCell {...editorCtx} label="Payment" value={offer.payment} testIdPath={fp('payment')} suffix="/mo" offerNum={num} fieldKey="payment" />
          <OfferCell {...editorCtx} label="Rate" value={offer.rate} testIdPath={fp('rate')} prefix="" suffix="%" offerNum={num} fieldKey="rate" />
        </div>
        {/* v3.15-921: Send to Agent — purpose-built outbound email composer.
             Sits directly below the offer's data grid (and below the inline
             Select Offer button in the header) so reps can quickly forward
             clean offer details to a partner/agent. Muted secondary style.
             v3.21-1696 — Adds sibling [Text Offer to Agent] via canonical
             TextBee SMS. Email action remains available and unchanged. */}
        <div className="mt-3 flex justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
            onClick={() => setSendToAgentTarget({ offer, offerNumber: num })}
            data-testid={`send-offer-to-agent-btn-${num}`}
          >
            <Mail className="h-3 w-3 mr-1" />
            Send to Agent
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
            onClick={() => setTextToAgentTarget({ offer, offerNumber: num })}
            data-testid={`text-offer-to-agent-btn-${num}`}
          >
            <MessageSquare className="h-3 w-3 mr-1" />
            Text Offer to Agent
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Card className="border border-blue-200 bg-gradient-to-r from-blue-50/30 to-slate-50/30" data-testid="offer-bar">
      <CardHeader className="pb-2 pt-3 px-4">
        {/* v3.21-1675 · Three-zone grid header — left/CENTER/right.
            View Lender Offers is anchored to the true visual center of
            the Financing Offers container regardless of the number of
            badges on the left or whether Decline All is present. */}
        <CardTitle className="text-sm font-semibold text-slate-700">
          <div className="grid grid-cols-3 items-center gap-2" data-testid="financing-offers-header-grid">
            <div className="flex items-center gap-2 justify-self-start min-w-0">
              <Banknote className="h-4 w-4 text-blue-500 shrink-0" />
              <span className="truncate">Financing Offers</span>
              {isApproved && (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                  <CheckCircle className="h-3 w-3 mr-0.5" />
                  Offer {optionSelected} Selected
                </Badge>
              )}
              {!isApproved && client?.offer_status === 'offer_sent' && (
                <Badge className="bg-blue-100 text-blue-700 text-[10px]" data-testid="offer-status-sent">
                  Offer Sent
                </Badge>
              )}
              {client?.offer_status === 'offer_selected' && (
                <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid="offer-status-selected">
                  Offer Selected
                </Badge>
              )}
              {client?.offer_status === 'in_funding' && (
                <Badge className="bg-purple-100 text-purple-700 text-[10px]" data-testid="offer-status-funding">
                  In Funding
                </Badge>
              )}
              {client?.funding_status === 'Confirmed' && (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid="funding-status-confirmed">
                  Funded
                </Badge>
              )}
              {isInternal && client?.selected_lender_name && (
                <Badge className="bg-slate-100 text-slate-600 text-[10px] font-mono" data-testid="selected-lender-badge">
                  {client.selected_lender_name}
                </Badge>
              )}
            </div>
            <div className="flex justify-center" data-testid="view-lender-offers-slot">
              {!isApproved && (hasFinturfIdentifier || hasFinturfOffers || hasOffer1 || hasOffer2) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                  onClick={() => { if (typeof onOpenLenderOffers === 'function') onOpenLenderOffers(); }}
                  data-testid="view-lender-offers-btn"
                >
                  <Banknote className="h-3 w-3 mr-1" />
                  View Lender Offers
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 justify-self-end">
              {!isApproved && (hasOffer1 || hasOffer2) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                  onClick={onDeclineOffers}
                  disabled={decliningOffer || acceptingOffer}
                  data-testid="decline-all-offers"
                >
                  {decliningOffer ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ThumbsDown className="h-3 w-3 mr-1" />}
                  Decline All
                </Button>
              )}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {renderOffer(offer1, 1)}
        {renderOffer(offer2, 2)}
      </CardContent>
      {/* v3.15-921: Send-to-Agent modal — single instance, payload swapped via state. */}
      <SendOfferToAgentModal
        open={!!sendToAgentTarget}
        onOpenChange={(o) => { if (!o) setSendToAgentTarget(null); }}
        client={client}
        offer={sendToAgentTarget?.offer}
        offerNumber={sendToAgentTarget?.offerNumber}
      />
      {/* v3.21-1696: Text-Offer-to-Agent modal — LEGACY single-recipient
          composer.  v3.21-1778 rewires the "Text Offer to Agent" button
          to open the v1776/v1777-secured multi-recipient
          `FinancingResultTeamMessageModal` instead.  The legacy modal
          is kept imported for regression rollback only — the active
          trigger below no longer mounts it.
          NOTE: `TextOfferToAgentModal` intentionally left unmounted. */}
      {/* v3.21-1778 · ACTIVE financing-result composer.
          Multi-recipient checklist + Select All + authoritative Deal
          recipient membership (`_resolve_deal_level_email_recipients`
          v3.15-1086 SSOT) + phone dedup + partial success + preview
          `SMS_ALLOWED_NUMBERS` allowlist gate + VALID_OFFER /
          NO_OFFER_FOUND / SUBMISSION_ERROR sendability.  Rewired
          v1778 — the same "Text Offer to Agent" button now opens this
          composer so the user gets the multi-recipient UI on the
          Financing Offers surface.
          v3.21-1781 · Passes the clicked offer + application identity
          + Deal id so the composer never has to re-derive the current
          result from a secondary lookup.  Fixes the Production
          regression where the modal showed "Could not load current
          financing result" while an offer was visibly present. */}
      <FinancingResultTeamMessageModal
        open={!!textToAgentTarget}
        onOpenChange={(o) => { if (!o) setTextToAgentTarget(null); }}
        client={client}
        initialOffer={textToAgentTarget?.offer || null}
        initialOfferNumber={textToAgentTarget?.offerNumber || null}
        initialApplicationId={
          client?.current_finturf_application_id
          || client?.deal_number
          || null
        }
        initialResultType={'VALID_OFFER'}
        dealId={client?.deal_id || client?.id || null}
      />
    </Card>
  );
};


const MdSplitEditor = ({ client, onSave }) => {
  const [dirPct, setDirPct] = useState(client?.md_director_pct || '');
  const [m1Pct, setM1Pct] = useState(client?.md_manager_1_pct || '');
  const [tmPct, setTmPct] = useState(client?.md_team_member_pct || '');
  const [saving, setSaving] = useState(false);

  const total = (Number(dirPct) || 0) + (Number(m1Pct) || 0) + (Number(tmPct) || 0);
  const isValid = Math.abs(total - 30) < 0.01;
  const hasValues = total > 0;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await onSave('md_director_pct', Number(dirPct) || 0);
      await onSave('md_manager_1_pct', Number(m1Pct) || 0);
      await onSave('md_team_member_pct', Number(tmPct) || 0);
    } catch (e) {
      console.error('Split save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="md-split-editor">
      <p className="text-[10px] text-slate-400 uppercase mb-1">Commission Split (must total 30%)</p>
      <div className="grid grid-cols-3 gap-1">
        <div>
          <label className="text-[9px] text-slate-400 block">Director %</label>
          <Input type="number" className={`h-6 text-xs py-0 px-1 ${hasValues && !isValid ? 'border-red-400 bg-red-50' : ''}`} value={dirPct} onChange={e => setDirPct(e.target.value)} min={0} max={30} data-testid="md-split-director" />
        </div>
        <div>
          <label className="text-[9px] text-slate-400 block">Manager 1 %</label>
          <Input type="number" className={`h-6 text-xs py-0 px-1 ${hasValues && !isValid ? 'border-red-400 bg-red-50' : ''}`} value={m1Pct} onChange={e => setM1Pct(e.target.value)} min={0} max={30} data-testid="md-split-manager1" />
        </div>
        <div>
          <label className="text-[9px] text-slate-400 block">Team Member %</label>
          <Input type="number" className={`h-6 text-xs py-0 px-1 ${hasValues && !isValid ? 'border-red-400 bg-red-50' : ''}`} value={tmPct} onChange={e => setTmPct(e.target.value)} min={0} max={30} data-testid="md-split-team" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className={`text-[10px] font-semibold ${isValid ? 'text-emerald-600' : hasValues ? 'text-red-600' : 'text-slate-400'}`}>
          Total: {total}% {isValid ? '' : hasValues ? '(must be 30%)' : ''}
        </span>
        <Button size="sm" className="h-5 text-[10px] px-2" disabled={!isValid || !hasValues || saving} onClick={handleSave} data-testid="md-split-save">
          {saving ? 'Saving...' : 'Save Split'}
        </Button>
      </div>
    </div>
  );
};

// v3.15-689: Auto-format helpers for SSN and Phone
const formatSSN = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};
const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const stripFormat = (value) => (value || '').replace(/\D/g, '');



export default function DealDashboard({
  client,
  user,
  isInternal,
  canEditFundingInfo,
  canEditPtcPayment,
  companies = [],
  ptcReps = [],
  users = [],
  fetchClient,
  onEdit,
  onSave,
  onMarkPaid,
  onCancelDeal,
  onSetNextAction,
  onCreateInvoice,
  onUpdateField,
  saving = false,
}) {
  // DEBUG: Log client data on every render to verify binding
  console.log("DEALDASHBOARD CLIENT DATA:", client ? {
    city: client.city, state: client.state, zip_code: client.zip_code,
    income: client.income, monthly_income: client.monthly_income,
    loan_amount: client.loan_amount, employment: client.employment,
    ssn: client.ssn ? '***SET***' : 'EMPTY', dob: client.dob || 'EMPTY',
    residence_type: client.residence_type
  } : "NULL");
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [bankingExpanded, setBankingExpanded] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // v3.18-1343 LAC-VERIFY — claim "editing deal ownership" while the
  // operator has an editable field open on this dashboard.  When
  // ``editingField`` clears (save or cancel) the hook releases the
  // claim on the next render; peers viewing the same client watch
  // the row disappear from ClientLiveActivityStrip.
  useLiveActivityClaim({
    clientId: client?.id,
    workflow: 'editing_deal_ownership',
    metadata: { field: editingField || null },
    enabled:  !!(client?.id && editingField),
  });

  // v3.18-1348 LAC-FIX — the Application Entry Assistant is the modal
  // that opens when the rep clicks "Send for Offers" and copy-pastes
  // fields into the lender portal. Previous release (v3.18-1343)
  // claimed only ``editing_deal_ownership`` — the AEA itself was
  // NEVER wrapped, which meant a second operator opening the same
  // client saw only "Also viewing…" and had no idea the first rep
  // was actively entering a credit application. Wire the claim
  // directly to the dialog's open state so peers see a red LIVE
  // ACTIVITY banner the moment the assistant opens and the claim
  // releases the instant it closes / the browser is closed.
  const [showNextActionDialog, setShowNextActionDialog] = useState(false);
  const [nextActionValue, setNextActionValue] = useState(client?.next_action || '');
  
  // v3.14-158: Notes editing state
  const [editingNotes, setEditingNotes] = useState(false);
  const [externalNotesEdit, setExternalNotesEdit] = useState('');
  const [internalNotesEdit, setInternalNotesEdit] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // v3.14-204: Finturf Offer Review Panel state
  const [showOfferPanel, setShowOfferPanel] = useState(false);
  const [finturfOffers, setFinturfOffers] = useState([]);

  // v3.21-1793 · WS2 · Auto Top-2 offers — APPLICATION-SCOPED slot state.
  //
  // Authoritative storage:
  //   `clients.financing_offer_slot_state[<application_id>]["1"|"2"] = {mode, offer_id}`
  //
  // Key = canonical application id + slot number.  Different
  // financing applications for the same Client have INDEPENDENT slot
  // state — a manual override on Application A must not suppress the
  // Recommended #1/#2 auto-fill on Application B.
  //
  // Legacy Client-level booleans (`financing_offer_[1|2]_auto`) are
  // treated as a one-time fallback for the current application only
  // when the map has no entry for it (soft migration).
  //
  // Reset-to-auto: set `mode = "auto"` for that (application, slot)
  // entry via the existing "Use Recommended" flow.
  //
  // Never touches `option_selected` / client acceptance / funding.
  const autoPopulateTopTwoOffers = useCallback(async (offers) => {
    if (!Array.isArray(offers) || offers.length === 0) return;
    if (!client?.id || !isInternal) return;
    // v3.21-1804 · Do NOT gate auto-population on `option_selected` —
    // the operator has explicitly linked the Application ID and wants
    // Recommended #1/#2 to populate.  `option_selected` reflects an
    // older selection cycle and must not silently block a fresh
    // recommendation for the currently linked application.
    const appId = String(
      client?.current_finturf_application_id
      || client?.deal_number
      || ''
    ).trim();
    if (!appId) return;  // No canonical application → nothing to key against.

    const requested = resolveRequestedAmount(client);  // v3.21-1838 · same resolver as Lender Offers
    const { top } = pickTopTwoRecommendations(offers, requested);
    if (!top || top.length === 0) return;

    const slotState = client?.financing_offer_slot_state || {};
    const appState  = slotState[appId] || {};
    // Legacy fallback for records that have not yet been migrated:
    // fall back to the old Client-level booleans ONLY for the current
    // application AND ONLY when the map has no entry for it.
    const legacyAuto1 = client?.financing_offer_1_auto !== false;
    const legacyAuto2 = client?.financing_offer_2_auto !== false;
    const mode1 = appState['1']?.mode || (legacyAuto1 ? 'auto' : 'manual');
    const mode2 = appState['2']?.mode || (legacyAuto2 ? 'auto' : 'manual');

    const opt2  = client?.financing_option_2 || {};
    const slot1Empty = !(client?.approved_amount || client?.loan_amount_requested);
    const slot2Empty = !(opt2.approved_amount || opt2.loan_amount_requested);
    const shouldFillSlot1 = mode1 === 'auto' || slot1Empty;
    const shouldFillSlot2 = mode2 === 'auto' || slot2Empty;

    const nextSlotState = { ...slotState, [appId]: { ...appState } };

    if (shouldFillSlot1 && top[0]) {
      const r = top[0].raw || top[0];
      // v3.21-1804 · Offer-selection MUST NEVER mutate `loan_amount_requested`
      // or `deal_amount` — those are canonical financing-application /
      // Deal fields, not lender-offer attributes.  Auto-populate only
      // the offer-specific slot fields (approved amount, term, payment,
      // rate, lender, offer identity).  Applied For and Deal Amount
      // remain the canonical requested / Deal amounts already stored
      // on the Client record.  Owner-reported v1804 regression fix.
      //
      // v3.21-1818 · REGRESSION FIX — the previous implementation
      // called ``onUpdateField(patch)`` with a single object, but
      // the parent signature is ``(field, value)`` (see the Use
      // Offer Amount button at line ~1119 and the Offer 2 write
      // below).  Passing an object as the first arg meant NOTHING
      // was persisted for Offer 1 — every field went through as
      // ``field=<patch object>, value=undefined`` and was dropped.
      // Iterate and write each canonical field individually.
      const patch = {
        approved_amount:       r?.approved_amount ?? r?.approvedAmount ?? null,
        term:                  r?.term ?? r?.months ?? null,
        payment:               r?.monthly_payment ?? r?.payment ?? null,
        rate:                  r?.apr ?? r?.rate ?? null,
        lending_institution:   r?.lender ?? r?.lender_name ?? r?.lending_institution ?? null,
      };
      nextSlotState[appId]['1'] = { mode: 'auto', offer_id: r?.offer_id ?? r?.offerId ?? null };
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === undefined || v === '') continue;
        try { await onUpdateField(k, v); } catch (e) { /* non-fatal */ }
      }
    }
    if (shouldFillSlot2 && top[1]) {
      const r = top[1].raw || top[1];
      // v3.21-1804 · Same rule for Offer 2 — do NOT mutate
      // loan_amount_requested / deal_amount on financing_option_2.
      const nextOpt2 = {
        ...opt2,
        approved_amount:       r?.approved_amount ?? r?.approvedAmount ?? null,
        term:                  r?.term ?? r?.months ?? null,
        payment:               r?.monthly_payment ?? r?.payment ?? null,
        rate:                  r?.apr ?? r?.rate ?? null,
        lending_institution:   r?.lender ?? r?.lender_name ?? r?.lending_institution ?? null,
      };
      nextSlotState[appId]['2'] = { mode: 'auto', offer_id: r?.offer_id ?? r?.offerId ?? null };
      try {
        await onUpdateField('financing_option_2', nextOpt2);
      } catch (e) { /* non-fatal */ }
    }
    if (top.length === 1 && mode2 === 'auto' && (opt2.approved_amount || opt2.loan_amount_requested)) {
      try {
        await onUpdateField('financing_option_2', {});
        nextSlotState[appId]['2'] = { mode: 'auto', offer_id: null };
      } catch (e) { /* non-fatal */ }
    }
    // Persist the application-scoped slot state map for this
    // application in one write so future refreshes have authoritative
    // per-application-per-slot mode information.
    try {
      await onUpdateField('financing_offer_slot_state', nextSlotState);
    } catch (e) { /* non-fatal */ }
  }, [client, isInternal, onUpdateField]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [selectedOffer1, setSelectedOffer1] = useState(null);
  const [selectedOffer2, setSelectedOffer2] = useState(null);
  const [settingOffers, setSettingOffers] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  
  // v3.14-211: Message PTC user dialog state (for external orgs)
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageTarget, setMessageTarget] = useState({ name: '', role: '' });
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // v3.15-935: strict VO dropdown feed — /api/vo-officers?company_id=...
  // Cached per-company so opening the panel doesn't re-fetch. currentUser is
  // the prop `user` above, aliased for the VO permission-gate readability.
  const currentUser = user;
  const [voOfficersByCompany, setVoOfficersByCompany] = useState({});
  // v3.15-935: Hard-to-Reach save-in-flight state for the toggle in the
  // At Risk control card. Mirrors the at-risk button pattern.
  const [htrBusy, setHtrBusy] = useState(false);
  // v3.15-1029: shared email-draft modal — completes the no-silent-send loop
  // for Hard-to-Reach + Deal-at-Risk. When the toggle endpoint returns
  // {email.draft:true}, populate this state and the modal opens; the rep
  // edits + clicks Send → re-POST with confirm:true.
  const [draftModal, setDraftModal] = useState(null); // { draft, endpoint, basePayload, workflowLabel }

  // v3.15-1104 — CF360 integration modal state
  const [cf360SendOpen, setCf360SendOpen] = useState(false);
  const [cf360MarkReturnedOpen, setCf360MarkReturnedOpen] = useState(false);
  const [cf360PendingTransfer, setCf360PendingTransfer] = useState(null);

  // v3.15-1123 — Let's Get This Deal modal state
  const [rallyModalOpen, setRallyModalOpen] = useState(false);

  // v3.15-1151 — Clear To Collect modal + authorization state
  const [ctcModalOpen, setCtcModalOpen] = useState(false);
  const [ctcAuth, setCtcAuth] = useState(null);          // active authorization (or null)
  const [ctcStale, setCtcStale] = useState(false);
  const [ctcStaleReason, setCtcStaleReason] = useState(null);
  const [ctcSelectedOffer, setCtcSelectedOffer] = useState(null);

  const fetchCtc = useCallback(async () => {
    if (!client?.id) return;
    try {
      const { data } = await api.get(`/clients/${client.id}/clear-to-collect`);
      setCtcAuth(data?.authorization || null);
      setCtcStale(!!data?.is_stale);
      setCtcStaleReason(data?.stale_reason || null);
      setCtcSelectedOffer(data?.selected_offer || null);
    } catch (_e) {
      // soft-fail — keep prior state, button still works
    }
  }, [client?.id]);

  useEffect(() => { fetchCtc(); }, [fetchCtc, client?.updated_at, client?.deal_amount]);
  // v3.21-1745 · Activity SSOT last-date lookup for the four operational
  // reaction actions moved into the new Activity Actions toolbar.  Reads
  // the existing activity_records collection via a thin read-only endpoint
  // (no new date field / parallel timestamp store).  Refreshed after each
  // successful action via fetchActionLastDates().
  const [actionLastDates, setActionLastDates] = useState(null);
  const fetchActionLastDates = useCallback(async () => {
    if (!client?.id) return;
    try {
      const { data } = await api.get(`/clients/${client.id}/action-last-dates`);
      setActionLastDates(data?.last_dates || null);
    } catch (_e) { /* soft-fail — buttons still work; dates show "—" */ }
  }, [client?.id]);
  useEffect(() => { fetchActionLastDates(); }, [fetchActionLastDates, client?.updated_at, client?.last_rallied_at]);
  // v3.21-1746 · Activity toolbar bridge — the operational reaction
  // buttons now live inside the real Activity toolbar in
  // ClientDetailPage.js.  They dispatch window CustomEvents; we
  // handle them here so state / modals / permission gates remain
  // in one place (no duplicated handlers, no parallel workflow).
  useEffect(() => {
    if (!client?.id) return undefined;
    const onAction = async (e) => {
      const detail = e?.detail || {};
      if (detail.clientId && detail.clientId !== client.id) return;
      const action = detail.action;
      try {
        if (action === 'at-risk') {
          const newVal = !client?.at_risk_manual;
          const res = await api.post(`/clients/${client.id}/at-risk`, { at_risk_manual: newVal });
          if (typeof fetchClient === 'function') fetchClient();
          fetchActionLastDates();
          if (newVal && res.data?.email?.draft) {
            setDraftModal({
              draft: res.data.email,
              endpoint: `/clients/${client.id}/at-risk`,
              basePayload: { at_risk_manual: true },
              workflowLabel: 'At-Risk Notification',
            });
          } else {
            toast.success(newVal ? 'Deal flagged as At Risk' : 'At Risk flag cleared');
          }
        } else if (action === 'hard-to-reach') {
          const isOn = !!client?.is_hard_to_reach;
          if (isOn && !window.confirm('Remove the Hard-to-Reach flag for this client?')) return;
          let note = '';
          if (!isOn) {
            const n = window.prompt('Optional note about contact attempts (leave blank to skip):', '');
            if (n === null) return;
            note = n || '';
          }
          setHtrBusy(true);
          try {
            const res = await api.post(`/clients/${client.id}/hard-to-reach`, { active: !isOn, note });
            if (typeof fetchClient === 'function') fetchClient();
            fetchActionLastDates();
            if (!isOn && res.data?.email?.draft) {
              setDraftModal({
                draft: res.data.email,
                endpoint: `/clients/${client.id}/hard-to-reach`,
                basePayload: { active: true, note },
                workflowLabel: 'Hard-to-Reach Recovery Email',
              });
            } else if (isOn) {
              toast.success('Hard-to-Reach flag cleared');
            } else {
              toast.success('Flagged Hard to Reach');
            }
          } finally { setHtrBusy(false); }
        } else if (action === 'rally') {
          setRallyModalOpen(true);
        } else if (action === 'clear-to-collect') {
          setCtcModalOpen(true);
        } else if (action === 'return-to-rep') {
          await api.post(`/clients/${client.id}/return-to-original-rep`);
          toast.success('Deal returned to original rep');
          if (typeof fetchClient === 'function') fetchClient();
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed to run ${action}`);
      }
    };
    window.addEventListener('hub:deal-action', onAction);
    // Broadcast that this dashboard instance can host actions; the
    // buttons in ClientDetailPage.js also derive their date chips from
    // the same /action-last-dates endpoint, so they self-refresh.
    return () => window.removeEventListener('hub:deal-action', onAction);
  }, [client?.id, client?.at_risk_manual, client?.is_hard_to_reach, fetchClient, fetchActionLastDates]);
  // Pull the most recent transfer (for credit_concerns text on the banner)
  useEffect(() => {
    if (!client?.id || client?.cf360_state !== 'in_cf360') {
      setCf360PendingTransfer(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/clients/${client.id}/cf360-transfers`);
        const transfers = res?.data?.transfers || [];
        const active = transfers.find(t => t.status === 'pending' || t.status === 'sent') || transfers[0];
        if (alive) setCf360PendingTransfer(active || null);
      } catch {
        if (alive) setCf360PendingTransfer(null);
      }
    })();
    return () => { alive = false; };
  }, [client?.id, client?.cf360_state]);
  // v3.15-951: latest Hard-to-Reach trigger note, surfaced inline under the
  // HARD TO REACH label so reps can see WHY the flag is on without opening
  // the Activity tab. Sourced from the most recent `client_hard_to_reach`
  // activity entry's metadata.note / description suffix.
  const [htrNote, setHtrNote] = useState('');
  useEffect(() => {
    if (!client?.id || !client?.is_hard_to_reach) { setHtrNote(''); return; }
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/clients/${client.id}/activities`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.activities || []);
        const latest = list.find(a => (a.activity_type || a.type) === 'client_hard_to_reach');
        if (!alive) return;
        const note = latest?.metadata?.note
          || (latest?.description && latest.description.includes(' — ')
              ? latest.description.split(' — ').slice(1).join(' — ')
              : '');
        setHtrNote(note || '');
      } catch {
        if (alive) setHtrNote('');
      }
    })();
    return () => { alive = false; };
  }, [client?.id, client?.is_hard_to_reach]);
  useEffect(() => {
    const coId = client?.company_id;
    if (!coId || voOfficersByCompany[coId]) return;
    // v3.18-1325 — fetch VO team members for ANY company, not just TS-Exit.
    // The endpoint returns [] when no VO Team exists, which is harmless.
    let alive = true;
    api.get(`/vo-officers?company_id=${encodeURIComponent(coId)}`)
      .then(res => {
        if (!alive) return;
        setVoOfficersByCompany(prev => ({ ...prev, [coId]: res.data?.users || [] }));
      })
      .catch(() => { if (alive) setVoOfficersByCompany(prev => ({ ...prev, [coId]: [] })); });
    return () => { alive = false; };
  }, [client?.company_id, companies]);

  // v3.15-971: 10/90 split auto-population for Paid to PTC / Paid to Partner.
  // Applies to BOTH ACH and Credit Card deals. Wire fee deducts ONLY from
  // partner side; PTC amount NEVER changes (always Deal Amount × 0.10).
  // v3.15-976: must_resolve gate guard removed — there is no longer any
  // 409 blocking at the backend, so the auto-recalc cannot loop.
  const splitInitialMountRef = useRef(true);
  useEffect(() => {
    const dealAmt = parseFloat(client?.deal_amount) || 0;
    if (dealAmt <= 0) {
      splitInitialMountRef.current = false;
      return;
    }
    const wireFee = client?.wire_transfer_fee !== undefined && client?.wire_transfer_fee !== null && client?.wire_transfer_fee !== ''
      ? parseFloat(client.wire_transfer_fee) || 0
      : 30;
    const computedPtc = Math.round(dealAmt * 0.10 * 100) / 100;
    const computedPartner = Math.round((dealAmt * 0.90 - wireFee) * 100) / 100;

    if (splitInitialMountRef.current) {
      splitInitialMountRef.current = false;
      const ptcEmpty = client?.amount_paid_to_ptc === undefined || client?.amount_paid_to_ptc === null || client?.amount_paid_to_ptc === '' || parseFloat(client?.amount_paid_to_ptc) === 0;
      const partnerEmpty = client?.amount_paid_to_partner === undefined || client?.amount_paid_to_partner === null || client?.amount_paid_to_partner === '' || parseFloat(client?.amount_paid_to_partner) === 0;
      if (ptcEmpty) onUpdateField && onUpdateField('amount_paid_to_ptc', computedPtc);
      if (partnerEmpty) onUpdateField && onUpdateField('amount_paid_to_partner', computedPartner);
      return;
    }
    // Subsequent changes to deal_amount or wire_transfer_fee — always recompute.
    onUpdateField && onUpdateField('amount_paid_to_ptc', computedPtc);
    onUpdateField && onUpdateField('amount_paid_to_partner', computedPartner);
  }, [client?.deal_amount, client?.wire_transfer_fee]);


  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    setSendingMessage(true);
    try {
      await api.post(`/clients/${client.id}/activities`, {
        type: 'note',
        content: `Message to ${messageTarget.name} (${messageTarget.role}): ${messageText}`,
        is_internal: false,
      });
      setMessageDialogOpen(false);
      setMessageText('');
      if (onSave) onSave();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // v3.21-1675 · Lender Offers modal state.
  // - `finturfOffers`: authoritative Finturf `offers[]` array (never Offer 1/2).
  // - `finturfSelectedOfferId`: provider's selectedOfferId (read-only badge).
  // - `refreshFailed` / `refreshSource`: transparency badges on the modal.
  // - `selectedIndices`: array of chosen offer indices (max 2).  Order
  //   matters: [0]→Offer 1, [1]→Offer 2.  Third selection is blocked.
  const [finturfSelectedOfferId, setFinturfSelectedOfferId] = useState(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [refreshSource, setRefreshSource] = useState(null);
  const [selectedIndices, setSelectedIndices] = useState([]);
  // v3.21-1676 · Manual "Link Existing Finturf Application" state.
  const [showLinkFinturfDialog, setShowLinkFinturfDialog] = useState(false);
  const [linkFinturfIdentifierInput, setLinkFinturfIdentifierInput] = useState('');
  const [linkFinturfPreview, setLinkFinturfPreview] = useState(null); // {summary,would_set_deal_number} | null
  const [linkFinturfLoading, setLinkFinturfLoading] = useState(false);

  const previewLinkFinturf = async () => {
    // v3.21-1677 · Safer legacy linking.  Accept either a raw identifier
    // or a pasted Finturf URL that deterministically contains
    // `/applications/{IDENTIFIER}`.  Normalize: trim + uppercase, reject
    // malformed characters, never guess.
    let raw = (linkFinturfIdentifierInput || '').trim();
    if (!raw) { toast.error('Enter the Finturf Application ID'); return; }
    const urlMatch = raw.match(/\/applications\/([A-Z0-9]{4,})/i);
    if (urlMatch) raw = urlMatch[1];
    const identifier = raw.toUpperCase();
    if (!/^[A-Z0-9]{4,}$/.test(identifier)) {
      toast.error('Malformed Finturf Application ID — allowed characters are letters and digits only.');
      return;
    }
    setLinkFinturfLoading(true);
    try {
      const res = await api.post('/finturf/applications/link', {
        client_id: client.id,
        identifier,
        confirmed: false,
      });
      if (res.data.status === 'preview') {
        setLinkFinturfPreview(res.data);
      } else if (res.data.status === 'not_found') {
        toast.error(res.data.message || 'Application not found. Verify the Finturf Application ID and try again.');
      } else if (res.data.status === 'conflict') {
        toast.error(res.data.message || 'Another client already owns that identifier.');
      } else {
        toast.error('Unexpected preview response');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setLinkFinturfLoading(false);
    }
  };

  const confirmLinkFinturf = async () => {
    const identifier = (linkFinturfIdentifierInput || '').trim().toUpperCase();
    setLinkFinturfLoading(true);
    try {
      const res = await api.post('/finturf/applications/link', {
        client_id: client.id,
        identifier,
        confirmed: true,
      });
      if (res.data.status === 'linked') {
        toast.success(`Linked ${identifier}${res.data.did_set_deal_number ? ' (Client ID set)' : ''}`);
        setShowLinkFinturfDialog(false);
        setLinkFinturfPreview(null);
        setLinkFinturfIdentifierInput('');
        if (fetchClient) fetchClient();
        // Also open the modal automatically to show the newly-linked offers.
        setSelectedIndices([]);
        setShowOfferPanel(true);
        await refreshFinturfOffers();
      } else {
        toast.error(res.data.message || 'Link failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Link failed');
    } finally {
      setLinkFinturfLoading(false);
    }
  };

  // v3.21-1846 · Fix D — AEA Finturf Application ID goes through the Link validation; invalid IDs never touch the client.
  const saveAeaFinturfId = async (_fieldName, value) => {
    let raw = String(value || '').trim();
    const urlMatch = raw.match(/\/applications\/([A-Z0-9]{4,})/i);
    if (urlMatch) raw = urlMatch[1];
    const identifier = raw.toUpperCase();
    if (!/^[A-Z0-9]{4,}$/.test(identifier)) {
      toast.error('Enter a valid Finturf Application ID (letters and digits only).');
      throw new Error('invalid_finturf_id');
    }
    const body = { client_id: client.id, identifier, set_current_application_id: true };
    const preview = await api.post('/finturf/applications/link', { ...body, confirmed: false });
    if (preview.data?.status !== 'preview') {
      toast.error(preview.data?.message || 'Finturf Application ID could not be verified.');
      throw new Error('finturf_id_rejected');
    }
    const res = await api.post('/finturf/applications/link', { ...body, confirmed: true });
    if (res.data?.status !== 'linked') {
      toast.error(res.data?.message || 'Link failed');
      throw new Error('finturf_link_failed');
    }
    toast.success(`Finturf Application ID ${identifier} verified and saved`);
    if (fetchClient) fetchClient();
  };

  const refreshFinturfOffers = async () => {
    if (!client?.id) return;
    setLoadingOffers(true);
    // v3.21-1678 · reset error state before attempting refresh
    setRefreshFailed(false);
    setRefreshSource(null);
    try {
      const res = await api.get(`/finturf/offers/${client.id}/refresh`);
      setFinturfOffers(res.data.offers || []);
      // v3.21-1792 · WS2 — Auto-populate Offer 1 / Offer 2 from the
      // top-two recommendations produced by `pickTopTwoRecommendations`.
      try { await autoPopulateTopTwoOffers(res.data.offers || []); } catch (e) { /* non-fatal */ }
      setFinturfSelectedOfferId(res.data.selected_offer_id || null);
      setRefreshFailed(!!res.data.refresh_failed);
      setRefreshSource(res.data.source || null);
    } catch (_err) {
      // Preserve last-known offers already in state; only flag refresh_failed.
      setRefreshFailed(true);
      setRefreshSource('refresh_error');
    } finally {
      setLoadingOffers(false);
    }
  };

  const toggleOfferSelection = (idx) => {
    setSelectedIndices((prev) => {
      if (prev.includes(idx)) {
        return prev.filter((i) => i !== idx);
      }
      if (prev.length >= 2) {
        toast.error('Maximum 2 offers can be shortlisted. Deselect one first.');
        return prev;
      }
      return [...prev, idx];
    });
  };

  const fetchFinturfOffers = async () => {
    setLoadingOffers(true);
    try {
      const res = await api.get(`/finturf/offers/${client.id}`);
      setFinturfOffers(res.data.offers || []);
      // v3.21-1792 · WS2 — Auto-populate Offer 1 / Offer 2 from ranking.
      try { await autoPopulateTopTwoOffers(res.data.offers || []); } catch (e) { /* non-fatal */ }
      setFinturfSelectedOfferId(res.data.selected_offer_id || null);
    } catch {
      setFinturfOffers([]);
    } finally {
      setLoadingOffers(false);
    }
  };

  const handleSetOffers = async () => {
    if (selectedIndices.length === 0) {
      toast.error('Select at least one offer');
      return;
    }
    setSettingOffers(true);
    try {
      await api.post(`/finturf/offers/${client.id}/set`, {
        offer_1_index: selectedIndices[0],
        offer_2_index: selectedIndices[1] ?? null,
      });
      toast.success('Selected offers copied to Offer 1 / Offer 2');
      setShowOfferPanel(false);
      setSelectedIndices([]);
      if (fetchClient) fetchClient();
    } catch {
      toast.error('Failed to set offers');
    } finally {
      setSettingOffers(false);
    }
  };

  const triggerFinturfSync = async () => {
    try {
      toast.info('Syncing with PTC Finance...');
      const res = await api.post('/finturf/sync/trigger');
      toast.success(`Sync complete: ${res.data.clients_updated} clients updated`);
      fetchFinturfOffers();
    } catch {
      toast.error('Sync failed');
    }
  };
  
  // v3.14-157: New state for Activity, Communication, Tasks
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [emailData, setEmailData] = useState({ to: '', subject: '', body: '' });
  const [smsData, setSmsData] = useState({ to: '', message: '' });
  const [taskData, setTaskData] = useState({ title: '', due_date: '', assigned_to: '', assigned_users: [] });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);

  // v3.14-297: Send for Offers state (client-only, no credit_app dependency)
  const [sendingOffers, setSendingOffers] = useState(false);
  const [offersConfirmOpen, setOffersConfirmOpen] = useState(false);
  const [offersConfirmOwner, setOffersConfirmOwner] = useState(null);

  // v3.18-1348 LAC-FIX — Application Entry Assistant claim.
  // Fires the ``entering_credit_application`` slug the moment the AEA
  // dialog opens; releases immediately on close or navigation.
  // Metadata carries ``owner_number`` so peers can tell WHICH owner's
  // application is being entered (relevant for co-borrower deals).
  useLiveActivityClaim({
    clientId: client?.id,
    workflow: 'entering_credit_application',
    metadata: { owner_number: offersConfirmOwner || 1, source: 'application_entry_assistant' },
    enabled:  !!(client?.id && offersConfirmOpen),
  });

  // v3.14-306: Client Messages state
  const [clientMessages, setClientMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // v3.15-689: Ownership batch-save — sends ALL ownership fields in one transaction
  const saveOwnershipBatch = useCallback(async (changedFields) => {
    const fullPayload = {
      company_id: client?.company_id || null,
      company_name: client?.company_name || null,
      director_name: client?.director_name || null,
      manager_name: client?.manager_name || null,
      sales_rep_id: client?.sales_rep_id || null,
      sales_rep_name: client?.sales_rep_name || null,
      eqf_director_name: client?.eqf_director_name || null,
      eqf_manager_name: client?.eqf_manager_name || null,
      ptc_rep_id: client?.ptc_rep_id || null,
      ptc_rep_name: client?.ptc_rep_name || null,
      ...changedFields,
    };
    await onUpdateField(fullPayload);
  }, [client, onUpdateField]);

  // v3.14-CALENDAR: Appointment scheduling state
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentDuration, setAppointmentDuration] = useState('30');
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [appointmentRep, setAppointmentRep] = useState('');
  const [schedulingAppointment, setSchedulingAppointment] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null); // null | 'checking' | 'available' | 'unavailable'
  const [suggestedSlots, setSuggestedSlots] = useState([]);
  const [busyPeriods, setBusyPeriods] = useState([]);

  // Check availability when date or time changes
  useEffect(() => {
    if (!appointmentDate || !appointmentTime) {
      setAvailabilityStatus(null);
      setSuggestedSlots([]);
      return;
    }
    const checkAvail = async () => {
      setAvailabilityStatus('checking');
      try {
        // Resolve rep_id from rep name
        let repId = '';
        if (appointmentRep && ptcReps.length) {
          const matched = ptcReps.find(r => r.full_name === appointmentRep);
          if (matched) repId = matched.id;
        }
        const res = await api.get('/google/calendar/availability', {
          params: { date: appointmentDate, time: appointmentTime, duration: parseInt(appointmentDuration) || 30, rep_id: repId || undefined }
        });
        if (res.data?.error) {
          setAvailabilityStatus(null);
          return;
        }
        setBusyPeriods(res.data.busy_periods || []);
        if (res.data.available === true) {
          setAvailabilityStatus('available');
          setSuggestedSlots([]);
        } else if (res.data.available === false) {
          setAvailabilityStatus('unavailable');
          setSuggestedSlots(res.data.suggested_slots || []);
        }
      } catch {
        setAvailabilityStatus(null);
      }
    };
    const timer = setTimeout(checkAvail, 400);
    return () => clearTimeout(timer);
  }, [appointmentDate, appointmentTime, appointmentDuration, appointmentRep, ptcReps]);

  const handleScheduleAppointment = async () => {
    if (!appointmentDate || !appointmentTime) {
      toast.error('Please select date and time');
      return;
    }
    setSchedulingAppointment(true);
    try {
      const dt = `${appointmentDate}T${appointmentTime}:00`;
      // Resolve rep_id from rep name
      let repId = '';
      if (appointmentRep && ptcReps.length) {
        const matched = ptcReps.find(r => r.full_name === appointmentRep);
        if (matched) repId = matched.id;
      }
      const res = await api.post('/google/calendar/create-event', {
        client_name: client?.full_name || 'Unknown',
        email: client?.email || '',
        phone: client?.phone || '',
        datetime: dt,
        duration_minutes: parseInt(appointmentDuration) || 30,
        notes: appointmentNotes || '',
        assigned_rep: appointmentRep || client?.ptc_rep_name || '',
        assigned_rep_id: repId || '',
        client_id: client?.id || '',
      });
      if (res.data?.success) {
        await api.put(`/clients/${client.id}`, {
          appointment_event_id: res.data.event_id,
          appointment_event_link: res.data.event_link,
          appointment_datetime: dt,
          appointment_rep: appointmentRep || client?.ptc_rep_name || '',
          appointment_notes: appointmentNotes,
          deal_stage: 'appointment_scheduled',
        });
        toast.success('Appointment scheduled and synced to Google Calendar');
        setShowAppointmentDialog(false);
        if (onSave) onSave();
      } else if (res.data?.error === 'Time slot not available') {
        toast.error('Time slot not available — see suggested times');
        setAvailabilityStatus('unavailable');
        setSuggestedSlots(res.data.suggested_slots || []);
      } else {
        toast.error(res.data?.details || 'Failed to create calendar event');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule appointment');
    } finally {
      setSchedulingAppointment(false);
    }
  };

  const handlePickSuggestedSlot = (slot) => {
    const dt = new Date(slot.start);
    setAppointmentDate(dt.toISOString().slice(0, 10));
    setAppointmentTime(dt.toTimeString().slice(0, 5));
  };

  // v3.14-167: Finturf interactive state REMOVED — lending actions centralized to LendingPage

  // v3.14-192: Fee deductions state
  const [feeDeductions, setFeeDeductions] = useState(client?.fee_deductions || []);
  const [showAddDeduction, setShowAddDeduction] = useState(false);
  const [deductionForm, setDeductionForm] = useState({ target_client_id: '', target_client_name: '', target_invoice_number: '', amount: '', notes: '' });
  const [savingDeduction, setSavingDeduction] = useState(false);
  const [searchClients, setSearchClients] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);

  const totalDeducted = feeDeductions.reduce((sum, d) => sum + (d.amount || 0), 0);
  const approvedAmt = parseFloat(client?.approved_amount || 0);
  const netToPartner = Math.max(0, approvedAmt - totalDeducted);

  const handleSearchClients = async (query) => {
    if (!query || query.length < 2) { setSearchClients([]); return; }
    setSearchingClients(true);
    try {
      const res = await api.get(`/clients?search=${encodeURIComponent(query)}&limit=10`);
      const list = res.data?.clients || res.data || [];
      setSearchClients(Array.isArray(list) ? list.filter(c => c.id !== client?.id) : []);
    } catch { setSearchClients([]); }
    finally { setSearchingClients(false); }
  };

  const handleAddDeduction = async () => {
    if (!deductionForm.target_client_id || !deductionForm.amount) {
      toast.error('Select a client and enter an amount');
      return;
    }
    setSavingDeduction(true);
    try {
      await api.post(`/clients/${client.id}/fee-deductions`, {
        target_client_id: deductionForm.target_client_id,
        target_client_name: deductionForm.target_client_name,
        target_invoice_number: deductionForm.target_invoice_number,
        amount: parseFloat(deductionForm.amount),
        notes: deductionForm.notes,
      });
      toast.success('Fee deduction added');
      setShowAddDeduction(false);
      setDeductionForm({ target_client_id: '', target_client_name: '', target_invoice_number: '', amount: '', notes: '' });
      if (onSave) onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add deduction');
    } finally {
      setSavingDeduction(false);
    }
  };

  const handleRemoveDeduction = async (deductionId) => {
    try {
      await api.delete(`/clients/${client.id}/fee-deductions/${deductionId}`);
      toast.success('Deduction removed');
      if (onSave) onSave();
    } catch { toast.error('Failed to remove deduction'); }
  };

  // Keep fee deductions in sync with client prop
  useEffect(() => {
    setFeeDeductions(client?.fee_deductions || []);
  }, [client?.fee_deductions]);

  // Auto-fetch Finturf offers on mount
  useEffect(() => {
    if (client?.id && isInternal) {
      fetchFinturfOffers();
    }
  }, [client?.id]);

  // v3.14-302: Unified block ordering — all blocks in one sortable context
  const DEFAULT_TOP_ORDER = ['client-info'];
  const DEFAULT_MID_ORDER = ['deal-summary', 'banking-info', 'deal-stages', 'appointment', 'confirmations', 'funding-completion', 'invoice'];
  // v3.14-203: Quick Actions and Funding/ACH blocks hidden for clean UI
  const DEFAULT_LOWER_ORDER = [];

  const storageKey = `dnd-order-${client?.id || 'default'}`;
  
  const [topOrder, setTopOrder] = useState(DEFAULT_TOP_ORDER);
  const [midOrder, setMidOrder] = useState(DEFAULT_MID_ORDER);
  const [lowerOrder, setLowerOrder] = useState(DEFAULT_LOWER_ORDER);

  // Load saved order from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.top) setTopOrder(parsed.top);
        if (parsed.mid) setMidOrder(parsed.mid);
        if (parsed.lower) setLowerOrder(parsed.lower);
      }
    } catch { /* use defaults */ }
  }, [storageKey]);

  const saveOrder = useCallback((top, mid, lower) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ top, mid, lower }));
    } catch { /* ignore storage errors */ }
  }, [storageKey]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ────────────────────────────────────────────────────────────────────────
  // v3.15-1060: DEAL OWNERSHIP DROPDOWN DATASET — HARDCODED HOTFIX.
  //
  // For PTC Inc deals: dropdown lists are LITERAL hardcoded arrays. NO
  // role-merging, NO companyUsers filter, NO ptcReps fallback. Lookup
  // by lowercase email — if the user is present in /api/users, that
  // record is merged in (preserving production id); otherwise the
  // hardcoded fallback object is used so the dropdown always renders.
  //
  // For non-PTC-Inc companies: unchanged baseline (companyUsers + role).
  // ────────────────────────────────────────────────────────────────────────
  const ownershipDropdownData = useMemo(() => {
    const safeUsers = Array.isArray(users) ? users : [];
    const companyId = client?.company_id || null;
    const isPtcInc = PTC_LITERAL_COMPANY_IDS.has(companyId);

    if (isPtcInc) {
      // STRICT HARDCODED ARRAYS — no derivation, no filtering.
      const directorList = _hardenPtcUserList(PTC_INC_LEADERSHIP_USERS, safeUsers);
      const managerList  = _hardenPtcUserList(PTC_INC_LEADERSHIP_USERS, safeUsers);
      const salesRepList = _hardenPtcUserList(PTC_INC_SALES_REP_USERS, safeUsers);
      return {
        companyUsers: [],
        isPtcInc: true,
        ptcDirectorOverrides: directorList,
        ptcManagerOverrides:  managerList,
        ptcSalesRepOverrides: salesRepList,
      };
    }

    // Non-PTC-Inc: original baseline path.
    const companyUsers = companyId
      ? safeUsers.filter(u => u.company_id === companyId && u.is_active !== false)
      : [];
    return {
      companyUsers,
      isPtcInc: false,
      ptcDirectorOverrides: [],
      ptcManagerOverrides:  [],
      ptcSalesRepOverrides: [],
    };
  }, [users, client?.company_id]);

  // v3.14-416: Compute which blocks are visible, and filter midOrder to match
  const visibleMidBlocks = useMemo(() => {
    const visible = new Set(['deal-stages', 'funding-completion']);
    // v3.14-529: Deal Financials only visible to internal PTC users
    if (isInternal) visible.add('deal-summary');
    if (client?.billing_type === 'Invoice') visible.add('invoice');
    if ((client?.billing_type || 'ACH') !== 'Invoice') visible.add('confirmations');
    return visible;
  }, [client?.billing_type, isInternal]);

  const activeMidOrder = useMemo(() => {
    // Ensure midOrder contains all visible blocks (handles new blocks added after saved order)
    const ordered = midOrder.filter(id => visibleMidBlocks.has(id));
    // Add any visible blocks not yet in the saved order
    for (const id of DEFAULT_MID_ORDER) {
      if (visibleMidBlocks.has(id) && !ordered.includes(id)) {
        ordered.push(id);
      }
    }
    return ordered;
  }, [midOrder, visibleMidBlocks]);

  const handleDragEnd = useCallback((event, section) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const reorder = (items) => {
      const oldIdx = items.indexOf(active.id);
      const newIdx = items.indexOf(over.id);
      return arrayMove(items, oldIdx, newIdx);
    };

    if (section === 'top') {
      const newOrder = reorder(topOrder);
      setTopOrder(newOrder);
      saveOrder(newOrder, midOrder, lowerOrder);
    } else if (section === 'mid') {
      const newOrder = reorder(midOrder);
      setMidOrder(newOrder);
      saveOrder(topOrder, newOrder, lowerOrder);
    } else if (section === 'lower') {
      const newOrder = reorder(lowerOrder);
      setLowerOrder(newOrder);
      saveOrder(topOrder, midOrder, newOrder);
    }
    toast.success('Block order saved');
  }, [topOrder, midOrder, lowerOrder, saveOrder]);

  // v3.14-157: Fetch activities on mount
  useEffect(() => {
    if (client?.id) {
      fetchActivities();
      fetchClientMessages();
    }
  }, [client?.id]);

  const fetchActivities = async () => {
    if (!client?.id) return;
    setLoadingActivities(true);
    try {
      const response = await api.get(`/clients/${client.id}/activities?limit=20`);
      setActivities(response.data || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  // v3.14-306: Fetch client messages (emails)
  const fetchClientMessages = async () => {
    if (!client?.id) return;
    setLoadingMessages(true);
    try {
      const response = await api.get(`/clients/${client.id}/emails`);
      setClientMessages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch client messages:', error);
      setClientMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // v3.14-298: Send for Offers handler — per-owner financial fields, client is single source of truth
  const handleSendForOffers = async (ownerNumber) => {
    // v3.21-1806 · EXISTING-APPLICATION BRANCH.
    //
    // When the Hub client already has a canonical Finturf identifier
    // persisted (either the v1793 canonical field
    // `current_finturf_application_id` or the legacy `deal_number`
    // fallback), AEA Submit MUST NOT POST a duplicate application to
    // Finturf.  Instead we refresh offers via the existing sync path
    // which resolves the application via
    // `finturf_matcher.MATCH_EXPLICIT_ASSOCIATION` and then the
    // regular `GET /finturf/offers/{client_id}` returns the current
    // upstream offers.  `fetchFinturfOffers` internally invokes
    // `autoPopulateTopTwoOffers` which — per v1804 contract — never
    // mutates Applied For / Deal Amount.
    //
    // The manual `Link Existing PTC Finance Application` button
    // remains available as recovery/relink for legacy portal-created
    // applications that have not yet been linked, but the ordinary
    // AEA-Submit path no longer requires the operator to click it
    // separately.
    const existingFinturfId = String(
      client?.current_finturf_application_id
      || client?.deal_number
      || ''
    ).trim();
    if (existingFinturfId) {
      setSendingOffers(true);
      try {
        // Refresh the upstream detail so the ranked offers are current.
        // Non-fatal on failure — the offers GET below still works from
        // whatever the last sync captured.
        try { await api.post('/finturf/sync/trigger'); } catch (_e) { /* non-fatal */ }
        await fetchFinturfOffers();
        toast.success(`Refreshed offers for existing application ${existingFinturfId}`);
        if (onSave) onSave();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to refresh existing application offers');
      } finally {
        setSendingOffers(false);
      }
      return;
    }

    const suffix = ownerNumber === 2 ? '_2' : '';
    const dob = client?.[`date_of_birth${suffix}`] || client?.[`dob${suffix}`];
    const ssn = client?.[`ssn${suffix}`];
    const income = client?.[`monthly_income${suffix}`] || client?.[`income${suffix}`];
    const loanAmount = client?.[`loan_amount_requested${suffix}`] || client?.[`loan_amount${suffix}`] || client?.[`requested_amount${suffix}`];
    const missingFields = [];
    if (!dob) missingFields.push('Date of Birth');
    if (!ssn) missingFields.push('SSN');
    if (!income) missingFields.push('Monthly Income');
    if (!loanAmount) missingFields.push('Loan Amount');
    if (missingFields.length > 0) {
      toast.error(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }
    setSendingOffers(true);
    try {
      const res = await api.post(`/v2/clients/${client.id}/send-for-offers`, {
        owner_number: ownerNumber,
      });
      if (res.data?.status === 'success') {
        toast.success(`Offers sent for Owner ${ownerNumber}`);
        if (onSave) onSave();
      } else {
        toast.error(res.data?.finturf_error || 'Failed to send for offers');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send for offers');
    } finally {
      setSendingOffers(false);
    }
  };

  // v3.14-167: Finturf interactive handlers REMOVED — lending actions centralized to LendingPage

  // v3.14-157: Send Email handler
  const handleSendEmail = async () => {
    if (!emailData.to || !emailData.subject) {
      toast.error('Please fill in recipient and subject');
      return;
    }
    setSendingEmail(true);
    try {
      await api.post(`/clients/${client.id}/email`, emailData);
      toast.success('Email sent successfully');
      setShowEmailDialog(false);
      setEmailData({ to: '', subject: '', body: '' });
      fetchActivities(); // Refresh timeline
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // v3.14-157: Send SMS handler
  const handleSendSms = async () => {
    if (!smsData.to || !smsData.message) {
      toast.error('Please fill in phone number and message');
      return;
    }
    setSendingSms(true);
    try {
      await api.post(`/clients/${client.id}/sms`, smsData);
      toast.success('SMS sent successfully');
      setShowSmsDialog(false);
      setSmsData({ to: '', message: '' });
      fetchActivities(); // Refresh timeline
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // v3.14-157: Create Task handler
  const handleCreateTask = async () => {
    if (!taskData.title) {
      toast.error('Please enter a task title');
      return;
    }
    setCreatingTask(true);
    try {
      await api.post('/tasks', {
        title: taskData.title,
        client_id: client.id,
        due_date: taskData.due_date || null,
        assigned_to: taskData.assigned_to || null,
        status: 'pending',
      });
      toast.success('Task created successfully');
      setShowTaskDialog(false);
      setTaskData({ title: '', due_date: '', assigned_to: '' });
      fetchActivities(); // Refresh timeline
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  // v3.14-158: Enter notes edit mode
  const handleStartEditNotes = (e) => {
    e.stopPropagation();
    setExternalNotesEdit(client?.external_notes || '');
    setInternalNotesEdit(client?.internal_notes || '');
    setEditingNotes(true);
    if (!notesExpanded) setNotesExpanded(true);
  };

  // v3.14-158: Cancel notes edit
  const handleCancelEditNotes = () => {
    setEditingNotes(false);
    setExternalNotesEdit('');
    setInternalNotesEdit('');
  };

  // v3.14-158: Save notes
  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const updates = {};
      const changed = [];
      if (externalNotesEdit !== (client?.external_notes || '')) {
        updates.external_notes = externalNotesEdit;
        changed.push('External');
      }
      if (internalNotesEdit !== (client?.internal_notes || '')) {
        updates.internal_notes = internalNotesEdit;
        changed.push('Internal');
      }

      if (Object.keys(updates).length === 0) {
        setEditingNotes(false);
        return;
      }

      // Save via existing update field handler
      await api.put(`/clients/${client.id}`, updates);

      // Log to Activity Timeline
      const changeDesc = `Notes updated (${changed.join(', ')})`;
      try {
        await api.post(`/clients/${client.id}/notes`, {
          content: changeDesc,
          is_internal: false,
        });
      } catch {
        // Timeline log is non-critical
      }

      toast.success('Notes saved');
      setEditingNotes(false);
      fetchActivities();

      // Trigger parent refresh so client data updates
      if (onUpdateField) {
        // Use a no-op field just to trigger refresh, or call the first update
        // Actually we already saved via api.put above, just need parent to re-fetch
      }
      // Force re-fetch by calling onSave (which triggers fetchClient in parent)
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // v3.14-157: Format activity time
  const formatActivityTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return timestamp;
    }
  };

  // v3.14-157: Get activity icon
  const getActivityIcon = (type) => {
    const icons = {
      note: FileText,
      email: Mail,
      sms: MessageSquare,
      status_change: ArrowRight,
      payment: DollarSign,
      task: CheckCircle,
      call: Phone,
    };
    return icons[type] || History;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `$${Number(amount).toLocaleString()}`;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return date;
    }
  };

  // Get stage display name
  const getStageDisplay = (stage) => {
    const stageNames = {
      lead: 'Lead',
      new: 'New',
      contacted: 'Contacted',
      application: 'Application',
      docs: 'Docs',
      submitted: 'Submitted',
      approved: 'Approved',
      contract: 'Contract',
      funded: 'Funded',
    };
    return stageNames[stage] || stage || '-';
  };

  // Get settlement status — v3.14-159: Use existing commission data as source of truth
  const getSettlementStatus = () => {
    const isFundedDeal = client?.funding_status === 'funded' || client?.funding_status === 'Confirmed' || client?.deal_stage === 'funded';
    if (!isFundedDeal) return { status: 'Pending', variant: 'pending' };
    
    // PTC paid: ptc_payment_status == "paid" OR ptc_payment_received_date exists
    const ptcPaid = (client?.ptc_payment_status?.toLowerCase() === 'paid') || !!client?.ptc_payment_received_date || !!client?.ptc_paid_date;
    // Partner paid: vendor_payment_received_date exists OR partner_paid_date exists
    const partnerPaid = !!client?.vendor_payment_received_date || !!client?.partner_paid_date;
    
    if (ptcPaid && partnerPaid) return { status: 'Paid', variant: 'paid' };
    return { status: 'Due', variant: 'due' };
  };

  // Get funds direction
  const getFundsDirection = () => {
    return client?.ach_entered ? 'PTC → Partner' : 'Partner → PTC';
  };

  // Handle inline field save
  const handleFieldSave = async (fieldName) => {
    if (onUpdateField) {
      await onUpdateField(fieldName, editValue);
    }
    setEditingField(null);
    setEditValue('');
  };

  // Handle next action save
  const handleNextActionSave = async () => {
    if (onSetNextAction) {
      await onSetNextAction(nextActionValue);
    }
    setShowNextActionDialog(false);
  };

  const settlementStatus = getSettlementStatus();
  const isFunded = client?.funding_status === 'funded' || client?.funding_status === 'Confirmed' || client?.deal_stage === 'funded';
  
  // Check if Client 2 exists
  const hasClient2 = client?.full_name_2 || client?.email_2 || client?.phone_2;

  // v3.21-1606 · Restore Client 2 / Joint Applicant.  Existing two-applicant
  // deals still render immediately via `hasClient2`.  For single-applicant
  // deals, an "Add Client 2 / Joint Applicant" button reveals the same
  // editable block — no data migration, no schema change.
  const [showClient2, setShowClient2] = useState(false);
  const client2Visible = !!hasClient2 || showClient2;

  // Derive industry category from client's company and fetch field config
  const [fetchedCompany, setFetchedCompany] = useState(null);
  const [fieldConfig, setFieldConfig] = useState(null);
  
  useEffect(() => {
    // For external users: companies prop may be empty, fetch client's company directly
    if (client?.company_id && (!companies?.length || !companies.find(c => c.id === client.company_id))) {
      api.get(`/companies/${client.company_id}`).then(res => {
        setFetchedCompany(res.data);
      }).catch(() => {});
    }
  }, [client?.company_id, companies]);

  const companyIndustry = useMemo(() => {
    if (!client?.company_id) return null;
    const fromProps = companies?.find(c => c.id === client.company_id);
    const company = fromProps || fetchedCompany;
    const industry = company?.industry_category || null;
    console.log("DealDashboard: companyIndustry resolved:", industry, "company:", company?.name, "company_id:", client.company_id);
    return industry;
  }, [client?.company_id, companies, fetchedCompany]);

  // v3.15-1120A: Company-driven schema swap. When the application's
  // associated company has industry_category=business_funding (or the
  // legacy company_type field equals it), the entire Client Info card
  // renders the Business Funding schema (Application ID, business name,
  // contact, amount requested, etc.) and the consumer-credit fields
  // (SSN, DOB, Income, Loan Amount, Loan Purpose, Education, Pay Freq,
  // Residence, Employment) are HIDDEN. Triggered purely by the company
  // industry — no hardcoded references to "Big Think Capital".
  const isBusinessFundingClient = useMemo(() => {
    const co = companies?.find(c => c.id === client?.company_id) || fetchedCompany;
    if (!co) return false;
    return co.industry_category === 'business_funding'
        || co.company_type    === 'business_funding';
  }, [client?.company_id, companies, fetchedCompany]);

  // Fetch field config from DB based on industry (falls back to 'general' server-side)
  useEffect(() => {
    const industry = companyIndustry || 'general';
    api.get(`/field-configs/${industry}`).then(res => {
      setFieldConfig(res.data);
    }).catch(() => {
      setFieldConfig(null);
    });
  }, [companyIndustry]);

  // v3.14-178: Offer acceptance state
  const [acceptingOffer, setAcceptingOffer] = useState(false);
  const [decliningOffer, setDecliningOffer] = useState(false);

  // v3.14-178: Check if external user can accept/deny offers
  const canAcceptOffers = !isInternal && (
    user?.role === 'company_director' || user?.role === 'company_manager' || user?.role === 'company_admin'
  );

  // v3.14-178: Accept offer handler
  const handleAcceptOffer = async (optionNum) => {
    const option = optionNum === 1
      ? { approved_amount: client?.approved_amount, term: client?.term, rate: client?.rate, payment: client?.payment }
      : { approved_amount: client?.financing_option_2?.approved_amount, term: client?.financing_option_2?.term, rate: client?.financing_option_2?.rate, payment: client?.financing_option_2?.payment };

    if (!option.approved_amount) {
      toast.error(`Offer ${optionNum} has no approved amount`);
      return;
    }
    setAcceptingOffer(true);
    try {
      await api.post(`/clients/${client.id}/approve-terms`, {
        approved_amount: parseFloat(option.approved_amount),
        term: option.term || null,
        rate: option.rate ? parseFloat(option.rate) : null,
        payment: option.payment ? parseFloat(option.payment) : null,
        option_selected: optionNum,
      });
      toast.success(`Offer ${optionNum} accepted! PTC team has been notified.`);
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to accept Offer ${optionNum}`);
    } finally {
      setAcceptingOffer(false);
    }
  };

  // v3.14-178: Decline all offers handler
  const handleDeclineOffers = async () => {
    if (!window.confirm('Are you sure you want to decline all financing options? The PTC team will be notified.')) return;
    setDecliningOffer(true);
    try {
      await api.post(`/clients/${client.id}/decline-terms`);
      toast.success('Financing declined. PTC team has been notified.');
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    } finally {
      setDecliningOffer(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ═══ v3.14-300: OFFER BAR — visible to ALL users (external orgs select offers here) ═══ */}
      <OfferBar
        client={client}
        isInternal={isInternal}
        canAcceptOffers={canAcceptOffers}
        acceptingOffer={acceptingOffer}
        decliningOffer={decliningOffer}
        onAcceptOffer={handleAcceptOffer}
        onDeclineOffers={handleDeclineOffers}
        onEdit={onEdit}
        onUpdateField={onUpdateField}
        companyIndustry={companyIndustry}
        finturfOffers={finturfOffers}
        hasFinturfIdentifier={!!((client?.deal_number || '').trim() || (client?.client_external_id || '').trim())}
        onOpenLenderOffers={() => {
          // v3.21-1675 · ALWAYS refresh live from Finturf when the modal
          // opens.  The authoritative source is the Finturf `offers[]`
          // array for the CURRENT application — never Offer 1/Offer 2.
          // On refresh failure, `refreshFinturfOffers` preserves the
          // last-known offers already in state and flags
          // `refreshFailed=true` so the modal shows a transparency banner.
          setSelectedIndices([]);
          setShowOfferPanel(true);
          refreshFinturfOffers();
        }}
      />

      {/* v3.14-204: Finturf actions moved to Finturf Offers block in mid-grid */}

      {/* v3.18-1326 — Offer Stacking action bar.  Sits between the
          Financing Offers section and the Lender Portal section so
          operators can combine multiple approvals into a single
          lender-neutral outbound package.  Inert when Stack Offers is
          off (current single-select workflow unchanged). */}
      <OfferStackingBar client={client} isInternal={isInternal} />

      {/* v3.18-1308 — Lender Portal Credentials.  Renders ONLY for
          authorized internal companies (Prestigious Companies /
          Prestigious Travel Club / EasyQuickFund); nothing appears in
          the DOM for everyone else.  Positioned directly below Offer #2
          and directly above the disposition checkboxes so the section
          visually belongs to the currently selected financing offer. */}
      {client?.id && <LenderPortalSection clientId={client.id} />}

      {/* v3.15-543: Deal status checkboxes — visible to ALL, read-only for external */}
      <div className="flex items-center gap-2 justify-center" data-testid="deal-status-section">
        <div className="flex items-center gap-3" data-testid="deal-status-checkboxes">
          <label className={`flex items-center gap-1.5 ${isInternal ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
            <Checkbox checked={client?.is_declined || false} onCheckedChange={isInternal ? (checked) => onUpdateField('is_declined', checked) : undefined} disabled={!isInternal} className="h-3.5 w-3.5" data-testid="checkbox-declined" />
            <span className="text-xs text-red-600 font-medium">Credit Declined</span>
          </label>
          {/* v3.21-1745 · Send To CF360 — MOVED here (immediately after
              Credit Declined) from the former standalone at-risk-
              control-block Responsiveness row.  Same handler
              (setCf360SendOpen), same role-allowlist, same in_cf360 /
              terminal-state disable logic — only the surface moved.
              Positioned inside the decision/status row so its
              operational relationship to Credit Declined is visually
              obvious. */}
          {isInternal && (() => {
            const role = currentUser?.role || '';
            const canSend = [
              'admin', 'ptc_admin', 'ptc_director', 'ptc_manager', 'ptc_team_member',
              'manager_director', 'manager_1', 'md_team_member',
              'company_director', 'company_manager',
            ].includes(role);
            if (!canSend) return null;
            const isInCf360 = client?.cf360_state === 'in_cf360';
            const isTerminal =
              client?.credit_declined || client?.client_cancelled ||
              client?.company_cancelled || client?.guest_did_not_finance ||
              client?.paid_in_full || client?.all_parties_settled ||
              client?.is_declined || client?.is_cancelled ||
              client?.did_not_finance || client?.deal_resolved;
            const disabled = isInCf360 || !!isTerminal;
            let reason = '';
            if (isInCf360) reason = 'Already in CF360';
            else if (isTerminal) reason = 'Deal is in a terminal outcome';
            return (
              <button
                type="button"
                data-testid="send-to-cf360-button"
                onClick={() => setCf360SendOpen(true)}
                disabled={disabled}
                title={disabled ? reason : 'Send this client to CF360 for credit recovery'}
                className={`text-[11px] px-2.5 py-1 rounded font-medium transition-colors ${
                  disabled
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
              >
                Send To CF360
              </button>
            );
          })()}
          <label className={`flex items-center gap-1.5 ${isInternal ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
            <Checkbox checked={client?.is_cancelled || false} onCheckedChange={isInternal ? (checked) => onUpdateField('is_cancelled', checked) : undefined} disabled={!isInternal} className="h-3.5 w-3.5" data-testid="checkbox-cancelled" />
            <span className="text-xs text-red-600 font-medium">Client Cancelled Deal</span>
          </label>
          <label className={`flex items-center gap-1.5 ${isInternal ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
            <Checkbox checked={client?.company_cancelled || false} onCheckedChange={isInternal ? (checked) => onUpdateField('company_cancelled', checked) : undefined} disabled={!isInternal} className="h-3.5 w-3.5" data-testid="checkbox-company-cancelled" />
            <span className="text-xs text-red-600 font-medium">Company Cancelled Deal</span>
          </label>
          <label className={`flex items-center gap-1.5 ${isInternal ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
            <Checkbox checked={client?.did_not_finance || false} onCheckedChange={isInternal ? (checked) => onUpdateField('did_not_finance', checked) : undefined} disabled={!isInternal} className="h-3.5 w-3.5" data-testid="checkbox-did-not-finance" />
            <span className="text-xs text-amber-600 font-medium">Guest Did Not Finance With Us</span>
          </label>
          {/* v3.18-1349 DEAL-DISPOSITION-CLEANUP — the duplicate
              "Deal Paid In Full All Parties Settled" disposition that
              used to live here has been removed. That state is now
              owned exclusively by the SSOT checkbox at the top of the
              client page (mark_deal_paid_in_full endpoint). Rendering
              it in two places let operators toggle from two different
              controls representing the same operational state; the
              user has ordered that duplication eliminated.
              Historical rows carrying deal_paid_settled=true remain
              readable everywhere. */}
          {/* v3.18-1349 DEAL-DISPOSITION-CLEANUP — "Guest Does Not Own
              Timeshare" replaces the removed duplicate. Backend gates
              visibility to timeshare-exit companies + Prestigious
              Travel Club + EasyQuickFund via
              `_offer_outcome_state.shows_timeshare_no_ownership`. */}
          {client?._offer_outcome_state?.shows_timeshare_no_ownership && (
            <label className={`flex items-center gap-1.5 ${isInternal ? 'cursor-pointer' : 'cursor-default opacity-80'}`}>
              <Checkbox
                checked={client?.guest_does_not_own_timeshare || false}
                onCheckedChange={isInternal ? (checked) => onUpdateField('guest_does_not_own_timeshare', checked) : undefined}
                disabled={!isInternal}
                className="h-3.5 w-3.5"
                data-testid="checkbox-guest-does-not-own-timeshare"
              />
              <span className="text-xs text-slate-700 font-medium">Guest Does Not Own Timeshare</span>
            </label>
          )}
        </div>
      </div>

      {/* v3.21-1746 · Standalone Activity Actions toolbar REMOVED.
          The four operational reaction buttons (Mark as At Risk /
          Mark Hard to Reach / Let's Get This Deal / Clear To Collect)
          + conditional Return to Original Rep now render INSIDE the
          actual existing Activity toolbar in ClientDetailPage.js —
          the same header row that contains Send Email / EQF-PTC /
          Call / Create Task — with a visual separator before the
          operational group.  Those buttons dispatch
          `hub:deal-action` window events; the bridge useEffect below
          listens and invokes DealDashboard's existing handlers so
          all state (DraftEmailModal, RallyModal, CtcModal, htrBusy,
          ctcAuth) stays in one place and no handler / permission /
          workflow was duplicated. */}

      {/* v3.21-1745 — Send To CF360 relocated INTO the deal-status-section
          row above (immediately after Credit Declined).  The four remaining
          operational reaction actions (Mark as At Risk / Mark Hard to Reach
          / Let's Get This Deal / Clear To Collect) are rendered by the
          Activity Actions toolbar below.  The former standalone Deal Risk /
          Responsiveness / Team Rally / Collection Card was removed. */}

      {/* v3.14-204: Finturf sync info — internal only */}
      {isInternal && (client?.finturf_offers_synced_at || client?.finturf_manual_override) && (
        <div className="flex items-center gap-2 justify-center">
          {client?.finturf_offers_synced_at && (
            <span className="text-[10px] text-slate-400">
              Last sync: {new Date(client.finturf_offers_synced_at).toLocaleString()}
            </span>
          )}
          {client?.finturf_manual_override && (
            <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-300">Manual Override</Badge>
          )}
        </div>
      )}

      {/* v3.21-1675 · Lender Offers modal.
          Authoritative source = Finturf `offers[]` for the CURRENT
          application (never Offer 1 / Offer 2).  On open, the parent
          calls `refreshFinturfOffers()` which hits
          `GET /finturf/offers/{id}/refresh` and preserves last-known
          offers on failure. */}
      <Dialog open={showOfferPanel} onOpenChange={(o) => { setShowOfferPanel(o); if (!o) setSelectedIndices([]); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0" data-testid="lender-offers-modal">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-lg flex-wrap">
              <Shield className="h-5 w-5 text-blue-500" />
              Lender Offers — {client?.full_name}
              <Badge variant="outline" className="text-xs" data-testid="lender-offers-total-count">
                {finturfOffers.length} offers
              </Badge>
              {refreshSource === 'finturf_live' && !refreshFailed && (
                <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300" data-testid="lender-offers-live-badge">
                  Live from PTC Finance
                </Badge>
              )}
              {refreshFailed && (
                <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300" data-testid="lender-offers-refresh-failed-badge">
                  Live refresh failed — showing last known
                </Badge>
              )}
              {client?.requested_amount && (
                <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                  Requested: ${Number(client.requested_amount || client.deal_amount || 0).toLocaleString()}
                </Badge>
              )}
              {finturfSelectedOfferId && (
                <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-300" data-testid="lender-offers-finturf-selected-badge">
                  PTC Finance selected offer detected
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
            {loadingOffers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                <span className="ml-2 text-slate-500">Loading offers...</span>
              </div>
            ) : finturfOffers.length === 0 ? (
              <div className="text-center py-8 text-slate-500" data-testid="lender-offers-empty-state">
                <Shield className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                {refreshSource === 'no_identifier' ? (
                  <p data-testid="lender-offers-state-no-identifier">PTC Finance application could not be resolved for this Client ID.</p>
                ) : refreshSource === 'refresh_error' ? (
                  <p data-testid="lender-offers-state-refresh-error">Unable to refresh lender offers &mdash; please try again.</p>
                ) : refreshFailed ? (
                  <p data-testid="lender-offers-state-refresh-failed">Unable to load lender offers &mdash; showing last known data where available.</p>
                ) : (
                  <p data-testid="lender-offers-state-genuine-zero">No lender offers are currently available for this application.</p>
                )}
              </div>
            ) : (
              <LenderOffersWorkspace
                offers={finturfOffers}
                requestedAmount={resolveRequestedAmount(client)}
                selectedIndices={selectedIndices}
                onToggleSelection={toggleOfferSelection}
                isInternal={isInternal}
                finturfSelectedOfferId={finturfSelectedOfferId}
              />
            )}
          </div>

          <div className="flex items-center justify-between px-6 py-3 border-t bg-slate-50/70">
            <div className="text-xs text-slate-600" data-testid="lender-offers-selection-count">
              <span className="font-semibold">{selectedIndices.length} of 2</span> selected
              {selectedIndices.length === 2 && (
                <span className="text-slate-400 ml-2">(Deselect one to change your shortlist)</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowOfferPanel(false)}>Cancel</Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleSetOffers}
                disabled={selectedIndices.length === 0 || settingOffers}
                data-testid="use-selected-offers-btn"
              >
                {settingOffers ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                Use Selected Offers
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* v3.21-1676 · Link Existing Finturf Application dialog. */}
      <Dialog
        open={showLinkFinturfDialog}
        onOpenChange={(o) => {
          setShowLinkFinturfDialog(o);
          if (!o) { setLinkFinturfPreview(null); setLinkFinturfIdentifierInput(''); }
        }}
      >
        <DialogContent className="max-w-lg" data-testid="link-finturf-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Link2 className="h-5 w-5 text-blue-500" />
              Link Existing PTC Finance Application
            </DialogTitle>
          </DialogHeader>
          {!linkFinturfPreview ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-600">
                Enter the exact Finturf Application ID (identifier).
                The Hub will fetch the application read-only for
                confirmation.  Nothing is written until you confirm.
              </p>
              <div className="space-y-1">
                <Label htmlFor="link-finturf-id-input" className="text-[11px] uppercase text-slate-500">Finturf Application ID</Label>
                <Input
                  id="link-finturf-id-input"
                  value={linkFinturfIdentifierInput}
                  onChange={(e) => setLinkFinturfIdentifierInput(e.target.value.toUpperCase())}
                  placeholder="e.g. NAA78631"
                  data-testid="link-finturf-identifier-input"
                  className="font-mono"
                />
              </div>
              <DialogFooter className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowLinkFinturfDialog(false)}>Cancel</Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={previewLinkFinturf}
                  disabled={linkFinturfLoading || !linkFinturfIdentifierInput.trim()}
                  data-testid="link-finturf-preview-btn"
                >
                  {linkFinturfLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Preview
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="link-finturf-preview">
              <p className="text-xs text-slate-600">
                PTC Finance returned the following application.  Confirm that
                this is <span className="font-semibold">{client?.full_name}</span>'s
                application before linking.
              </p>
              <div className="border rounded p-3 bg-slate-50 text-xs space-y-1">
                <div><span className="text-slate-400">Identifier:</span> <span className="font-mono">{linkFinturfPreview.summary.identifier}</span></div>
                <div><span className="text-slate-400">Status:</span> {linkFinturfPreview.summary.status}</div>
                <div><span className="text-slate-400">Requested Amount:</span> ${Number(linkFinturfPreview.summary.requested_amount || 0).toLocaleString()}</div>
                <div><span className="text-slate-400">Max Loan Amount:</span> ${Number(linkFinturfPreview.summary.max_loan_amount || 0).toLocaleString()}</div>
                <div><span className="text-slate-400">Offers:</span> {linkFinturfPreview.summary.offers_count}</div>
                {linkFinturfPreview.summary.offers_count > 0 && (
                  <div className="border-t pt-1 mt-1">
                    <p className="text-slate-400 mb-1">First offer:</p>
                    <div className="pl-2">
                      <div>Lender: <span className="font-semibold">{linkFinturfPreview.summary.offers[0].lender_name || '—'}</span></div>
                      <div>Approved Amount: <span className="font-semibold text-emerald-600">${Number(linkFinturfPreview.summary.offers[0].approved_amount || 0).toLocaleString()}</span></div>
                      <div>APR: {linkFinturfPreview.summary.offers[0].apr}%</div>
                      <div>Monthly Payment: ${Number(linkFinturfPreview.summary.offers[0].monthly_payment || 0).toLocaleString()}/mo</div>
                      <div>Term: {linkFinturfPreview.summary.offers[0].term}</div>
                    </div>
                  </div>
                )}
                {linkFinturfPreview.would_set_deal_number && (
                  <div className="border-t pt-1 mt-1 text-amber-700">
                    Confirming will set Client ID = <span className="font-mono">{linkFinturfPreview.summary.identifier}</span>
                  </div>
                )}
              </div>
              <DialogFooter className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setLinkFinturfPreview(null)}>Back</Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={confirmLinkFinturf}
                  disabled={linkFinturfLoading}
                  data-testid="link-finturf-confirm-btn"
                >
                  {linkFinturfLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  Confirm Link
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ TOP GRID: Client Info (left) + Deal blocks (right) ═══ */}
      {/* v3.15-1104: CF360 dormant banner — rendered above the top grid */}
      <CF360DormantBanner
        client={client}
        currentUser={currentUser}
        pendingTransfer={cf360PendingTransfer}
        onMarkReturnedClick={() => setCf360MarkReturnedOpen(true)}
      />
      {/* v3.21-1832: CF360 company-bucket transfer banner */}
      <CF360TransferBanner client={client} />
      {/* v3.15-1104: CF360 History section — visible whenever the client has
          any CF360 cycle on record (current or past). The component renders
          its own empty/loading/error states, so we gate by cf360_state to
          avoid an unnecessary network call on clients that never touched
          CF360. */}
      {client?.cf360_state && (
        <Card
          className="rounded-none shadow-none border-b"
          data-testid="dashboard-block-cf360-history"
        >
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle
              className="text-sm font-semibold flex items-center gap-2 text-slate-700"
              data-testid="cf360-history-tab-trigger"
            >
              <History className="h-4 w-4 text-slate-500" />
              CF360 History
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <CF360HistoryTab clientId={client.id} />
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-0 items-start" data-testid="top-grid-sortable">
        
        {/* LEFT — Client Info + Deal Financials */}
        <div>
        <Card className="rounded-none shadow-none border-b" data-testid="dashboard-block-client-info">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
              <User className="h-4 w-4 text-slate-500" />
              {isBusinessFundingClient ? 'Business Funding Application' : 'Client Info'}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
          {/* v3.15-1120A: Company-driven schema swap. When the company's
              industry_category=business_funding, render the Business
              Funding schema and SKIP the consumer-credit Client Info
              block entirely. Spec: SSN / DOB / Residence / Employment /
              Income / Education / Loan Amount / Loan Purpose / Pay Freq
              / Credit Application # / Client 1/2 are HIDDEN for BF. */}
          {isBusinessFundingClient ? (
            <div className="space-y-3" data-testid="bf-client-info-panel">
              {/* Application ID — prominent header */}
              <div className="rounded border-2 border-indigo-200 bg-indigo-50/40 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">
                  EQF Application #
                </p>
                <p
                  className="font-mono text-xl text-slate-900 mt-0.5 truncate"
                  data-testid="bf-client-application-id"
                  title="EQF confirmation number (immutable)"
                >
                  {client?.application_id || client?.credit_app_confirmation_number || '—'}
                </p>
                <p className="text-[10px] text-indigo-700 mt-1">
                  EasyQuickFund confirmation number — primary cross-reference between EQF, Hub, and the funding partner.
                </p>
              </div>

              {/* All BF fields per spec Part 7. Values map from the
                  underlying client doc where applicable. Edits persist
                  into client doc via onUpdateField (admin-only). */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <EditableField
                  label="Business Name"
                  value={client?.business_name ?? client?.full_name}
                  fieldName="business_name"
                  onSave={onUpdateField}
                  data-testid="bf-client-business-name"
                />
                <EditableField
                  label="Contact Name"
                  value={client?.contact_name ?? client?.full_name}
                  fieldName="contact_name"
                  onSave={onUpdateField}
                  data-testid="bf-client-contact-name"
                />
                <EditableField
                  label="Phone Number"
                  value={client?.phone_number ?? client?.mobile_phone}
                  fieldName="phone_number"
                  onSave={onUpdateField}
                  data-testid="bf-client-phone"
                />
                <EditableField
                  label="Email Address"
                  value={client?.email_address ?? client?.email}
                  fieldName="email_address"
                  onSave={onUpdateField}
                  data-testid="bf-client-email"
                />
                <EditableField
                  label="Business State"
                  value={client?.business_state ?? client?.state}
                  fieldName="business_state"
                  onSave={onUpdateField}
                  data-testid="bf-client-state"
                />
                <EditableField
                  label="Business Industry"
                  value={client?.business_industry}
                  fieldName="business_industry"
                  onSave={onUpdateField}
                  data-testid="bf-client-business-industry"
                />
                <EditableField
                  label="Amount Requested"
                  value={client?.amount_requested ?? client?.loan_amount_requested ?? client?.deal_amount}
                  fieldName="amount_requested"
                  onSave={onUpdateField}
                  data-testid="bf-client-amount"
                />
                <EditableField
                  label="Time In Business"
                  value={client?.time_in_business}
                  fieldName="time_in_business"
                  onSave={onUpdateField}
                  data-testid="bf-client-tib"
                />
                <EditableField
                  label="Avg Monthly Revenue"
                  value={client?.average_monthly_revenue}
                  fieldName="average_monthly_revenue"
                  onSave={onUpdateField}
                  data-testid="bf-client-amr"
                />
                <EditableField
                  label="Authorization"
                  value={client?.authorization_accepted === true ? 'Accepted'
                       : client?.authorization_accepted === false ? 'Not Accepted'
                       : null}
                  fieldName="authorization_accepted"
                  onSave={onUpdateField}
                  data-testid="bf-client-authorization"
                />
              </div>

              {/* Additional Comments — full width */}
              <div className="pt-2 border-t">
                <EditableField
                  label="Additional Comments"
                  value={client?.additional_comments ?? client?.notes}
                  fieldName="additional_comments"
                  onSave={onUpdateField}
                  data-testid="bf-client-comments"
                />
              </div>

              {/* v3.15-1125 — Send To Partner (company-configurable delivery) */}
              <SendToPartnerBlock
                client={client}
                company={(companies?.find(c => c.id === client?.company_id) || fetchedCompany)}
                onSuccess={() => { if (typeof fetchClient === 'function') fetchClient(); }}
              />

              {/* Cross-link to the BF dashboard */}
              <div className="pt-2 border-t flex items-center justify-between">
                <p className="text-[11px] text-slate-500 italic">
                  Business Funding workflow — consumer credit fields hidden per industry schema.
                </p>
                <a
                  href={`/business-funding?q=${encodeURIComponent(client?.application_id || client?.credit_app_confirmation_number || '')}`}
                  className="text-[11px] text-indigo-700 hover:text-indigo-900 font-medium"
                  data-testid="bf-open-dashboard-link"
                >
                  Open in Business Funding →
                </a>
              </div>
            </div>
          ) : (
          <div className="space-y-2 relative">
            {/* v3.15-1091 — Removed the duplicated header row that mirrored
                `application_id` → "Client ID" and `credit_app_confirmation_number`
                → "Credit Application #". Per-client section below is now the
                single authoritative location. The Re-Link from EQF button +
                ClientLocalTimeBadge were relocated inline with the Client 1
                Credit Application # field. */}
            <div className="grid grid-cols-2 gap-3 pb-2 border-b">
              {/* v3.14-234: Created Date inline */}
              <div>
                <p className="text-[10px] text-slate-400 uppercase mb-0.5">Created</p>
                {isInternal ? (
                  <input
                    type="date"
                    className="text-xs text-slate-700 font-mono bg-transparent border border-slate-200 rounded px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-blue-300"
                    defaultValue={client?.created_at ? client.created_at.split('T')[0] : ''}
                    onBlur={(e) => {
                      const newDate = e.target.value ? new Date(e.target.value).toISOString() : null;
                      if (newDate !== client?.created_at) onUpdateField('created_at', newDate);
                    }}
                    data-testid="created-date-inline"
                  />
                ) : (
                  <p className="text-xs text-slate-500 font-mono">
                    {client?.created_at ? formatETDate(client.created_at) : '-'}
                  </p>
                )}
              </div>
            </div>

            {/* v3.15-724: Deal Source moved to bottom "Payroll Settings" section — duplicate removed from top */}

            {/* Client 1 — compact contact + credit app horizontal */}
            <div>
              {/* v3.19-1415 1PM UPDATE — C-1 label appears only when Client 2 exists.
                  v3.21-1606 · Also shown while Client 2 is being added. */}
              <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1 font-semibold" data-testid="client-1-heading">
                {client2Visible ? (
                  <>
                    <span className="text-slate-700 font-bold mr-1.5" data-testid="coapplicant-label-c1">C-1</span>
                    <span>· Client 1</span>
                  </>
                ) : (
                  'Client 1'
                )}
              </p>
              <div className="space-y-0.5">
                {/* v3.15-765: per-guest Client ID + Credit Application # */}
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-600 mb-1" data-testid="client-1-application-identifiers">
                  <span>EQF Application #: <span className="font-mono text-slate-800" data-testid="client-eqf-application-number">{client?.credit_app_confirmation_number || '—'}</span></span>
                  <span>Finturf Application ID: <span className="font-mono text-slate-800" data-testid="client-finturf-application-id">{client?.current_finturf_application_id || client?.deal_number || '—'}</span></span>
                  {client?.finturf_application_id_conflict && (
                    <span className="text-amber-700" data-testid="client-finturf-id-conflict-flag">
                      Review: new Finturf application {client.finturf_application_id_conflict.new} not saved (existing {client.finturf_application_id_conflict.existing})
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 pb-1.5 mb-1 border-b border-dashed border-slate-200" data-testid="client-1-ids">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase mb-0.5">Business Reference ID</p>
                    <EditableField label="" value={client?.client_external_id} fieldName="client_external_id" onSave={onUpdateField} />
                    {/* v3.21-1676 · Manual link for legacy/portal-created
                        Finturf applications with no deterministic Hub
                        mapping.  Corporate-email-only.  Two-step: preview
                        the Finturf application, then confirm. */}
                    {isInternal && !((client?.deal_number || '').trim() || (client?.client_external_id || '').trim()) && (
                      <button
                        type="button"
                        onClick={() => { setLinkFinturfIdentifierInput(''); setLinkFinturfPreview(null); setShowLinkFinturfDialog(true); }}
                        className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[10px] font-medium"
                        data-testid="link-existing-finturf-application-btn"
                      >
                        <Link2 className="h-3 w-3" />
                        Link Existing PTC Finance Application
                      </button>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="text-[10px] text-slate-400 uppercase">EQF Application #</p>
                      <div className="flex items-center gap-1.5">
                        {/* v3.15-1091 — Re-Link + local-time relocated here from
                            the removed header row. Single source of truth for
                            the Credit App # is the per-client field below. */}
                        <button
                          type="button"
                          title="Re-link from EQF — pulls the credit application # from applications_new / credit_applications by client_id / email / phone."
                          onClick={async () => {
                            try {
                              const res = await api.post(`/clients/${client?.id}/relink-credit-app`);
                              const d = res?.data || {};
                              if (d.ok) {
                                toast.success(`Linked: ${d.matched_value} (from ${d.source_collection})`);
                                if (typeof fetchClient === 'function') fetchClient();
                              } else {
                                toast.warning(d.message || 'No matching EQF application found.');
                              }
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Re-link failed');
                            }
                          }}
                          className="text-[9px] uppercase tracking-wider text-blue-600 hover:text-blue-800 underline decoration-dotted underline-offset-2"
                          data-testid="relink-credit-app-btn"
                        >
                          Re-Link from EQF
                        </button>
                        <ClientLocalTimeBadge client={client} compact testid="client-info-local-time" />
                      </div>
                    </div>
                    <EditableField label="" value={client?.credit_app_confirmation_number} fieldName="credit_app_confirmation_number" onSave={onUpdateField} />
                  </div>
                </div>
                <EditableField label="Name" value={client?.full_name} fieldName="full_name" onSave={onUpdateField} />
                <div className="flex items-center gap-1">
                  <div className="flex-1"><EditableField label="Email" value={client?.email} fieldName="email" onSave={onUpdateField} /></div>
                  {client?.email && <ClickableContact value={client.email} type="email" className="text-[10px] px-1" />}
                </div>
                <div className="flex items-center gap-1">
                  {/* v3.15-1093: Mobile Phone — primary number used for SMS. */}
                  <div className="flex-1"><EditableField label="Mobile Phone" value={formatPhone(client?.mobile_phone || client?.phone)} fieldName="mobile_phone" onSave={(f, v) => onUpdateField(f, stripFormat(v))} formatter={formatPhone} /></div>
                  {(client?.mobile_phone || client?.phone) && <PhoneClickable phone={client.mobile_phone || client.phone} name={client?.full_name} clientId={client?.id} clientName={client?.full_name} className="text-[10px] px-1" showIcon={true} disableModal={true} />}
                </div>
                <div className="flex items-center gap-1">
                  {/* v3.15-1093: Home Phone — fallback for calling when no mobile is on file. SMS does NOT use this. */}
                  <div className="flex-1"><EditableField label="Home Phone" value={formatPhone(client?.home_phone)} fieldName="home_phone" onSave={(f, v) => onUpdateField(f, stripFormat(v))} formatter={formatPhone} /></div>
                  {client?.home_phone && <PhoneClickable phone={client.home_phone} name={client?.full_name} clientId={client?.id} clientName={client?.full_name} className="text-[10px] px-1" showIcon={true} disableModal={true} />}
                </div>
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-1">
                  <EditableField label="Street" value={client?.street_address} fieldName="street_address" onSave={onUpdateField} />
                  <EditableField label="City" value={client?.city} fieldName="city" onSave={onUpdateField} />
                  <EditableField label="State" value={client?.state} fieldName="state" onSave={onUpdateField} />
                  <EditableField label="Zip" value={client?.zip_code} fieldName="zip_code" onSave={onUpdateField} />
                </div>
                {/* Credit App — compact horizontal rows */}
                {(() => {
                  const hasCreditApp = !!(client?.ssn || client?.dob || client?.date_of_birth || client?.income || client?.monthly_income);
                  const needsApp = !hasCreditApp && !client?.credit_app_confirmation_number;
                  const creditPlaceholder = needsApp ? 'Pending App' : 'Add...';
                  return (
                    <>
                    {needsApp && (
                      <div className="flex items-center gap-2 px-2 py-1.5 mt-1 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-medium">Credit Application Not Submitted</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-5 text-[10px] ml-auto border-amber-300 text-amber-700 hover:bg-amber-100 px-2"
                          onClick={() => {
                            const stageEl = document.querySelector('[data-testid="deal-stages-right-internal"]');
                            if (stageEl) stageEl.scrollIntoView({ behavior: 'smooth' });
                          }}
                          data-testid="send-app-link-cta"
                        >
                          <Send className="h-3 w-3 mr-1" /> Send Application Link
                        </Button>
                      </div>
                    )}
                    <div className="grid grid-cols-4 gap-x-2 pt-1 mt-1 border-t border-dashed border-slate-200">
                      <EditableField label="SSN" value={formatSSN(client?.ssn)} fieldName="ssn" onSave={(f, v) => onUpdateField(f, stripFormat(v))} formatter={formatSSN} placeholder={creditPlaceholder} />
                      <EditableField label="DOB" value={client?.dob || client?.date_of_birth} fieldName="dob" inputType="date" onSave={onUpdateField} placeholder={creditPlaceholder} />
                      <EditableField label="Income" value={client?.income || client?.monthly_income} fieldName="income" onSave={onUpdateField} placeholder={creditPlaceholder} />
                      <EditableField label="Employment" value={client?.employment || client?.employer_name} fieldName="employment" onSave={onUpdateField} placeholder={creditPlaceholder} />
                    </div>
                    <div className="grid grid-cols-4 gap-x-2">
                      <EditableField label="Residence" value={client?.residence_type} fieldName="residence_type" onSave={onUpdateField} placeholder={creditPlaceholder} />
                      <EditableField label="Loan Amt" value={client?.loan_amount || client?.requested_amount} fieldName="loan_amount" onSave={onUpdateField} placeholder={creditPlaceholder} />
                      <EditableField label="Loan Purpose" value={client?.loan_purpose} fieldName="loan_purpose" onSave={onUpdateField} placeholder={creditPlaceholder} />
                      <EditableDropdownField label="Education" value={normalizeEducationForDropdown(client?.education_level || client?.education)} fieldName="education_level" onSave={onUpdateField} options={[{value:'college_degree',label:'College Degree'},{value:'bachelors_degree',label:'Bachelors Degree'}]} />
                    </div>
                    </>
                  );
                })()}
                <div className="grid grid-cols-4 gap-x-2">
                  <EditableDropdownField label="Pay Freq." value={client?.pay_frequency} fieldName="pay_frequency" onSave={onUpdateField} options={[{value:'weekly',label:'Weekly'},{value:'bi_weekly',label:'Bi-Weekly'},{value:'monthly',label:'Monthly'},{value:'yearly',label:'Yearly'}]} />
                </div>
              </div>
              {/* v3.15-767: read-only underwriting chips — Client 1 */}
              <UnderwritingChips
                testId="underwriting-chips-client-1"
                income_type={client?.income_type}
                employment_status={client?.employment_status}
                education={client?.education}
                pay_frequency={client?.pay_frequency}
              />
              {/* v3.21-1673 · Start Financing Application — Client 1 (v1672 canonical flow). */}
              <div className="pt-1.5">
                <Button
                  size="sm"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs"
                  onClick={() => { setOffersConfirmOwner(1); setOffersConfirmOpen(true); }}
                  disabled={sendingOffers}
                  data-testid="start-financing-application-client-1"
                >
                  {sendingOffers ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                  Start Financing Application
                </Button>
                {client?.finturf_auto_sent && (
                  <p className="text-[10px] text-slate-400 mt-1" data-testid="prior-application-hint-c1">
                    Prior application(s) already submitted. A new application can still be started when needed.
                  </p>
                )}
              </div>
            </div>
            
            {/* Client 2 — compact horizontal layout matching Client 1.
                v3.19-1415 1PM UPDATE — Entire Client 2 block gated on
                `hasClient2` so single-applicant deals render exactly as before.
                v3.21-1606 · Also revealed when the user clicks
                "Add Client 2 / Joint Applicant" so the joint-applicant fields
                are always reachable without a data migration. */}
            {!client2Visible && (
              <div className="pt-1.5 border-t" data-testid="add-client-2-panel">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs h-7 border-slate-300 hover:border-blue-500 hover:bg-blue-50 text-slate-700"
                  onClick={() => setShowClient2(true)}
                  data-testid="add-client-2-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Client 2 / Joint Applicant
                </Button>
              </div>
            )}
            {client2Visible && (
            <div className="pt-1.5 border-t">
              {/* v3.19-1415 1PM UPDATE — C-2 label. Only rendered when
                  Client 2 exists (this whole block is already conditionally shown). */}
              <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1 font-semibold" data-testid="client-2-heading">
                <span className="text-slate-700 font-bold mr-1.5" data-testid="coapplicant-label-c2">C-2</span>
                <span>· Client 2</span>
              </p>
              <div className="space-y-0.5">
                {/* v3.15-765: per-guest Client ID + Credit Application # */}
                <div className="grid grid-cols-2 gap-3 pb-1.5 mb-1 border-b border-dashed border-slate-200" data-testid="client-2-ids">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase mb-0.5">Business Reference ID</p>
                    <EditableField label="" value={client?.client_external_id_2} fieldName="client_external_id_2" onSave={onUpdateField} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase mb-0.5">EQF Application #</p>
                    <EditableField label="" value={client?.credit_app_confirmation_number_2} fieldName="credit_app_confirmation_number_2" onSave={onUpdateField} />
                  </div>
                </div>
                <EditableField label="Name" value={client?.full_name_2} fieldName="full_name_2" onSave={onUpdateField} />
                <div className="flex items-center gap-1">
                  <div className="flex-1"><EditableField label="Email" value={client?.email_2} fieldName="email_2" onSave={onUpdateField} /></div>
                  {client?.email_2 && <ClickableContact value={client.email_2} type="email" className="text-[10px] px-1" />}
                </div>
                <div className="flex items-center gap-1">
                  {/* v3.15-1093: Client 2 Mobile Phone — primary for SMS. */}
                  <div className="flex-1"><EditableField label="Mobile Phone" value={formatPhone(client?.mobile_phone_2 || client?.phone_2)} fieldName="mobile_phone_2" onSave={(f, v) => onUpdateField(f, stripFormat(v))} formatter={formatPhone} /></div>
                  {(client?.mobile_phone_2 || client?.phone_2) && <PhoneClickable phone={client.mobile_phone_2 || client.phone_2} name={client?.full_name_2} clientId={client?.id} clientName={client?.full_name_2} className="text-[10px] px-1" showIcon={true} disableModal={true} />}
                </div>
                <div className="flex items-center gap-1">
                  {/* v3.15-1093: Client 2 Home Phone — fallback for calling. SMS does NOT use this. */}
                  <div className="flex-1"><EditableField label="Home Phone" value={formatPhone(client?.home_phone_2)} fieldName="home_phone_2" onSave={(f, v) => onUpdateField(f, stripFormat(v))} formatter={formatPhone} /></div>
                  {client?.home_phone_2 && <PhoneClickable phone={client.home_phone_2} name={client?.full_name_2} clientId={client?.id} clientName={client?.full_name_2} className="text-[10px] px-1" showIcon={true} disableModal={true} />}
                </div>
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-1">
                  <EditableField label="Street" value={client?.street_address_2} fieldName="street_address_2" onSave={onUpdateField} />
                  <EditableField label="City" value={client?.city_2} fieldName="city_2" onSave={onUpdateField} />
                  <EditableField label="State" value={client?.state_2} fieldName="state_2" onSave={onUpdateField} />
                  <EditableField label="Zip" value={client?.zip_code_2} fieldName="zip_code_2" onSave={onUpdateField} />
                </div>
                <div className="grid grid-cols-4 gap-x-2 pt-1 mt-1 border-t border-dashed border-slate-200">
                  <EditableField label="SSN" value={client?.ssn_2} fieldName="ssn_2" onSave={onUpdateField} />
                  <EditableField label="DOB" value={client?.dob_2} fieldName="dob_2" inputType="date" onSave={onUpdateField} />
                  <EditableField label="Income" value={client?.income_2} fieldName="income_2" onSave={onUpdateField} />
                  <EditableField label="Employment" value={client?.employment_2} fieldName="employment_2" onSave={onUpdateField} />
                </div>
                <div className="grid grid-cols-4 gap-x-2">
                  <EditableField label="Residence" value={client?.residence_type_2} fieldName="residence_type_2" onSave={onUpdateField} />
                  <EditableField label="Loan Amt" value={client?.loan_amount_2} fieldName="loan_amount_2" onSave={onUpdateField} />
                  <EditableField label="Loan Purpose" value={client?.loan_purpose_2} fieldName="loan_purpose_2" onSave={onUpdateField} />
                  <EditableDropdownField label="Education" value={normalizeEducationForDropdown(client?.education_level_2 || client?.education_2)} fieldName="education_level_2" onSave={onUpdateField} options={[{value:'college_degree',label:'College Degree'},{value:'bachelors_degree',label:'Bachelors Degree'}]} />
                </div>
                <div className="grid grid-cols-4 gap-x-2">
                  <EditableDropdownField label="Pay Freq." value={client?.pay_frequency_2} fieldName="pay_frequency_2" onSave={onUpdateField} options={[{value:'weekly',label:'Weekly'},{value:'bi_weekly',label:'Bi-Weekly'},{value:'monthly',label:'Monthly'},{value:'yearly',label:'Yearly'}]} />
                </div>
              </div>
              {/* v3.15-767: read-only underwriting chips — Client 2 */}
              <UnderwritingChips
                testId="underwriting-chips-client-2"
                income_type={client?.income_type_2}
                employment_status={client?.employment_status_2}
                education={client?.education_2}
                pay_frequency={client?.pay_frequency_2}
              />
              {/* v3.21-1673 · Start Financing Application — Client 2 (v1672 canonical flow). */}
              <div className="pt-1.5">
                <Button
                  size="sm"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs"
                  onClick={() => { setOffersConfirmOwner(2); setOffersConfirmOpen(true); }}
                  disabled={sendingOffers}
                  data-testid="start-financing-application-client-2"
                >
                  {sendingOffers ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                  Start Financing Application
                </Button>
                {client?.finturf_auto_sent && (
                  <p className="text-[10px] text-slate-400 mt-1" data-testid="prior-application-hint-c2">
                    Prior application(s) already submitted. A new application can still be started when needed.
                  </p>
                )}
              </div>
            </div>
            )}{/* /client2Visible */}

            {/* Industry-Specific Fields — dynamically rendered from field config DB.
                v3.15-1225 OWNERSHIP-CLEANUP: the legacy "Ownership & Mortgage"
                group is filtered out here because its authoritative replacement
                is the "Ownership Information" tab (OWNERSHIP-001). UI-only
                filter — the field config, schema, and API remain untouched. */}
            {fieldConfig?.field_groups?.filter((g) => {
              const nm = (g?.group_name || '').trim().toLowerCase();
              return nm !== 'ownership & mortgage' && nm !== 'ownership and mortgage';
            }).map((group) => {
              const enabledFields = group.fields.filter(f => f.enabled);
              if (enabledFields.length === 0) return null;
              return (
                <div key={group.group_name} className="pt-1.5 border-t" data-testid={`industry-fields-${fieldConfig.industry_category}`}>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1 font-semibold">{group.group_name}</p>
                  <div className="space-y-0.5">
                    {/* Render fields in pairs (2-col grid) */}
                    {Array.from({ length: Math.ceil(enabledFields.length / 2) }, (_, i) => {
                      const pair = enabledFields.slice(i * 2, i * 2 + 2);
                      if (pair.length === 2) {
                        return (
                          <div key={pair[0].field_name} className="grid grid-cols-2 gap-x-3">
                            {pair.map(f => (
                              <EditableField key={f.field_name} label={f.label} value={client?.[f.field_name]} fieldName={f.field_name} onSave={onUpdateField} />
                            ))}
                          </div>
                        );
                      }
                      return <EditableField key={pair[0].field_name} label={pair[0].label} value={client?.[pair[0].field_name]} fieldName={pair[0].field_name} onSave={onUpdateField} />;
                    })}
                  </div>
                </div>
              );
            })}

            {/* Notes — compact single-line, saves on blur */}
            <div className="pt-1 border-t">
              <EditableField label="Notes" value={client?.notes} fieldName="notes" onSave={onUpdateField} />
            </div>
          </div>
          )}
          </CardContent>
        </Card>

        {/* v3.21-1743 · Deal Financials REMOVED from the left column and
            MOVED to the right column directly below Funding Completion.
            This removes the largest source of dead vertical space on
            the left side and lets Banking Information / Funding Sources /
            Fee Deductions rise naturally under Client Info.  All
            calculations, permissions, and controls (Gross Deal Amount,
            PTC Fee %, PTC Revenue, Net to Partner, Billing Type,
            Settlement Summary) are preserved verbatim — see the new
            right-column render at data-testid="dashboard-block-deal-summary". */}

        {/* v3.21-1741 · Banking Information — repositioned into the left
            column (previously wasted space) so it sits in the prime upper /
            mid page area beside Deal Ownership / Deal Stages / Funding
            Completion in the right column.  Guest 1 and Guest 2 render
            SIDE BY SIDE on desktop (lg+) and stack cleanly on smaller
            screens.  Internal-only, unchanged permission model.  All
            editable fields, routing-number lookup, BILL v3 ACH pipeline
            (BankingAchPanel — Reveal Routing / Reveal Account / ACH
            amount fields / Authorization method / Capture Authorization /
            Process ACH / Send BILL Payment Link / Manual BILL Payment
            Fallback) preserved verbatim from the v3.21-1740 right-column
            placement. */}
        {isInternal && (
        <Card className="border rounded-none shadow-none border-b" data-testid="banking-info-left-block">
          <CardHeader className="pb-1 pt-2 px-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
              <Landmark className="h-4 w-4 text-slate-500" />
              Banking Information
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-1">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs" data-testid="banking-info-block">
              {/* v3.21-1741: Guest 1 Banking — left half on lg+, top on smaller. */}
              <div className="space-y-1.5" data-testid="banking-guest-1">
                <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Guest 1 Banking</p>
                <EditableField label="Account Holder" value={client?.banking_account_holder} fieldName="banking_account_holder" onSave={onUpdateField} />
                {/* v3.15-1093: Bank Name — manual edit sets `banking_bank_name_manual_override=true`.
                    Auto-fill from routing lookup is suppressed once override is set; routing-change re-arms. */}
                <EditableField label="Bank Name" value={client?.banking_bank_name} fieldName="banking_bank_name" onSave={async (f, v) => {
                  await onUpdateField(f, v);
                  await onUpdateField('banking_bank_name_manual_override', true);
                }} />
                <EditableField label="Routing Number" value={client?.banking_routing_number} fieldName="banking_routing_number" onSave={async (f, v) => {
                  await onUpdateField(f, v);
                  // v3.15-1093: Routing change re-arms auto-fill — clear manual-override and look up the bank.
                  try {
                    const { data } = await api.get(`/banking/routing-lookup/${encodeURIComponent(String(v || '').replace(/\D/g, ''))}`);
                    if (data?.valid && data?.bank_name) {
                      if (!client?.banking_bank_name_manual_override || (client?.banking_routing_number !== v)) {
                        await onUpdateField('banking_bank_name', data.bank_name);
                        await onUpdateField('banking_bank_name_manual_override', false);
                        toast.success(`Bank identified: ${data.bank_name}`);
                      }
                    } else if (data?.valid && !data?.bank_name) {
                      toast.info('Unable to identify bank from routing — please enter Bank Name manually.');
                    } else if (data && data.valid === false) {
                      toast.warning('Routing number checksum failed.');
                    }
                  } catch (_err) { /* swallow — lookup is advisory, never blocks save */ }
                }} />
                <EditableField label="Account Number" value={client?.banking_account_number} fieldName="banking_account_number" onSave={onUpdateField} />
                <EditableField label="Account Type" value={client?.banking_account_type} fieldName="banking_account_type" onSave={onUpdateField} />
                <EditableField label="Confirmation Number" value={client?.banking_ach_confirmation_number || client?.banking_confirmation_number} fieldName="banking_ach_confirmation_number" onSave={onUpdateField} />
                <EditableField label="Date / Time" value={client?.banking_date_time} fieldName="banking_date_time" onSave={onUpdateField} />
                {/* v3.21-1689 — BILL v3 ACH pipeline (Guest 1) — preserved. */}
                <BankingAchPanel dealId={client?.id} clientId={client?.id} guest="g1" guestLabel="Guest 1" />
              </div>

              {/* v3.21-1741: Guest 2 Banking — right half on lg+, below Guest 1
                  on smaller screens.  Divider is a top border on stacked
                  layout, a left border on side-by-side. */}
              <div className="space-y-1.5 pt-2 border-t border-slate-100 lg:pt-0 lg:border-t-0 lg:pl-4 lg:border-l lg:border-slate-100" data-testid="banking-guest-2">
                <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Guest 2 Banking</p>
                <EditableField label="Account Holder" value={client?.banking_account_holder_2} fieldName="banking_account_holder_2" onSave={onUpdateField} />
                <EditableField label="Bank Name" value={client?.banking_bank_name_2} fieldName="banking_bank_name_2" onSave={async (f, v) => {
                  await onUpdateField(f, v);
                  await onUpdateField('banking_bank_name_manual_override_2', true);
                }} />
                <EditableField label="Routing Number" value={client?.banking_routing_number_2} fieldName="banking_routing_number_2" onSave={async (f, v) => {
                  await onUpdateField(f, v);
                  try {
                    const { data } = await api.get(`/banking/routing-lookup/${encodeURIComponent(String(v || '').replace(/\D/g, ''))}`);
                    if (data?.valid && data?.bank_name) {
                      if (!client?.banking_bank_name_manual_override_2 || (client?.banking_routing_number_2 !== v)) {
                        await onUpdateField('banking_bank_name_2', data.bank_name);
                        await onUpdateField('banking_bank_name_manual_override_2', false);
                        toast.success(`Guest 2 bank identified: ${data.bank_name}`);
                      }
                    } else if (data?.valid && !data?.bank_name) {
                      toast.info('Unable to identify Guest 2 bank — please enter Bank Name manually.');
                    } else if (data && data.valid === false) {
                      toast.warning('Guest 2 routing number checksum failed.');
                    }
                  } catch (_err) { /* advisory only */ }
                }} />
                <EditableField label="Account Number" value={client?.banking_account_number_2} fieldName="banking_account_number_2" onSave={onUpdateField} />
                <EditableField label="Account Type" value={client?.banking_account_type_2} fieldName="banking_account_type_2" onSave={onUpdateField} />
                <EditableField label="Confirmation Number" value={client?.banking_ach_confirmation_number_2 || client?.banking_confirmation_number_2} fieldName="banking_ach_confirmation_number_2" onSave={onUpdateField} />
                <EditableField label="Date / Time" value={client?.banking_date_time_2} fieldName="banking_date_time_2" onSave={onUpdateField} />
                {/* v3.21-1689 — BILL v3 ACH pipeline (Guest 2) — preserved. */}
                <BankingAchPanel dealId={client?.id} clientId={client?.id} guest="g2" guestLabel="Guest 2" />
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* v3.18-1283 PAYMENT-008 — Unified Funding Sources block.
            Replaces the historical CC/ACH Confirmations Card. Canonical
            data lives in `client.funding_sources[]`; legacy flat fields
            are compatibility mirrors ONLY. Multi-source (unlimited CC +
            ACH mix) is supported; deal totals derived from the canonical
            array. See backend/routes/funding_sources.py.
            v3.21-1741: repositioned below Banking Information in the left
            column so the payment-processing blocks (Funding Sources +
            Credit Card Authorizations) sit in the lower processing area,
            not in prime upper-page space. */}
        {(client?.billing_type || 'ACH') !== 'Invoice' && (
          <FundingSourcesBlock
            clientId={client?.id}
            canEdit={canEditFundingInfo}
          />
        )}

        {/* v3.21-1744 · Credit Card Authorizations — LEFT COLUMN placement,
            directly below Funding Sources.  Wrapper retains `min-w-0`
            and `max-w-full` so the component reflows cleanly inside the
            left column (the 3fr side of the 3fr:2fr top grid) without
            horizontal overflow.  All controls (Enter New Credit Card,
            current authorization count, Fee Calculator, Client Charge,
            Fee %, Flat $, Amount to Enter in Bill, warnings, existing
            cards, sensitive-data handling, permissions) preserved. */}
        <div className="min-w-0 max-w-full" data-testid="credit-card-auth-right-column-wrapper">
          <CreditCardAuthorizationBlock
            clientId={client?.id}
            userRole={user?.role}
            clientEmail={client?.email}
            clientPhone={client?.phone}
            clientName={client?.full_name}
          />
        </div>

        {/* v3.21-1745 · Standalone Deal Risk / Responsiveness /
            Team Rally / Collection block REMOVED from the Deal-page
            layout.  Its four operational reaction actions (Mark as
            At Risk / Mark Hard to Reach / Let's Get This Deal /
            Clear To Collect) + conditional Return to Original Rep
            were moved into the new Activity Actions toolbar rendered
            at the top of the Deal page (see "v3.21-1745 · Activity
            Actions toolbar" above).  Send To CF360 was moved into
            the deal-status-section between Credit Declined and
            Client Cancelled Deal.  All existing handlers, permission
            gates, DraftEmailModal integration, Activity SSOT, and
            business rules are preserved verbatim in the new
            surfaces — this is a pure UI-relocation pass. */}

        {/* v3.18-1283: Fee Deductions — preserved internal-only surface.
            Reads `is_ach_deal` (kept in sync by the funding-source mirror
            based on the primary source's method) so this block continues
            to show only when the primary source is ACH. */}
        {(client?.billing_type || 'ACH') !== 'Invoice'
          && client?.is_ach_deal !== false
          && isInternal && (
          <Card className="rounded-none shadow-none border-b" data-testid="dashboard-block-fee-deductions">
            <CardHeader className="pb-1 pt-2 px-3">
              <CardTitle className="text-sm font-semibold flex items-center justify-between text-slate-700">
                <span className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500">
                  Fee Deductions
                </span>
                <button
                  onClick={() => setShowAddDeduction(!showAddDeduction)}
                  className="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                  data-testid="add-deduction-btn"
                >+ Add</button>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-2 pt-0">
              {feeDeductions.map((d) => (
                <div key={d.id} className="flex items-center justify-between py-1 group">
                  <span className="text-xs text-slate-600 truncate flex-1">
                    → {d.target_client_name} {d.target_invoice_number ? `(#${d.target_invoice_number})` : ''}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-mono text-red-600">-${Number(d.amount).toLocaleString()}</span>
                    <button
                      onClick={() => handleRemoveDeduction(d.id)}
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all"
                      data-testid={`remove-deduction-${d.id}`}
                    ><X className="h-3 w-3" /></button>
                  </div>
                </div>
              ))}
              {feeDeductions.length > 0 && (
                <div className="mt-1 pt-1 border-t border-slate-200 space-y-0.5">
                  <div className="flex justify-between">
                    <span className="text-[10px] text-slate-500">Total Deducted</span>
                    <span className="text-xs font-mono text-red-600 font-semibold">-${totalDeducted.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] text-slate-500 font-semibold">Net to Partner</span>
                    <span className="text-xs font-mono text-emerald-600 font-bold">${netToPartner.toLocaleString()}</span>
                  </div>
                </div>
              )}
              {showAddDeduction && (
                <div className="mt-2 p-2 bg-slate-50 rounded border space-y-2" data-testid="add-deduction-form">
                  <div>
                    <Input
                      placeholder="Search client name..."
                      className="h-7 text-xs"
                      onChange={(e) => handleSearchClients(e.target.value)}
                      data-testid="deduction-client-search"
                    />
                    {searchClients.length > 0 && (
                      <div className="mt-1 max-h-24 overflow-y-auto border rounded bg-white">
                        {searchClients.map((c) => (
                          <button
                            key={c.id}
                            className={`w-full text-left text-xs px-2 py-1.5 hover:bg-blue-50 ${
                              deductionForm.target_client_id === c.id ? 'bg-blue-100' : ''
                            }`}
                            onClick={() => {
                              setDeductionForm({
                                ...deductionForm,
                                target_client_id: c.id,
                                target_client_name: c.full_name,
                                target_invoice_number: c.invoice_number || '',
                              });
                              setSearchClients([]);
                            }}
                          >
                            {c.full_name} {c.invoice_number ? `(Inv #${c.invoice_number})` : ''}
                          </button>
                        ))}
                      </div>
                    )}
                    {deductionForm.target_client_name && (
                      <p className="text-[10px] text-emerald-600 mt-0.5">
                        Selected: {deductionForm.target_client_name}
                      </p>
                    )}
                  </div>
                  <Input
                    type="number"
                    placeholder="Amount ($)"
                    className="h-7 text-xs"
                    value={deductionForm.amount}
                    onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })}
                    data-testid="deduction-amount"
                  />
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] flex-1"
                      onClick={() => setShowAddDeduction(false)}
                    >Cancel</Button>
                    <Button
                      size="sm"
                      className="h-6 text-[10px] flex-1"
                      onClick={handleAddDeduction}
                      disabled={savingDeduction}
                      data-testid="submit-deduction"
                    >
                      {savingDeduction ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* v3.21-1743 · Credit Card Authorizations REMOVED from the left
            column and MOVED to the right column directly below the new
            right-column Deal Financials block.  This lets Fee Deductions
            close cleanly against Funding Sources and eliminates the
            large dead area at the bottom of the left column.  All
            controls (Enter New Credit Card, current authorization
            count, Fee Calculator, Client Charge, Fee %, Flat $, Amount
            to Enter in Bill, warnings, existing cards, sensitive-data
            handling, permissions) are preserved verbatim — see the new
            right-column render below at
            data-testid="credit-card-auth-right-column-wrapper". */}

        </div>
        <div className="flex flex-col gap-0">

          {/* Deal Ownership — v3.15-689 HARD LOCK
              ORDER IS NON-NEGOTIABLE:
              1. COMPANY (external) — ALWAYS FIRST
              2. EQF TEAM (internal) — ALWAYS SECOND
              DO NOT REORDER. DO NOT SWAP. */}
          <Card className="border rounded-none shadow-none border-b" data-testid="deal-ownership-right-block">
            <CardHeader className="pb-2 pt-3 px-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                <Briefcase className="h-4 w-4 text-slate-500" />
                Deal Ownership
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-1">
              {/* === 1. COMPANY (external org) — SHOWN FIRST === */}
              {(() => {
                // v3.15-1054: dropdown dataset is memoized at component scope
                // (see `ownershipDropdownData` above) and recomputes whenever
                // `users` or `client.company_id` change. This guarantees
                // dropdowns populate AFTER /api/users resolves on cold load
                // / hard refresh, and explicit allowlist users (Lauren, Adris,
                // Jeremy, Jessica, Masha) surface deterministically for PTC
                // Inc deals.
                // v3.15-1059: PTC Inc uses STRICT allowlist (no merge with
                // company-user role filtering) — Director/Manager render
                // ONLY the leadership allowlist, Sales Rep renders ONLY
                // approved PTC team members. Solar/Tradeline/external users
                // are guaranteed-excluded.
                const {
                  companyUsers,
                  isPtcInc,
                  ptcDirectorOverrides,
                  ptcManagerOverrides,
                  ptcSalesRepOverrides,
                } = ownershipDropdownData;
                // De-dupe helper: filter out overrides already present in baseline.
                const dedupeAppend = (baseline, overrides) => {
                  const baselineIds = new Set(baseline.map(u => u.id));
                  return [...baseline, ...overrides.filter(u => !baselineIds.has(u.id))];
                };
                return (
                  <div className="border-b pb-1.5 mb-1.5">
                    <p className="text-[10px] text-amber-600 uppercase font-semibold mb-1">Company</p>
                    <div className="space-y-1">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase mb-0.5">Company</p>
                        <Select value={client?.company_id || ''} onValueChange={async (v) => {
                          const sel = companies.find(c => c.id === v);
                          await saveOwnershipBatch({ company_id: v, company_name: sel?.name || '' });
                        }}>
                          <SelectTrigger className="h-7 text-xs" data-testid="ownership-company-select"><SelectValue placeholder="Select">{client?.company_name || 'Select'}</SelectValue></SelectTrigger>
                          <SelectContent>{companies.filter(c => {
                            // v3.15-748: Company dropdown must contain only real companies.
                            // Marketing Partner is NOT a company — excluded here.
                            const it = String(c.industry_category || '').toLowerCase();
                            const tp = String(c.company_type || '').toLowerCase();
                            const nm = String(c.name || '').toLowerCase();
                            return it !== 'marketing_partner' && tp !== 'marketing_partner' && !nm.includes('marketing partner');
                          }).map(c => (<SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        {/* v3.21-1748 · Canonical SSOT — write director_id AND director_name so
                            client_access.py can authorize on director_id (visible = canonical). */}
                        <p className="text-[10px] text-slate-400 uppercase mb-0.5">Director</p>
                        <Select value={client?.director_id || 'unassigned'} onValueChange={async (v) => {
                          if (v === 'unassigned') {
                            await saveOwnershipBatch({ director_id: null, director_name: null });
                          } else {
                            const dir = [...companyUsers, ...ptcDirectorOverrides].find(u => u.id === v);
                            if (dir) await saveOwnershipBatch({ director_id: dir.id, director_name: dir.full_name });
                          }
                        }}>
                          <OwnershipContactHover
                            user={[...companyUsers, ...ptcDirectorOverrides].find(u => u.id === client?.director_id)}
                            label={client?.director_name || 'Unassigned'}
                            testid="company-director-hover"
                          >
                            <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="company-director-select"><SelectValue>{client?.director_name || 'Unassigned'}</SelectValue></SelectTrigger>
                          </OwnershipContactHover>
                          <SelectContent>
                            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                            {/* v3.15-1059: PTC Inc uses STRICT allowlist
                                (Adris, Lauren, Jeremy). All other companies
                                keep baseline role-eligibility merge logic. */}
                            {groupUsersByCompany(
                              isPtcInc
                                ? ptcDirectorOverrides
                                : dedupeAppend(
                                    companyUsers.filter(u => u.role && DEAL_OWNERSHIP_DIRECTOR_ELIGIBLE_ROLES.has(u.role)),
                                    ptcDirectorOverrides
                                  ),
                              { companies }
                            ).map((g) => (
                              <SelectGroup key={`dir-grp-${g.company_name}`}>
                                <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                                {g.users.map(u => (
                                  <SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`company-director-option-${u.id}`}>{u.full_name}</SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        {/* v3.21-1748 · Canonical SSOT — write manager_id AND manager_name. */}
                        <p className="text-[10px] text-slate-400 uppercase mb-0.5">Manager</p>
                        <Select value={client?.manager_id || 'unassigned'} onValueChange={async (v) => {
                          if (v === 'unassigned') {
                            await saveOwnershipBatch({ manager_id: null, manager_name: null });
                          } else {
                            const mgr = [...companyUsers, ...ptcManagerOverrides].find(u => u.id === v);
                            if (mgr) await saveOwnershipBatch({ manager_id: mgr.id, manager_name: mgr.full_name });
                          }
                        }}>
                          <OwnershipContactHover
                            user={[...companyUsers, ...ptcManagerOverrides].find(u => u.id === client?.manager_id)}
                            label={client?.manager_name || 'Unassigned'}
                            testid="company-manager-hover"
                          >
                            <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="company-manager-select"><SelectValue>{client?.manager_name || 'Unassigned'}</SelectValue></SelectTrigger>
                          </OwnershipContactHover>
                          <SelectContent>
                            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                            {/* v3.15-1059: PTC Inc uses STRICT allowlist
                                (Adris, Lauren, Jeremy). All other companies
                                keep baseline role-eligibility merge logic. */}
                            {groupUsersByCompany(
                              isPtcInc
                                ? ptcManagerOverrides
                                : dedupeAppend(
                                    companyUsers.filter(u => u.role && (DEAL_OWNERSHIP_DIRECTOR_ELIGIBLE_ROLES.has(u.role) || DEAL_OWNERSHIP_MANAGER_ELIGIBLE_ROLES.has(u.role))),
                                    ptcManagerOverrides
                                  ),
                              { companies }
                            ).map((g) => (
                              <SelectGroup key={`mgr-grp-${g.company_name}`}>
                                <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                                {g.users.map(u => (
                                  <SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`company-manager-option-${u.id}`}>{u.full_name}</SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase mb-0.5">Sales Rep 1</p>
                        <Select value={client?.sales_rep_id || 'unassigned'} onValueChange={async (v) => {
                          if (v === 'unassigned') await saveOwnershipBatch({ sales_rep_name: null, sales_rep_id: null, sales_rep_1_id: null, sales_rep_1_name: null });
                          else {
                            // v3.15-1052: also resolve from PTC-Inc Sales Rep allowlist overrides.
                            // v3.21-1749 · Write canonical sales_rep_1_id + sales_rep_1_name AND
                            // the legacy sales_rep_id + sales_rep_name mirror so read/write/access
                            // all agree; backend also mirrors defensively.
                            const rep = companyUsers.find(u => u.id === v)
                              || ptcSalesRepOverrides.find(u => u.id === v);
                            if (rep) await saveOwnershipBatch({
                              sales_rep_name: rep.full_name,
                              sales_rep_id: rep.id,
                              sales_rep_1_id: rep.id,
                              sales_rep_1_name: rep.full_name,
                            });
                          }
                        }}>
                          <OwnershipContactHover
                            user={[...companyUsers, ...ptcSalesRepOverrides].find(u => u.id === client?.sales_rep_id)}
                            label={client?.sales_rep_name || 'Unassigned'}
                            testid="company-salesrep-1-hover"
                          >
                            <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="company-salesrep-1-select"><SelectValue>{client?.sales_rep_name || 'Unassigned'}</SelectValue></SelectTrigger>
                          </OwnershipContactHover>
                          <SelectContent>
                            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                            {/* v3.15-1059: PTC Inc uses STRICT allowlist
                                (approved PTC team members only — strictly
                                excludes Solar/Tradeline/external). All
                                other companies keep baseline companyUsers. */}
                            {groupUsersByCompany(
                              (isPtcInc
                                ? ptcSalesRepOverrides
                                : dedupeAppend(companyUsers, ptcSalesRepOverrides)
                              ).filter(u => u.is_active !== false),
                              { companies }
                            ).map((g) => (
                              <SelectGroup key={`rep1-grp-${g.company_name}`}>
                                <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                                {g.users.map(u => (
                                  <SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`company-salesrep-1-option-${u.id}`}>{u.full_name}</SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* v3.15-1191 CRM-002 — Sales Rep 2 (split-deal support).
                          Backed by the sales_rep_2_id/name fields added in CRM-001.
                          Same picker pool as Sales Rep 1. Optional — unassigned by default. */}
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase mb-0.5">Sales Rep 2</p>
                        <Select value={client?.sales_rep_2_id || 'unassigned'} onValueChange={async (v) => {
                          if (v === 'unassigned') await saveOwnershipBatch({ sales_rep_2_id: null, sales_rep_2_name: null });
                          else {
                            const rep = companyUsers.find(u => u.id === v)
                              || ptcSalesRepOverrides.find(u => u.id === v);
                            if (rep) await saveOwnershipBatch({ sales_rep_2_id: rep.id, sales_rep_2_name: rep.full_name });
                          }
                        }}>
                          <OwnershipContactHover
                            user={[...companyUsers, ...ptcSalesRepOverrides].find(u => u.id === client?.sales_rep_2_id)}
                            label={client?.sales_rep_2_name || 'Unassigned'}
                            testid="company-salesrep-2-hover"
                          >
                            <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="company-salesrep-2-select"><SelectValue>{client?.sales_rep_2_name || 'Unassigned'}</SelectValue></SelectTrigger>
                          </OwnershipContactHover>
                          <SelectContent>
                            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                            {groupUsersByCompany(
                              (isPtcInc
                                ? ptcSalesRepOverrides
                                : dedupeAppend(companyUsers, ptcSalesRepOverrides)
                              ).filter(u => u.is_active !== false && u.id !== client?.sales_rep_id),
                              { companies }
                            ).map((g) => (
                              <SelectGroup key={`rep2-grp-${g.company_name}`}>
                                <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                                {g.users.map(u => (
                                  <SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`company-salesrep-2-option-${u.id}`}>{u.full_name}</SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* v3.15-1191 CRM-002 — Presenter (PTC ownership slot).
                          Backed by presenter_id/name fields added in CRM-001. */}
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase mb-0.5">Presenter</p>
                        <Select value={client?.presenter_id || 'unassigned'} onValueChange={async (v) => {
                          if (v === 'unassigned') await saveOwnershipBatch({ presenter_id: null, presenter_name: null });
                          else {
                            const rep = companyUsers.find(u => u.id === v)
                              || ptcSalesRepOverrides.find(u => u.id === v);
                            if (rep) await saveOwnershipBatch({ presenter_id: rep.id, presenter_name: rep.full_name });
                          }
                        }}>
                          <OwnershipContactHover
                            user={[...companyUsers, ...ptcSalesRepOverrides].find(u => u.id === client?.presenter_id)}
                            label={client?.presenter_name || 'Unassigned'}
                            testid="company-presenter-hover"
                          >
                            <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="company-presenter-select"><SelectValue>{client?.presenter_name || 'Unassigned'}</SelectValue></SelectTrigger>
                          </OwnershipContactHover>
                          <SelectContent>
                            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                            {groupUsersByCompany(
                              (isPtcInc
                                ? ptcSalesRepOverrides
                                : dedupeAppend(companyUsers, ptcSalesRepOverrides)
                              ).filter(u => u.is_active !== false),
                              { companies }
                            ).map((g) => (
                              <SelectGroup key={`presenter-grp-${g.company_name}`}>
                                <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                                {g.users.map(u => (
                                  <SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`company-presenter-option-${u.id}`}>{u.full_name}</SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* v3.15-935: VO Officer — strict is_vo=true filter
                          via /api/vo-officers?company_id=... + permission gate.
                          Only PTC Admin or the matching Company Director can
                          actually save a change (backend enforces too). */}
                      {(() => {
                        const co = companies.find(c => c.id === client?.company_id);
                        const isTimeshareExit = co && ((co.company_type === 'timeshare_exit') || (co.industry_category === 'timeshare_exit'));
                        const voOfficers = voOfficersByCompany[client?.company_id] || [];
                        // v3.18-1325 — show the dropdown whenever the company
                        // has an active VO Team (Teams are the new authority),
                        // even for non-TS companies.  Also keep it visible for
                        // legacy TS-Exit companies that haven't had a Team
                        // created yet, and for any deal already carrying a VO
                        // assignment (so operators can clear it).
                        if (!isTimeshareExit && voOfficers.length === 0 && !client?.vo_officer_id) return null;
                        const canAssignVo = (
                          currentUser?.role === 'ptc_admin' ||
                          (currentUser?.role === 'company_director' &&
                           currentUser?.company_id &&
                           currentUser?.company_id === client?.company_id)
                        );
                        return (
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase mb-0.5">
                              VO Officer
                              {!canAssignVo && (
                                <span
                                  className="ml-1.5 text-[9px] normal-case text-slate-400"
                                  title="Only Admin or the Company Director can assign a VO"
                                  data-testid="vo-officer-readonly-hint"
                                >(read-only)</span>
                              )}
                            </p>
                            <Select
                              value={client?.vo_officer_id || 'unassigned'}
                              disabled={!canAssignVo}
                              onValueChange={async (v) => {
                                if (!canAssignVo) return;
                                if (v === 'unassigned') {
                                  await saveOwnershipBatch({ vo_officer_id: null, vo_officer_name: null });
                                } else {
                                  const vo = voOfficers.find(u => u.id === v);
                                  if (vo) await saveOwnershipBatch({ vo_officer_id: vo.id, vo_officer_name: vo.full_name });
                                }
                              }}
                            >
                              <SelectTrigger className="h-7 text-xs" data-testid="company-vo-officer-select"><SelectValue>{client?.vo_officer_name || 'Unassigned'}</SelectValue></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                                {/* v3.18-1352 — helpful empty state so operators know
                                    the query ran and returned zero eligible users. */}
                                {voOfficers.length === 0 && (
                                  <div
                                    className="px-2 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-100 leading-snug"
                                    data-testid="vo-officer-empty-hint"
                                  >
                                    No eligible VO Officers for this company.
                                    <br />
                                    Designate a VO in HR (role = <b>vo_officer</b>) for this company, or invite a @prestigioustravelclub.org teammate to be assigned.
                                  </div>
                                )}
                                {/* v3.18-1354 — VO picker: Designated (this company) + PTC Guest (@prestigioustravelclub.org) */}
                                {voOfficers.map(u => {
                                  const isDesig  = u.eligibility?.is_designated_vo;
                                  const isGuest  = u.eligibility?.is_ptc_guest;
                                  return (
                                    <SelectItem key={u.id} value={u.id} className="text-xs"
                                                data-testid={`company-vo-officer-option-${u.id}`}>
                                      <div className="flex items-center justify-between gap-3 w-full">
                                        <span className="font-medium text-slate-800 truncate">{u.full_name}</span>
                                        <span className="flex items-center gap-2 shrink-0">
                                          {u.primary_role && u.primary_role !== '—' && (
                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">{u.primary_role}</span>
                                          )}
                                          {isDesig && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">Designated VO</span>
                                          )}
                                          {isGuest && !isDesig && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">PTC Guest</span>
                                          )}
                                        </span>
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              {/* === 2. EQF TEAM (internal) — SHOWN SECOND === */}
              <div>
                <p className="text-[10px] text-blue-500 uppercase font-semibold mb-1">EQF Team</p>
                <div className="space-y-1">
                  <div>
                    {/* v3.21-1748 · Canonical SSOT — write eqf_director_id AND eqf_director_name. */}
                    <p className="text-[10px] text-slate-400 uppercase mb-0.5">Director</p>
                    <Select value={client?.eqf_director_id || 'unassigned'} onValueChange={async (v) => {
                      if (v === 'unassigned') {
                        await saveOwnershipBatch({ eqf_director_id: null, eqf_director_name: null });
                      } else {
                        const dir = ptcReps.find(u => u.id === v);
                        if (dir) await saveOwnershipBatch({ eqf_director_id: dir.id, eqf_director_name: dir.full_name });
                      }
                    }}>
                      <OwnershipContactHover
                        user={ptcReps.find(u => u.id === client?.eqf_director_id)}
                        label={client?.eqf_director_name || 'Unassigned'}
                        testid="eqf-director-hover"
                      >
                        <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="eqf-director-select"><SelectValue>{client?.eqf_director_name || 'Unassigned'}</SelectValue></SelectTrigger>
                      </OwnershipContactHover>
                      <SelectContent>
                        <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                        {/* v3.15-1057: grouped by company. */}
                        {groupUsersByCompany(
                          ptcReps.filter(u => u.role === 'ptc_director' || u.role === 'ptc_admin'),
                          { companies }
                        ).map((g) => (
                          <SelectGroup key={`eqf-dir-${g.company_name}`}>
                            <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                            {g.users.map(u => (<SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`eqf-director-option-${u.id}`}>{u.full_name}</SelectItem>))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    {/* v3.21-1748 · Canonical SSOT — write eqf_manager_id AND eqf_manager_name. */}
                    <p className="text-[10px] text-slate-400 uppercase mb-0.5">Manager</p>
                    <Select value={client?.eqf_manager_id || 'unassigned'} onValueChange={async (v) => {
                      if (v === 'unassigned') {
                        await saveOwnershipBatch({ eqf_manager_id: null, eqf_manager_name: null });
                      } else {
                        const mgr = ptcReps.find(u => u.id === v);
                        if (mgr) await saveOwnershipBatch({ eqf_manager_id: mgr.id, eqf_manager_name: mgr.full_name });
                      }
                    }}>
                      <OwnershipContactHover
                        user={ptcReps.find(u => u.id === client?.eqf_manager_id)}
                        label={client?.eqf_manager_name || 'Unassigned'}
                        testid="eqf-manager-hover"
                      >
                        <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="eqf-manager-select"><SelectValue>{client?.eqf_manager_name || 'Unassigned'}</SelectValue></SelectTrigger>
                      </OwnershipContactHover>
                      <SelectContent>
                        <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                        {/* v3.15-1057: grouped by company. */}
                        {groupUsersByCompany(
                          ptcReps.filter(u => u.role === 'ptc_manager' || u.role === 'ptc_director' || u.role === 'ptc_admin'),
                          { companies }
                        ).map((g) => (
                          <SelectGroup key={`eqf-mgr-${g.company_name}`}>
                            <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                            {g.users.map(u => (<SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`eqf-manager-option-${u.id}`}>{u.full_name}</SelectItem>))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase mb-0.5">Application Concierge</p>
                    {/* v3.21-1748 · Application Concierge SSOT is application_concierge_id
                        (NOT ptc_rep_id). ptc_rep_id is mirrored server-side for legacy
                        commission triggers, but it no longer authorizes access. */}
                    <Select value={client?.application_concierge_id || 'unassigned'} onValueChange={async (v) => {
                      if (v === 'unassigned') {
                        await saveOwnershipBatch({
                          application_concierge_id: null,
                          application_concierge_name: null,
                          ptc_rep_id: null,
                          ptc_rep_name: null,
                        });
                      } else {
                        const rep = ptcReps.find(r => r.id === v);
                        if (rep) await saveOwnershipBatch({
                          application_concierge_id: rep.id,
                          application_concierge_name: rep.full_name,
                          // Legacy mirror — kept in sync so legacy reports/triggers keep working.
                          ptc_rep_id: rep.id,
                          ptc_rep_name: rep.full_name,
                        });
                      }
                    }}>
                      <OwnershipContactHover
                        user={ptcReps.find(u => u.id === client?.application_concierge_id)}
                        label={client?.application_concierge_name || 'Unassigned'}
                        testid="eqf-concierge-hover"
                      >
                        <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="eqf-concierge-select"><SelectValue>{client?.application_concierge_name || 'Unassigned'}</SelectValue></SelectTrigger>
                      </OwnershipContactHover>
                      <SelectContent>
                        <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                        {/* v3.15-1057: grouped by company.
                            v3.15-1065: when this is a PTC deal, the Application
                            Concierge dropdown must NOT show Solar / Tradeline
                            test reps. We restrict to ptcReps whose company_id
                            is in the PTC allowlist so the placeholder
                            `Rep sol-…` accounts cannot leak into PTC deals. */}
                        {groupUsersByCompany(
                          ownershipDropdownData.isPtcInc
                            ? ptcReps.filter(u => PTC_LITERAL_COMPANY_IDS.has(u.company_id))
                            : ptcReps,
                          { companies }
                        ).map((g) => (
                          <SelectGroup key={`eqf-conc-${g.company_name}`}>
                            <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                            {g.users.map(u => (<SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`eqf-concierge-option-${u.id}`}>{u.full_name}</SelectItem>))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* === 3. MARKETING PARTNER (referral) — SHOWN BELOW EQF TEAM (v3.15-748) === */}
              <div data-testid="marketing-partner-section" className="border-t pt-1.5 mt-1.5">
                <p className="text-[10px] text-purple-500 uppercase font-semibold mb-1">Marketing Partner</p>
                <div className="space-y-1">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase mb-0.5">Marketing Partner</p>
                    {(() => {
                      const marketingPartners = users.filter(u => u.role === 'marketing_partner' && u.is_active !== false);
                      return (
                        <Select value={client?.marketing_partner_id || 'unassigned'} onValueChange={async (v) => {
                          if (v === 'unassigned') await saveOwnershipBatch({ marketing_partner_id: null, marketing_partner_name: null });
                          else { const mp = marketingPartners.find(u => u.id === v); if (mp) await saveOwnershipBatch({ marketing_partner_id: mp.id, marketing_partner_name: mp.full_name }); }
                        }}>
                          <OwnershipContactHover
                            user={marketingPartners.find(u => u.id === client?.marketing_partner_id)}
                            label={client?.marketing_partner_name || 'Unassigned'}
                            testid="marketing-partner-hover"
                          >
                            <SelectTrigger className={OWNERSHIP_TRIGGER_CLASS} data-testid="marketing-partner-select"><SelectValue>{client?.marketing_partner_name || 'Unassigned'}</SelectValue></SelectTrigger>
                          </OwnershipContactHover>
                          <SelectContent>
                            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                            {/* v3.15-1057: grouped by company. */}
                            {groupUsersByCompany(marketingPartners, { companies }).map((g) => (
                              <SelectGroup key={`mp-grp-${g.company_name}`}>
                                <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{g.company_name}</SelectLabel>
                                {g.users.map(u => (<SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`marketing-partner-option-${u.id}`}>{u.full_name}</SelectItem>))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* v3.21-1744 · Deal Risk / Operational Actions block was here
              (v3.15-697 at-risk-control-block).  MOVED to the LEFT column
              between Credit Card Authorizations and Fee Deductions so the
              right column ends cleanly after Deal Financials and the
              lower Activity / Compensation Tracking / Payroll area can
              rise naturally.  All five actions (Mark as At Risk / Send
              To CF360 / Mark Hard to Reach / Let's Get This Deal / Clear
              To Collect) + Return to Original Rep are preserved verbatim
              in the new left-column render. */}


          {/* v3.21-1741 · Banking Information moved to LEFT column (upper /
              mid) with Guest 1 / Guest 2 rendered side-by-side.  Deal
              Stages and Funding Completion now naturally sit higher in
              this right column (immediately after Deal Ownership /
              At-Risk) rather than being stranded below the tall stacked
              banking block.  Old right-column banking-info-right-block
              removed. */}

          {/* Deal Stages — repositioned upward (v3.21-1741) */}
          {isInternal && (
          <Card className="border rounded-none shadow-none border-b" data-testid="deal-stages-right-internal">
            <CardHeader className="pb-2 pt-3 px-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                <TrendingUp className="h-4 w-4 text-slate-500" />
                Deal Stages
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3">
              <DealStagesChecklist clientId={client?.id} isInternal={isInternal} client={client} companyIndustry={companyIndustry} />
            </CardContent>
          </Card>
          )}

          {/* Funding Completion — under Deal Stages
              v3.21-1625: canonical statuses are now Pending Deposit /
              Partially Funded / Funded.  Legacy "Pending" (Sentence case
              or lowercase) rows continue to render as "Pending Deposit"
              via the compat mapping below — no destructive migration
              is required.  Value stored under `funding_status`. */}
          <Card className="border rounded-none shadow-none border-b" data-testid="funding-completion-right">
            <CardHeader className="pb-1 pt-2 px-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                <CheckCircle className="h-4 w-4 text-slate-500" />
                Funding Completion
                {client?.current_finturf_application_id || client?.deal_number ? (
                  <span
                    className="ml-2 text-[10px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"
                    data-testid="funding-completion-client-id"
                    title="Finturf Application ID"
                  >
                    Finturf Application ID: {client.current_finturf_application_id || client.deal_number}
                  </span>
                ) : null}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-3 pb-2">
              <div className="space-y-1.5 text-xs" data-testid="funding-completion-block-right">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-0.5">Status</p>
                  {(() => {
                    // Compat mapping — historic "Pending"/"pending" is
                    // presented as the new "Pending Deposit" bucket so
                    // old rows keep rendering without a data migration.
                    const raw = client?.funding_status || '';
                    const norm = (typeof raw === 'string' && raw.trim().toLowerCase() === 'pending')
                                 ? 'Pending Deposit'
                                 : raw;
                    return (
                      <Select value={norm} onValueChange={v => onUpdateField('funding_status', v)}>
                        <SelectTrigger className="h-7 text-xs" data-testid="funding-status-select"><SelectValue placeholder="Select">{norm || 'Select'}</SelectValue></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending Deposit"  className="text-xs" data-testid="funding-status-opt-pending-deposit">Pending Deposit</SelectItem>
                          <SelectItem value="Partially Funded" className="text-xs" data-testid="funding-status-opt-partially-funded">Partially Funded</SelectItem>
                          <SelectItem value="Funded"           className="text-xs" data-testid="funding-status-opt-funded">Funded</SelectItem>
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>
                <EditableField label="Funded Date" value={client?.funded_date} fieldName="funded_date" onSave={onUpdateField} inputType="date" />
                <EditableField label="Funded Amount" value={client?.funded_amount} fieldName="funded_amount" onSave={onUpdateField} highlight={!!client?.funded_amount} />
              </div>
            </CardContent>
          </Card>

          {/* v3.21-1743 · Deal Financials — RIGHT COLUMN placement.
              Moved from the left column so the payment-processing area
              (Deal Financials → Credit Card Authorizations) sits in the
              right column below the workflow/status area (Deal Ownership
              → At-Risk → Deal Stages → Funding Completion).  Internal-only
              (external orgs continue to see their own Deal Financials
              variant at data-testid="deal-financials-right-block" further
              below).  All calculations, permissions, and Settlement
              Summary preserved verbatim from the previous left-column
              placement. */}
          {isInternal && (
          <Card className="border rounded-none shadow-none border-b" data-testid="dashboard-block-deal-summary">
            <CardHeader className="pb-1 pt-2 px-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                <DollarSign className="h-4 w-4 text-slate-500" />
                Deal Financials
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-3 pb-2">
              <div className="space-y-1" data-testid="deal-financials-waterfall">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-0.5">Gross Deal Amount</p>
                  <EditableField label="" value={client?.deal_amount} fieldName="deal_amount" onSave={async (field, val) => {
                    // v3.15-1062: STRICT — Gross Deal Amount onSave updates
                    // ONLY deal_amount + derived PTC Revenue / Net to Partner
                    // (those are pure computations of deal_amount × ptc_fee%).
                    // It does NOT mutate approved_amount (the lender's
                    // authoritative approval) under any condition.
                    const pct = parseFloat(client?.ptc_fee_percent) || 10;
                    const gross = parseFloat(val) || 0;
                    const updates = { [field]: val };
                    if (gross && pct) {
                      updates.ptc_revenue = Math.round(gross * pct / 100 * 100) / 100;
                      updates.net_to_partner = Math.round((gross - updates.ptc_revenue) * 100) / 100;
                    }
                    console.log(`[FIN-1062] gross_deal_amount write`, updates);
                    await onUpdateField(updates);
                  }} highlight={!!client?.deal_amount} />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-0.5">PTC Fee %</p>
                  <EditableField label="" value={client?.ptc_fee_percent || '10'} fieldName="ptc_fee_percent" onSave={async (field, val) => {
                    // v3.15-1062: STRICT — PTC Fee % onSave updates ptc_fee_percent
                    // + derived PTC Revenue / Net to Partner ONLY. Does NOT
                    // touch approved_amount or deal_amount.
                    const gross = parseFloat(client?.deal_amount) || 0;
                    const pct = parseFloat(val) || 0;
                    const updates = { [field]: val };
                    if (gross && pct) {
                      updates.ptc_revenue = Math.round(gross * pct / 100 * 100) / 100;
                      updates.net_to_partner = Math.round((gross - updates.ptc_revenue) * 100) / 100;
                    }
                    console.log(`[FIN-1062] ptc_fee_percent write`, updates);
                    await onUpdateField(updates);
                  }} />
                </div>
                <div className="bg-emerald-50 rounded px-2 py-1 border border-emerald-200">
                  <p className="text-[10px] text-emerald-600 uppercase font-semibold">PTC Revenue ($) — Money In</p>
                  <p className="text-sm font-bold text-emerald-700" data-testid="ptc-revenue-display">{client?.ptc_revenue ? `$${Number(client.ptc_revenue).toLocaleString()}` : '—'}</p>
                </div>
                <div className="bg-blue-50 rounded px-2 py-1 border border-blue-200">
                  <p className="text-[10px] text-blue-600 uppercase font-semibold">Net to Partner — Money Out</p>
                  <p className="text-sm font-bold text-blue-700" data-testid="net-partner-display">{client?.net_to_partner ? `$${Number(client.net_to_partner).toLocaleString()}` : '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-0.5">Billing Type</p>
                  <Select
                    value={client?.billing_type || 'ACH'}
                    onValueChange={async (value) => {
                      // v3.15-1062: STRICT — Billing Type change updates the
                      // billing_type field only. invoice_amount IS allowed
                      // (it's a derived display field for the Invoice flow,
                      // NOT an authoritative lender/financial value).
                      // approved_amount is NEVER touched here — it belongs
                      // to the lender's approval, not PTC's revenue split.
                      const updates = { billing_type: value };
                      if (value === 'Invoice' && client?.ptc_revenue) {
                        updates.invoice_amount = client.ptc_revenue;
                      }
                      console.log(`[FIN-1062] billing_type write`, updates);
                      await onUpdateField(updates);
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs" data-testid="billing-type-select">
                      <SelectValue placeholder="Select billing type">{client?.billing_type || 'ACH'}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Invoice" className="text-xs">Invoice</SelectItem>
                      <SelectItem value="ACH" className="text-xs">ACH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(() => {
                  const bt = client?.billing_type || 'ACH';
                  return (
                    <div className={`text-[10px] rounded px-2 py-1.5 border ${bt === 'Invoice' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                      {bt === 'Invoice'
                        ? `→ Enter PTC Revenue ($${client?.ptc_revenue ? Number(client.ptc_revenue).toLocaleString() : '...'}) into Invoice "Total Owed to PTC"`
                        : `→ Enter PTC Revenue ($${client?.ptc_revenue ? Number(client.ptc_revenue).toLocaleString() : '...'}) into Confirmations "Approved ACH Amt"`
                      }
                    </div>
                  );
                })()}
                {/* v3.15-706: Settlement Summary — correct calculations */}
                <div className="pt-1 border-t space-y-1" data-testid="settlement-summary">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Settlement Summary</p>
                  {(() => {
                    const dealAmt = parseFloat(client?.deal_amount) || 0;
                    const feePct = parseFloat(client?.ptc_fee_percent) || 10;
                    const ptcRev = parseFloat(client?.ptc_revenue) || (dealAmt > 0 ? Math.round(dealAmt * feePct / 100 * 100) / 100 : 0);
                    const paidToPartner = parseFloat(client?.net_to_partner) || (dealAmt > 0 ? Math.round((dealAmt - ptcRev) * 100) / 100 : 0);
                    const settlement = Math.round((ptcRev - (parseFloat(client?.amount_paid_to_partner) || 0)) * 100) / 100;
                    const fmt = (v) => v > 0 ? `$${v.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 2})}` : '—';
                    return (
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                        <div><p className="text-[10px] text-slate-400">Deal Amount</p><p className="text-xs font-medium text-slate-700">{fmt(dealAmt)}</p></div>
                        <div><p className="text-[10px] text-slate-400">PTC Revenue</p><p className="text-xs font-medium text-emerald-700">{fmt(ptcRev)}</p></div>
                        <div><p className="text-[10px] text-slate-400">Paid to Partner</p><p className="text-xs font-medium text-blue-700">{fmt(paidToPartner)}</p></div>
                        <div><p className="text-[10px] text-slate-400">Settlement Total</p><p className="text-xs font-bold text-slate-900" data-testid="settlement-total">{dealAmt > 0 ? fmt(settlement >= 0 ? settlement : 0) : '—'}</p></div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* v3.21-1744 · Credit Card Authorizations were here in the
              right column.  MOVED to the LEFT column between Funding
              Sources and the Deal Risk / Actions block so the right
              column ends cleanly after Deal Financials. */}

          {/* v3.15-543: Deal Financials — in right column for external orgs */}
          {!isInternal && (
          <Card className="border rounded-lg" data-testid="deal-financials-right-block">
            <CardHeader className="pb-2 pt-3 px-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                <DollarSign className="h-4 w-4 text-slate-500" />
                Deal Financials
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3">
              <div className="space-y-1" data-testid="deal-financials-waterfall-ext">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-0.5">Gross Deal Amount</p>
                  <EditableField label="" value={client?.deal_amount} fieldName="deal_amount" onSave={async (field, val) => {
                    // v3.15-1062: STRICT — Gross Deal Amount onSave updates
                    // ONLY deal_amount + derived PTC Revenue / Net to Partner
                    // (those are pure computations of deal_amount × ptc_fee%).
                    // It does NOT mutate approved_amount (the lender's
                    // authoritative approval) under any condition.
                    const pct = parseFloat(client?.ptc_fee_percent) || 10;
                    const gross = parseFloat(val) || 0;
                    const updates = { [field]: val };
                    if (gross && pct) {
                      updates.ptc_revenue = Math.round(gross * pct / 100 * 100) / 100;
                      updates.net_to_partner = Math.round((gross - updates.ptc_revenue) * 100) / 100;
                    }

                    console.log(`[FIN-1062] gross_deal_amount write`, updates);
                    await onUpdateField(updates);
                  }} highlight={!!client?.deal_amount} />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-0.5">PTC Fee %</p>
                  <EditableField label="" value={client?.ptc_fee_percent || '10'} fieldName="ptc_fee_percent" onSave={async (field, val) => {
                    // v3.15-1062: STRICT — PTC Fee % onSave updates ptc_fee_percent
                    // + derived PTC Revenue / Net to Partner ONLY. Does NOT
                    // touch approved_amount or deal_amount.
                    const gross = parseFloat(client?.deal_amount) || 0;
                    const pct = parseFloat(val) || 0;
                    const updates = { [field]: val };
                    if (gross && pct) {
                      updates.ptc_revenue = Math.round(gross * pct / 100 * 100) / 100;
                      updates.net_to_partner = Math.round((gross - updates.ptc_revenue) * 100) / 100;
                    }

                    console.log(`[FIN-1062] ptc_fee_percent write`, updates);
                    await onUpdateField(updates);
                  }} />
                </div>
                <div className="bg-emerald-50 rounded px-2 py-1 border border-emerald-200">
                  <p className="text-[10px] text-emerald-600 uppercase font-semibold">PTC Revenue ($) — Money In</p>
                  <EditableField label="" value={client?.ptc_revenue} fieldName="ptc_revenue" onSave={onUpdateField} highlight={true} />
                </div>
                <div className="bg-blue-50 rounded px-2 py-1 border border-blue-200">
                  <p className="text-[10px] text-blue-600 uppercase font-semibold">Net to Partner — Money Out</p>
                  <EditableField label="" value={client?.net_to_partner} fieldName="net_to_partner" onSave={onUpdateField} highlight={true} />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-0.5">Billing Type</p>
                  <Select
                    value={client?.billing_type || 'ACH'}
                    onValueChange={async (value) => {
                      // v3.15-1062: STRICT — Billing Type change writes
                      // billing_type only (+ invoice_amount mirror for the
                      // Invoice flow). approved_amount is NEVER touched.
                      const updates = { billing_type: value };
                      if (value === 'Invoice' && client?.ptc_revenue) updates.invoice_amount = client.ptc_revenue;

                      console.log(`[FIN-1062] billing_type (ext) write`, updates);
                      await onUpdateField(updates);
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs" data-testid="billing-type-select-ext">
                      <SelectValue placeholder="Select billing type">{client?.billing_type || 'ACH'}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Invoice" className="text-xs">Invoice</SelectItem>
                      <SelectItem value="ACH" className="text-xs">ACH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-0.5 border-t">
                  <EditableField label="Partner Paid" value={client?.vendor_payment_received_date || client?.partner_paid_date} fieldName="partner_paid_date" onSave={onUpdateField} inputType="date" />
                  <EditableField label="Funding Date" value={client?.funding_date} fieldName="funding_date" onSave={onUpdateField} inputType="date" />
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* v3.15-543: Deal Stages — in right column for external orgs */}
          {!isInternal && (
          <Card className="border rounded-lg" data-testid="deal-stages-right-block">
            <CardHeader className="pb-2 pt-3 px-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                <TrendingUp className="h-4 w-4 text-slate-500" />
                Deal Stages
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3">
              <DealStagesChecklist clientId={client?.id} isInternal={isInternal} client={client} companyIndustry={companyIndustry} />
            </CardContent>
          </Card>
          )}

        </div>
      </div>

      {/* v3.14-302: Unified sortable grid — all blocks draggable */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'mid')}>
        <SortableContext items={activeMidOrder} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 items-start" data-testid="unified-block-grid">

        {/* Deal Financials moved to left column under Client Info */}

        {/* Banking Information — moved to right column under Deal Ownership (v3.15) */}

        {/* v3.14-529: Deal Ownership moved to right column next to Client Info */}

        {/* Deal Stages moved to right column under Banking */}

        {/* v3.14-CALENDAR: Appointment Block — moved to Activity tabs area v3.14-414 */}
        {false && (
        <SortableDashboardBlock id="appointment" title="Appointment" icon={Calendar} compact>
          <div className="space-y-2 text-xs" data-testid="appointment-block">
            {client?.appointment_event_id ? (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="appointment-scheduled-info">
                  <div className="flex items-center gap-1.5 mb-2">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="font-semibold text-emerald-700 text-xs">Appointment Scheduled</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Date / Time</span>
                      <span className="font-medium text-slate-800" data-testid="appointment-datetime">
                        {client?.appointment_datetime ? new Date(client.appointment_datetime).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}
                      </span>
                    </div>
                    {client?.appointment_rep && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Rep</span>
                        <span className="font-medium text-slate-800" data-testid="appointment-rep">{client.appointment_rep}</span>
                      </div>
                    )}
                    {client?.appointment_notes && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Notes</span>
                        <span className="text-slate-600 truncate max-w-[180px]">{client.appointment_notes}</span>
                      </div>
                    )}
                  </div>
                  {client?.appointment_event_link && (
                    <a
                      href={client.appointment_event_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-medium text-xs"
                      data-testid="appointment-calendar-link"
                    >
                      <Calendar className="h-3 w-3" />
                      View in Calendar
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                {/* Reschedule button */}
                {isInternal && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-xs h-7"
                    onClick={() => setShowAppointmentDialog(true)}
                    data-testid="reschedule-appointment-btn"
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    Reschedule
                  </Button>
                )}
              </>
            ) : (
              <div className="text-center py-2">
                <p className="text-slate-400 mb-2" data-testid="no-appointment">No appointment scheduled</p>
                {isInternal && (
                  <Button
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs"
                    onClick={() => setShowAppointmentDialog(true)}
                    data-testid="schedule-appointment-btn"
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    Schedule Appointment
                  </Button>
                )}
              </div>
            )}
          </div>
        </SortableDashboardBlock>
        )}

        {/* Block 3: Invoice — only visible when Invoice billing explicitly selected */}
        {client?.billing_type === 'Invoice' && (
        <SortableDashboardBlock id="invoice" title="" icon={FileText} compact orderIndex={activeMidOrder.indexOf('invoice')} customHeader={          <div className="flex items-center justify-between w-full">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <FileText className="h-4 w-4 text-slate-500" />
              Invoice
            </span>
            {!client?.is_ach_deal && (
              <Badge className="bg-amber-100 text-amber-700 text-[10px]">Due</Badge>
            )}
          </div>
        }>
          <div className={`space-y-1.5 text-xs ${client?.is_ach_deal ? 'opacity-30 pointer-events-none select-none' : ''}`} data-testid="invoice-content">
            <EditableField
              label="Invoice #"
              value={client?.invoice_number}
              fieldName="invoice_number"
              onSave={onUpdateField}
            />
            <EditableField
              label="Creation Date"
              value={client?.invoice_creation_date}
              fieldName="invoice_creation_date"
              onSave={onUpdateField}
              inputType="date"
            />
            <EditableField
              label="Due Date"
              value={client?.invoice_due_date}
              fieldName="invoice_due_date"
              onSave={onUpdateField}
              inputType="date"
            />
            <EditableField
              label="Total Owed to PTC"
              value={client?.invoice_amount}
              fieldName="invoice_amount"
              onSave={onUpdateField}
              highlight={true}
            />
            <EditableField
              label="Payment Date"
              value={client?.invoice_payment_date}
              fieldName="invoice_payment_date"
              onSave={onUpdateField}
              inputType="date"
            />
            <EditableField
              label="Payment Type"
              value={client?.invoice_payment_type}
              fieldName="invoice_payment_type"
              onSave={onUpdateField}
            />
            {/* v3.14-192: Show source if paid via fee hold */}
            {client?.invoice_paid_from_client_name && (
              <div className="flex justify-between items-center py-0.5 bg-blue-50/50 rounded px-1 -mx-1">
                <span className="text-slate-500">Paid From</span>
                <span className="font-medium text-blue-600 truncate max-w-[120px]">{client.invoice_paid_from_client_name}</span>
              </div>
            )}
            <div className="flex items-center justify-between cursor-pointer hover:bg-slate-50 rounded transition-colors" onClick={onEdit}>
              <span className="text-[10px] text-slate-500 uppercase">Status</span>
              <StatusBadge 
                status={client?.billcom_invoice_status === 'paid' ? 'Paid' : client?.billcom_invoice_status === 'sent' ? 'Invoice Sent' : client?.invoice_status || 'Pending'} 
                variant={client?.billcom_invoice_status === 'paid' ? 'paid' : client?.billcom_invoice_status === 'sent' ? 'sent' : client?.invoice_status === 'paid' ? 'paid' : client?.invoice_status === 'sent' ? 'sent' : 'pending'}
              />
            </div>
            {client?.billcom_invoice_link && (
              <a 
                href={client.billcom_invoice_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                data-testid="billcom-invoice-link"
              >
                Bill.com Invoice <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {client?.invoice_link && (
              <a 
                href={client.invoice_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                View Invoice <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {isInternal && !client?.billcom_invoice_id && !client?.invoice_link && (
              <Button
                size="sm"
                variant="outline"
                className="w-full text-xs h-7"
                onClick={onCreateInvoice}
              >
                Create Invoice
              </Button>
            )}
            {/* v3.14-416: Paid to PTC & Paid to Partner — always visible */}
            <div className="mt-1.5 pt-1.5 border-t border-slate-200" data-testid="invoice-payment-summary">
              <EditableField
                label="Paid to PTC"
                value={client?.amount_paid_to_ptc}
                fieldName="amount_paid_to_ptc"
                onSave={onUpdateField}
                highlight={!!client?.amount_paid_to_ptc}
              />
              <EditableField
                label="Paid to Partner"
                value={client?.amount_paid_to_partner}
                fieldName="amount_paid_to_partner"
                onSave={onUpdateField}
                highlight={!!client?.amount_paid_to_partner}
              />
            </div>
          </div>
        </SortableDashboardBlock>
        )}


        {/* ACH Confirmations moved to left column under Deal Financials */}

        {/* v3.14-300: Finance Offers block moved to right column next to Client Info */}

        {/* Funding Completion moved to right column under Deal Stages */}
          </div>
        </SortableContext>
      </DndContext>

      {/* Client Messages removed — duplicates Communications tab */}

      {/* Next Action Dialog */}
      <Dialog open={showNextActionDialog} onOpenChange={setShowNextActionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Next Action</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>What needs to happen next?</Label>
            <Select value={nextActionValue} onValueChange={setNextActionValue}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select an action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="call_client">Call Client</SelectItem>
                <SelectItem value="send_docs">Send Documents</SelectItem>
                <SelectItem value="follow_up">Follow Up</SelectItem>
                <SelectItem value="submit_app">Submit Application</SelectItem>
                <SelectItem value="await_approval">Await Approval</SelectItem>
                <SelectItem value="collect_payment">Collect Payment</SelectItem>
                <SelectItem value="schedule_install">Schedule Install</SelectItem>
                <SelectItem value="close_deal">Close Deal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNextActionDialog(false)}>Cancel</Button>
            <Button onClick={handleNextActionSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.14-157: Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Send Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>To</Label>
              <Input
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                placeholder="recipient@email.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                placeholder="Email subject"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                placeholder="Type your message..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail}>
              {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.14-157: SMS Dialog */}
      <Dialog open={showSmsDialog} onOpenChange={setShowSmsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-600" />
              Send SMS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Phone Number</Label>
              <Input
                value={smsData.to}
                onChange={(e) => setSmsData({ ...smsData, to: e.target.value })}
                placeholder="(555) 123-4567"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={smsData.message}
                onChange={(e) => setSmsData({ ...smsData, message: e.target.value })}
                placeholder="Type your message... (160 char limit)"
                rows={3}
                maxLength={160}
                className="mt-1"
              />
              <p className="text-xs text-slate-400 mt-1">{smsData.message.length}/160 characters</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSmsDialog(false)}>Cancel</Button>
            <Button onClick={handleSendSms} disabled={sendingSms} className="bg-green-600 hover:bg-green-700">
              {sendingSms ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.14-157: Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              Create Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Task Title</Label>
              <Input
                value={taskData.title}
                onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
                placeholder="What needs to be done?"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={taskData.due_date}
                onChange={(e) => setTaskData({ ...taskData, due_date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Assigned Users</Label>
              {/* v3.15-1080: multi-assignee picker. Backend defaults to
                  creator if no users selected. */}
              <div className="mt-1">
                <UserMultiSelect
                  users={users || []}
                  companies={companies || []}
                  value={taskData.assigned_users}
                  onChange={(ids) => setTaskData({ ...taskData, assigned_users: ids })}
                  placeholder="Assign to one or more users (default: yourself)"
                  testId="dd-task-assignees"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={creatingTask} className="bg-purple-600 hover:bg-purple-700">
              {creatingTask ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.14-167: Finturf Offer Modal REMOVED — lending actions centralized to LendingPage */}

      {/* v3.14-297: Credit Application Modal REMOVED — financing fields now in Client Info */}

      {/* v3.14-211: Message PTC User Dialog — for external orgs */}
      <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              Message {messageTarget.name}
            </DialogTitle>
            <p className="text-xs text-slate-500">{messageTarget.role} for {client?.full_name}</p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              placeholder={`Type your message to ${messageTarget.name}...`}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={4}
              className="text-sm"
              data-testid="message-ptc-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setMessageDialogOpen(false)}>Cancel</Button>
            <Button 
              size="sm" 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleSendMessage}
              disabled={!messageText.trim() || sendingMessage}
              data-testid="send-message-ptc-btn"
            >
              {sendingMessage ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.14-CALENDAR: Appointment Scheduling Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Schedule Appointment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  className="mt-1 h-8 text-xs"
                  data-testid="appointment-date-input"
                />
              </div>
              <div>
                <Label className="text-xs">Time</Label>
                <Input
                  type="time"
                  value={appointmentTime}
                  onChange={(e) => setAppointmentTime(e.target.value)}
                  className="mt-1 h-8 text-xs"
                  data-testid="appointment-time-input"
                />
              </div>
            </div>

            {/* Availability indicator */}
            {availabilityStatus === 'checking' && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500 px-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Checking availability...
              </div>
            )}
            {availabilityStatus === 'available' && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="slot-available">
                <CheckCircle className="h-3.5 w-3.5" />
                Time slot is available
              </div>
            )}
            {availabilityStatus === 'unavailable' && (
              <div className="space-y-2" data-testid="slot-unavailable">
                <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  <XCircle className="h-3.5 w-3.5" />
                  Time slot not available
                </div>
                {suggestedSlots.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-2">
                    <p className="text-[10px] font-semibold text-blue-700 mb-1.5">Suggested available times:</p>
                    <div className="space-y-1">
                      {suggestedSlots.map((slot, i) => (
                        <button
                          key={slot.display || `slot-${i}`}
                          onClick={() => handlePickSuggestedSlot(slot)}
                          className="w-full text-left text-xs px-2 py-1 rounded hover:bg-blue-100 text-blue-800 transition-colors"
                          data-testid={`suggested-slot-${i}`}
                        >
                          {slot.display}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs">Duration</Label>
              <Select value={appointmentDuration} onValueChange={setAppointmentDuration}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="appointment-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15" className="text-xs">15 minutes</SelectItem>
                  <SelectItem value="30" className="text-xs">30 minutes</SelectItem>
                  <SelectItem value="45" className="text-xs">45 minutes</SelectItem>
                  <SelectItem value="60" className="text-xs">1 hour</SelectItem>
                  <SelectItem value="90" className="text-xs">1.5 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Assigned Rep</Label>
              <Select value={appointmentRep} onValueChange={setAppointmentRep}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="appointment-rep-select">
                  <SelectValue placeholder="Select rep" />
                </SelectTrigger>
                <SelectContent>
                  {ptcReps.map((r) => (
                    <SelectItem key={r.id} value={r.full_name} className="text-xs">{r.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={appointmentNotes}
                onChange={(e) => setAppointmentNotes(e.target.value)}
                placeholder="Appointment notes..."
                className="mt-1 text-xs min-h-[60px]"
                data-testid="appointment-notes-input"
              />
            </div>
            <div className="bg-slate-50 border rounded p-2 text-[10px] text-slate-500">
              Client: {client?.full_name || '-'} | Email: {client?.email || '-'} | Phone: {client?.phone || '-'}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAppointmentDialog(false)}>Cancel</Button>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleScheduleAppointment}
              disabled={schedulingAppointment || !appointmentDate || !appointmentTime || availabilityStatus === 'unavailable' || availabilityStatus === 'checking'}
              data-testid="confirm-schedule-btn"
            >
              {schedulingAppointment ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Calendar className="h-3 w-3 mr-1" />}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.15-689: Application Entry Assistant — replaces old Send for Offers confirmation.
          v3.21-1793 · Application ID CANONICAL SSOT correction.  The
          user-facing "Application ID" field now writes to and reads
          from `clients.current_finturf_application_id` — the REAL
          Finturf/PTC Finance application identifier used by every
          downstream offer-lookup, refresh, link, and financing-result
          call (see `initialApplicationId` at line ~1319 of this file
          and `/api/finturf/offers/{id}/refresh`).  Legacy
          `client_external_id` remains an internal business/partner
          identifier and is NEVER displayed as Application ID.  The
          modal cannot be dismissed (X / Esc / backdrop) and Submit
          is disabled until the canonical field has successfully
          persisted to the backend SSOT via `onUpdateField`. */}
      <Dialog open={offersConfirmOpen} onOpenChange={(v) => {
        if (!v && offersConfirmOwner) {
          const canonical = String(
            client?.current_finturf_application_id
            || client?.deal_number
            || ''
          ).trim();
          if (!canonical) {
            toast.error('Finturf Application ID is required.');
            return;
          }
        }
        setOffersConfirmOpen(v);
      }}>
        <DialogContent
          className="max-w-lg"
          data-testid="app-entry-assistant"
          onEscapeKeyDown={(e) => {
            if (!offersConfirmOwner) return;
            const canonical = String(
              client?.current_finturf_application_id
              || client?.deal_number
              || ''
            ).trim();
            if (!canonical) { e.preventDefault(); toast.error('Finturf Application ID is required.'); }
          }}
          onPointerDownOutside={(e) => {
            if (!offersConfirmOwner) return;
            const canonical = String(
              client?.current_finturf_application_id
              || client?.deal_number
              || ''
            ).trim();
            if (!canonical) { e.preventDefault(); toast.error('Finturf Application ID is required.'); }
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">Application Entry Assistant</DialogTitle>
          </DialogHeader>
          {offersConfirmOwner && (() => {
            const suffix = offersConfirmOwner === 2 ? '_2' : '';
            const firstName = (client?.[`full_name${suffix}`] || client?.full_name || '').split(' ')[0] || '';
            const lastName = (client?.[`full_name${suffix}`] || client?.full_name || '').split(' ').slice(1).join(' ') || '';
            const dobRaw = client?.[`date_of_birth${suffix}`] || client?.[`dob${suffix}`] || '';
            // v3.15-1024: System-standard DOB display = mm/dd/yyyy
            const dob = (() => {
              if (!dobRaw) return '';
              try {
                const d = new Date(String(dobRaw).slice(0, 10) + 'T00:00:00');
                if (isNaN(d.getTime())) return dobRaw;
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return `${mm}/${dd}/${d.getFullYear()}`;
              } catch { return dobRaw; }
            })();
            const ssn = client?.[`ssn${suffix}`] || '';
            const phone = client?.[`phone${suffix === '_2' ? '_2' : ''}`] || client?.phone || '';
            const email = client?.[`email${suffix === '_2' ? '_2' : ''}`] || client?.email || '';
            const address = client?.address || client?.street_address || '';
            const city = client?.city || '';
            const state = client?.state || '';
            const zip = client?.zip_code || client?.zip || '';
            const income = client?.[`monthly_income${suffix}`] || client?.[`income${suffix}`] || '';
            const loanAmt = client?.[`loan_amount_requested${suffix}`] || client?.[`loan_amount${suffix}`] || '';
            // v3.15-1031: underwriting fields — match the chip line on the
            // Client Info card ("Job Income · Employed · Bachelors · Bi-Weekly").
            const incomeType    = client?.[`income_type${suffix}`] || '';
            const employmentStatus = client?.[`employment_status${suffix}`] || client?.[`employment${suffix}`] || '';
            const educationVal  = client?.[`education${suffix}`] || client?.[`education_level${suffix}`] || '';
            const payFreq       = client?.[`pay_frequency${suffix}`] || '';
            const employerName  = client?.[`employer_name${suffix}`] || client?.[`employer${suffix}`] || '';
            const loanPurpose   = client?.[`loan_purpose${suffix}`] || client?.loan_purpose || '';
            const residence     = client?.[`residence_type${suffix}`] || client?.[`residence${suffix}`] || '';
            // v3.21-1793 · Application ID canonical SSOT correction.
            // The user-facing "Application ID" is now the REAL Finturf
            // application identifier stored in
            // `clients.current_finturf_application_id` (fallback
            // `clients.deal_number` for legacy records).  This is the
            // same field the Hub uses for offer lookup, application
            // refresh, application link, and financing-result
            // retrieval (see initialApplicationId at line ~1319 of
            // this file, `/api/finturf/offers/{id}/refresh`, and
            // `/api/finturf/applications/link`).  The legacy
            // `client_external_id[_2]` field remains a Business /
            // partner identifier — it is NOT displayed as
            // "Application ID".
            const canonicalApplicationId = String(
              client?.current_finturf_application_id
              || client?.deal_number
              || ''
            ).trim();
            const legacyBusinessClientId = suffix === '_2'
              ? (client?.client_external_id_2 || '')
              : (client?.client_external_id   || '');
            // Conflict flag: canonical is populated AND differs from
            // the legacy value.  Never overwritten by v1793 — surfaced
            // so the operator can reconcile manually.
            const _canon = canonicalApplicationId;
            const _legacy = String(legacyBusinessClientId || '').trim();
            const applicationIdConflict = !!(_canon && _legacy && _canon !== _legacy);
            const businessClientId = canonicalApplicationId;
            // v3.21-1781 · Prefill normalizers — the Client Info card
            // stores the operator-typed value verbatim ("Bi-Weekly",
            // "own", "Own", "OWN OUTRIGHT" …) but the dropdown option
            // values are the underscored canonical slugs.  Without
            // normalization the <select> falls to its blank default
            // even though the field IS populated in Client Info,
            // producing the "prefill blank" defect the operator saw
            // in Production.  Normalization is one-way (raw → option
            // value) and never overwrites the stored value.
            const _normalizePayFrequency = (raw) => {
              const v = String(raw || '').trim().toLowerCase()
                .replace(/[\s\-]+/g, '_');
              if (!v) return '';
              if (['weekly', 'wk'].includes(v)) return 'weekly';
              if (['bi_weekly', 'biweekly', 'every_two_weeks', '2_weeks',
                   'every_2_weeks', 'fortnightly'].includes(v)) return 'bi_weekly';
              // v3.21-1782 · Semi-monthly (24/yr, twice a month on
              // fixed calendar dates) is NOT bi-weekly (26/yr, every
              // 14 days).  Do NOT silently collapse semi-monthly into
              // bi-weekly — that would change the business meaning of
              // the operator-entered value.  The current AEA dropdown
              // does not offer a Semi-Monthly option, so return the
              // raw slug ('semi_monthly'); it will not match any
              // dropdown value and the operator will see the field
              // blank, forcing an explicit correction rather than a
              // silent semantic change.
              if (['monthly', 'month'].includes(v)) return 'monthly';
              if (['yearly', 'annually', 'annual'].includes(v)) return 'yearly';
              return v;
            };
            const _normalizeResidence = (raw) => {
              const v = String(raw || '').trim().toLowerCase()
                .replace(/[\s\-]+/g, '_');
              if (!v) return '';
              if (['rent', 'renter', 'renting', 'rented'].includes(v)) return 'rent';
              if (['own', 'owner', 'owned', 'own_outright', 'owner_no_mortgage',
                   'own_no_mortgage'].includes(v)) return 'own_outright';
              if (['own_with_mortgage', 'owner_with_mortgage', 'mortgage',
                   'own_mortgage'].includes(v)) return 'own_with_mortgage';
              if (['other', 'family', 'living_with_family'].includes(v)) return 'other';
              return v;
            };
            const payFreqNorm  = _normalizePayFrequency(payFreq);
            const residenceNorm = _normalizeResidence(residence);
            const isSolar = (companyIndustry || '').toLowerCase().includes('solar');
            const adjustedAmt = isSolar && loanAmt ? Math.round(parseFloat(loanAmt) * 1.10) : null;

            const ssnStr = String(ssn || '').replace(/\D/g, '');
            const ssnFirst5 = ssnStr.length >= 5
              ? `${ssnStr.slice(0, 3)}-${ssnStr.slice(3, 5)}`
              : (ssnStr.slice(0, 5) || '');
            const ssnLast4 = ssnStr.length >= 4 ? ssnStr.slice(-4) : (ssnStr || '');
            // v3.21-1679 · Preflight required-field visibility.  Compiles the
            // list of Finturf-required fields that are still missing so the
            // operator can correct them inline BEFORE submitting.  Mirrors the
            // canonical backend preflight in services/finturf_service.py.
            const preflightMissing = [];
            if (!firstName) preflightMissing.push('First Name');
            if (!lastName) preflightMissing.push('Last Name');
            if (!dob) preflightMissing.push('DOB');
            if (!ssnStr || ssnStr.length !== 9) preflightMissing.push('SSN (9 digits)');
            if (!phone) preflightMissing.push('Phone');
            if (!email) preflightMissing.push('Email');
            if (!address) preflightMissing.push('Address');
            if (!zip) preflightMissing.push('Zip');
            if (!income) preflightMissing.push('Monthly Income');
            if (!loanAmt) preflightMissing.push('Loan Amount');
            if (!residence) preflightMissing.push('Residence');
            if (!incomeType && !employmentStatus) preflightMissing.push('Income Source');
            const copyText = (fields) => {
              const text = fields.map(f => `${f.label}: ${f.value || 'N/A'}`).join('\n');
              navigator.clipboard.writeText(text);
              toast.success('Copied to clipboard');
            };
            const readonlyRow = (label, value, emphasis = false, testKey = null) => (
              <div key={label} className="flex justify-between gap-4" data-testid={`app-entry-row-${(testKey || label).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <span className="text-slate-500 whitespace-nowrap">{label}:</span>
                <span className={`font-medium text-slate-800 text-right select-all ${emphasis ? 'text-sm tracking-wide' : ''}`}>{value || 'N/A'}</span>
              </div>
            );
            const editableRow = (label, fieldName, currentValue, extraProps = {}) => (
              <div key={fieldName} className="flex items-center gap-3" data-testid={`app-entry-editable-${fieldName}`}>
                <span className="text-slate-500 whitespace-nowrap flex-shrink-0 w-32">{label}:</span>
                <div className="flex-1 min-w-0">
                  <EditableField label="" value={currentValue} fieldName={fieldName} onSave={onUpdateField} {...extraProps} />
                </div>
              </div>
            );
            const editableDropdown = (label, fieldName, currentValue, options) => (
              <div key={fieldName} className="flex items-center gap-3" data-testid={`app-entry-editable-${fieldName}`}>
                <span className="text-slate-500 whitespace-nowrap flex-shrink-0 w-32">{label}:</span>
                <div className="flex-1 min-w-0">
                  <EditableDropdownField label="" value={currentValue} fieldName={fieldName} onSave={onUpdateField} options={options} />
                </div>
              </div>
            );
            // v3.21-1679 · Suffix-aware canonical field names for onUpdateField.
            const F = (base) => `${base}${suffix}`;
            const copyAllFields = [
              { label: 'First Name', value: firstName },
              { label: 'Last Name', value: lastName },
              { label: 'DOB', value: dob },
              { label: 'SSN (Last 4)', value: ssnLast4 },
              { label: 'SSN (First 5)', value: ssnFirst5 },
              { label: 'SSN (Full)', value: formatSSN(ssn) },
              { label: 'Phone', value: formatPhone(phone) },
              { label: 'Email', value: email },
              { label: 'Address', value: address },
              { label: 'City', value: city },
              { label: 'State', value: state },
              { label: 'Zip', value: zip },
              { label: 'Monthly Income', value: income ? `$${Number(income).toLocaleString()}` : '' },
              { label: 'Loan Amount', value: loanAmt ? `$${Number(loanAmt).toLocaleString()}` : '' },
              { label: 'Income Source', value: prettifyChip(incomeType) },
              { label: 'Employment Status', value: prettifyChip(employmentStatus) },
              { label: 'Employer', value: employerName },
              { label: 'Education', value: prettifyChip(educationVal) },
              { label: 'Pay Frequency', value: prettifyChip(payFreq) },
              { label: 'Loan Purpose', value: prettifyChip(loanPurpose) },
              { label: 'Residence', value: prettifyChip(residence) },
            ];

            return (
              <div className="space-y-3">
                {preflightMissing.length > 0 && (
                  <div className="bg-amber-50 border border-amber-300 rounded p-2 text-[11px] text-amber-800" data-testid="preflight-missing-banner">
                    <p className="font-semibold mb-0.5">Missing required fields — correct inline below:</p>
                    <p>{preflightMissing.join(' · ')}</p>
                  </div>
                )}

                {/* Identity read-only preview (Copy targets) */}
                <div className="bg-slate-50 border rounded p-3 space-y-1.5 font-mono text-xs" data-testid="app-entry-fields">
                  {readonlyRow('First Name', firstName)}
                  {readonlyRow('Last Name', lastName)}
                  {readonlyRow('DOB', dob)}
                  {readonlyRow('SSN (Last 4)', ssnLast4, true)}
                  {readonlyRow('SSN (First 5)', ssnFirst5)}
                  {readonlyRow('SSN (Full)', formatSSN(ssn))}
                  {readonlyRow('Phone', formatPhone(phone))}
                  {readonlyRow('Email', email)}
                  {readonlyRow('Address', address)}
                  {readonlyRow('City', city)}
                  {readonlyRow('State', state)}
                  {readonlyRow('Zip', zip)}
                </div>

                {/* v3.21-1679 · Editable correction panel — click any row to fix
                    it in place. Saves persist to `clients` via onUpdateField
                    (PUT /clients/{id}) so the canonical Finturf payload builder
                    reads the corrected values when Submit Application is clicked.
                    SSN, DOB, Address remain visible above; edits below never
                    clear other fields (PUT is per-field). */}
                <div className="border rounded p-3 space-y-1 text-xs" data-testid="app-entry-editable-panel">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Correct before submitting</p>
                  {/* v3.21-1793 · Application ID writes to the REAL
                      canonical Finturf application identifier
                      (`current_finturf_application_id`).  Client Info
                      reads the same field, so the value round-trips
                      without a second relink step. */}
                  {readonlyRow('EQF Application #', client?.[`credit_app_confirmation_number${suffix}`], false, 'eqf-application-number')}
                  {editableRow('Finturf Application ID', 'current_finturf_application_id', canonicalApplicationId, { onSave: saveAeaFinturfId })}
                  {client?.finturf_application_id_conflict && (
                    <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid="aea-finturf-id-conflict-flag">
                      Review: a new Finturf application ({client.finturf_application_id_conflict.new}) was not saved because this client already has {client.finturf_application_id_conflict.existing}.
                    </div>
                  )}
                  {applicationIdConflict && (
                    <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid="application-id-conflict-warning">
                      Legacy Business ID (<span className="font-mono">{_legacy}</span>) differs from the canonical Application ID (<span className="font-mono">{_canon}</span>) — not overwritten. Reconcile manually if the two records represent the same financing application.
                    </div>
                  )}
                  {editableRow('SSN (Full)', F('ssn'), ssn, { formatter: formatSSN })}
                  {editableRow('DOB', F('date_of_birth'), dobRaw, { inputType: 'date' })}
                  {editableRow('Monthly Income', F('monthly_income'), income)}
                  {editableRow('Loan Amount', F('loan_amount_requested'), loanAmt)}
                  {editableDropdown('Income Source', F('income_type'), incomeType, [
                    { value: 'job_income', label: 'Job Income' },
                    { value: 'self_employed', label: 'Self-Employed' },
                    { value: 'retirement', label: 'Retirement' },
                    { value: 'social_security', label: 'Social Security' },
                    { value: 'disability', label: 'Disability' },
                    { value: 'benefits', label: 'Benefits' },
                    { value: 'pension', label: 'Pension' },
                    { value: 'military', label: 'Military' },
                    { value: 'student', label: 'Student' },
                    { value: 'other', label: 'Other' },
                  ])}
                  {editableRow('Employment Status', F('employment_status'), employmentStatus)}
                  {editableRow('Employer', F('employer_name'), employerName)}
                  {editableDropdown('Education', F('education_level'), normalizeEducationForDropdown(educationVal), [
                    { value: 'high_school', label: 'High School' },
                    { value: 'associates_degree', label: "Associate's Degree" },
                    { value: 'bachelors_degree', label: "Bachelor's Degree" },
                    { value: 'masters_degree', label: "Master's Degree" },
                    { value: 'doctorate', label: 'Doctorate' },
                  ])}
                  {editableDropdown('Pay Frequency', F('pay_frequency'), payFreqNorm, [
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'bi_weekly', label: 'Bi-Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'yearly', label: 'Yearly' },
                  ])}
                  {editableDropdown('Residence', F('residence_type'), residenceNorm, [
                    { value: 'rent', label: 'Rent' },
                    { value: 'own_outright', label: 'Own Outright' },
                    { value: 'own_with_mortgage', label: 'Own with Mortgage' },
                    { value: 'other', label: 'Other' },
                  ])}
                  {editableRow('Loan Purpose', F('loan_purpose'), loanPurpose)}
                  {isSolar && adjustedAmt && (
                    <div className="mt-2 pt-2 border-t text-[11px] text-slate-500" data-testid="solar-adjusted-hint">
                      Solar adjustment preview: original ${Number(loanAmt).toLocaleString()} → +10% ${adjustedAmt.toLocaleString()}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => copyText(copyAllFields)} data-testid="copy-all-btn">Copy All</Button>
                  <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => copyText(copyAllFields.filter(f => ['First Name','Last Name','DOB','SSN (Last 4)','SSN (First 5)','SSN (Full)','Phone','Email'].includes(f.label)))}>Copy Personal</Button>
                  <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => copyText(copyAllFields.filter(f => ['Address','City','State','Zip'].includes(f.label)))}>Copy Address</Button>
                  <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => copyText(copyAllFields.filter(f => f.label.includes('Income') || f.label.includes('Amount')))}>Copy Income</Button>
                </div>
                {/* v3.21-1792 · Application ID REQUIRED gate.  The button
                    is disabled until the canonical `client_external_id`
                    (or `_2`) has been persisted to the backend SSOT.
                    Merely typing text into the correction panel is NOT
                    sufficient — the value must reach the server.  */}
                {!businessClientId && (
                  <div className="rounded border border-red-300 bg-red-50 px-2 py-1 text-[11px] text-red-800" data-testid="application-id-required-message">
                    Finturf Application ID is required.
                  </div>
                )}
                <Button size="sm" className="w-full text-xs bg-blue-600 hover:bg-blue-700" disabled={sendingOffers || !businessClientId} onClick={() => { setOffersConfirmOpen(false); handleSendForOffers(offersConfirmOwner); }} data-testid="submit-application-btn">
                  {sendingOffers ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* v3.15-1029: shared email-draft preview modal — Hard-to-Reach + At-Risk */}
      <EmailDraftModal
        open={!!draftModal}
        onClose={() => setDraftModal(null)}
        draft={draftModal?.draft}
        endpoint={draftModal?.endpoint}
        basePayload={draftModal?.basePayload}
        workflowLabel={draftModal?.workflowLabel}
        onSent={() => { if (typeof fetchClient === 'function') fetchClient(); }}
      />

      {/* v3.15-1104 — CF360 modals (Send + Mark Returned). The Send button
          is added to the deal-actions column; the Mark Returned trigger
          lives on the dormant banner. Both modals refetch the client on
          success so the banner / badge update in the same render. */}
      <SendToCF360Modal
        open={cf360SendOpen}
        onOpenChange={setCf360SendOpen}
        client={client}
        onSuccess={() => { if (typeof fetchClient === 'function') fetchClient(); }}
      />
      <MarkReturnedFromCF360Modal
        open={cf360MarkReturnedOpen}
        onOpenChange={setCf360MarkReturnedOpen}
        client={client}
        onSuccess={() => { if (typeof fetchClient === 'function') fetchClient(); }}
      />

      {/* v3.15-1123 — Let's Get This Deal rally modal */}
      <LetsGetThisDealModal
        open={rallyModalOpen}
        onOpenChange={setRallyModalOpen}
        client={client}
        companyName={(companies?.find(c => c.id === client?.company_id) || fetchedCompany)?.name || client?.company_name || null}
        onSuccess={() => { if (typeof fetchClient === 'function') fetchClient(); }}
      />

      {/* v3.15-1151 — Clear To Collect authorization modal */}
      <ClearToCollectModal
        open={ctcModalOpen}
        onOpenChange={setCtcModalOpen}
        client={client}
        selectedOffer={ctcSelectedOffer}
        existing={ctcAuth}
        isStale={ctcStale}
        staleReason={ctcStaleReason}
        currentUser={currentUser}
        onSuccess={() => {
          fetchCtc();
          if (typeof fetchClient === 'function') fetchClient();
        }}
      />
    </div>
  );
}
